import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTableDataSource } from '@angular/material/table';

import mime from 'mime';
import moment from 'moment';
import { FilePondComponent } from 'ngx-filepond';
import { merge, Observable, Subscription, of } from 'rxjs';
import { catchError, finalize, map, take } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { CaseDateFilters, CasesFacade } from 'src/app/abstraction/cases.facade';
import { EntitiesFacade } from 'src/app/abstraction/entities.facade';
import { DeleteCaseModuleResult, ModulesFacade } from 'src/app/abstraction/modules.facade';
import { AUTHORIZATION_BACKGROUND_COLORS, AUTHORIZATION_COLORS, ProvisionsFacade } from 'src/app/abstraction/provisions.facade';
import { ProvisionsFrequencyUnit } from 'src/app/core/enums/provisionsFrequencyUnit';
import { ProvisionsUnit } from 'src/app/core/enums/provisionsUnit';
import { ViewManagementEntities } from 'src/app/core/enums/ViewManagementEntities';
import { OrdersStates } from 'src/app/core/enums/ordersStates';
import { AttentionsService } from 'src/app/core/services/attentions.service';
import { CasesService, PatchOrderBody } from 'src/app/core/services/cases.service';
import { BudgetBody, QuoteBody } from 'src/app/core/services/provisions.service';
import { FileUtilitiesService } from 'src/app/core/services/fileUtilities.service';
import { NomenclatorService } from 'src/app/core/services/nomenclator.service';
import { CustomValidators } from 'src/app/core/validators/custom-validators';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { MessageDialogComponent } from 'src/app/shared/components/message-dialog/message-dialog.component';
import { Arrangement } from 'src/app/shared/models/arrangement';
import { ArrangementItem } from 'src/app/shared/models/arrangementItem';
import { Authorization } from 'src/app/shared/models/authorization';
import { Case } from 'src/app/shared/models/case';
import { CaseArrangement } from 'src/app/shared/models/caseArrangement';
import { CaseModule, CaseModuleComposed, Module, ModuleItems } from 'src/app/shared/models/module';
import { CaseModulePostQPS, modulesGetQPS } from 'src/app/shared/models/modulesQPS';
import { Order } from 'src/app/shared/models/order';
import { Practice } from 'src/app/shared/models/practice';
import { Provision } from 'src/app/shared/models/provision';
import { ProvisionFee } from 'src/app/shared/models/provisionFee';
import { Provider } from 'src/app/shared/models/provider';
import { Item } from 'src/app/shared/models/item';
import { HttpBaseResponseErrorTeaPot } from 'src/app/shared/models/httpBaseResponseErrorTeaPot';
import { ProvisionAgreementFilters } from 'src/app/shared/models/ProvisionAgreementFilters';
import { GeneralService } from 'src/app/core/services/general.service';
import { Agreement } from 'src/app/shared/models/agreement';
import { Attention } from 'src/app/shared/models/attention';
import { AttentionStates } from 'src/app/core/enums/attentionsStates';
import { AttentionType } from 'src/app/core/enums/attentionType';
import { ModulesService } from 'src/app/core/services/modules.service';
import { ProvidersService, ProvidersBySpecialtyQPS } from 'src/app/core/services/providers.service';
import { DateRange } from 'moment-range';
import { EvolutionsFacade } from 'src/app/abstraction/evolutions.facade';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { EvolutionsDialogComponent } from '../evolutions-dialog/evolutions-dialog.component';
import { OrderSharedActionsService } from '../../services/order-shared-actions.service';
import { ModuleArrangement } from 'src/app/core/services/arrangements.service';
import { isGuardiaLikeRestriction, isPatientPracticeRestriction } from 'src/app/core/utils/practice-restriction.utils';
import { ExperienceI18nService } from 'src/app/core/experience/experience-i18n.service';
import { AlertService } from 'src/app/core/services/alert.service';
import { AuthorizationState } from 'src/app/core/enums/authorizationState';
import { HttpErrorResponse } from '@angular/common/http';

type SupplyUnit = ProvisionsUnit | string;
type FrequencyUnit = ProvisionsFrequencyUnit | ProvisionsUnit | string;

export interface ProvisionRow {
    id?: number;
    codigo: string;
    practica: string;
    checked?: boolean;
    frecuencia: string;
    totalAgendado: number;
    realizado: number;
    facturable: number;
    debitable: number;
    informado: number;

    /** Referencia al objeto original (para nieta/acuerdos, etc.) */
    raw?: Provision;
}

export interface ModulePracticeSelected {
    practiceId: number;
    code?: string;
    name: string;
    frequencyAmount: number | null;
    frequencyUnit: FrequencyUnit;
    minAttentions: number | null;
    maxAttentions: number | null;
    allowedHours?: number | null;
    restrictionId?: number;
    restrictionRequiredTime?: boolean;
    restrictionPatient?: boolean;
    id?: number | null; // Id del ModuleItem (para editar)
}

export interface ModuleSupplySelected {
    practiceId: number;
    provisionFeeId?: number | null; // Clave única para diferenciar insumos del mismo practiceId
    code?: string;
    name: string;
    providerId: number | null;
    provider: Provider | null;
    providerName: string | null;
    quantity: number | null;
    unit: SupplyUnit;
    unitCost: number | null;
    id?: number | null; // Id del ModuleItem (para editar)
}

export interface AgreementRow {
    checked: boolean;
    agreement: string;
    totalScheduled: number;
    cost: number;
    done: number;
    notDone: number;
    billable: number;
    debitable: number;
    reported: number;
    vigency: string;
}

interface ModulesFooterSummary {
    modulesCount: number;
    totalScheduledAmount: number;
    totalBillableAmount: number;
    totalDebitableAmount: number;
    totalInformedAmount: number;
}

interface CaseModuleFrequency {
    amount: number;
    unit: string;
}

@Component({
    selector: 'app-modules-scheme',
    templateUrl: './modules-scheme.component.html',
    styleUrl: './modules-scheme.component.scss',
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('230ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
            transition('expanded <=> void', animate('230ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class ModulesSchemeComponent implements OnChanges, OnDestroy {
    // ===== Inputs / Outputs =====
    // @Input() attentions: Attention[];
    @Input() caseModules: CaseModule[];
    @Input() caseId: number;
    @Input() financierId: number;
    @Input() caseModulesComposed: CaseModuleComposed[] = [];
    @Input() hideSuppliesSection: boolean = false;
    @Input() hideOrdersSection: boolean = false;
    @Input() isCollapsed: boolean = false;
    /** Pulsar con new Date() desde el padre para resetear todos los checkboxes */
    @Input() resetChecks: Date | null = null;
    @Input() singleSelectionMode: boolean = false;  // Activa modo radio
    @Output() caseModulesChanged = new EventEmitter<void>();
    @Output() selectedCaseModuleIdsChanged = new EventEmitter<number[]>();
    @Output() singleSelectedModuleChanged = new EventEmitter<{ id: number | null, name: string | null }>();
    @Output() ordersRadioSelectedChanged = new EventEmitter<boolean>();
    @Output() loadPriceRequested = new EventEmitter<ProvisionFee[]>();

    // ===== View children / templates =====
    @ViewChild('myPond') myPond: FilePondComponent;
    @ViewChild('moduleTemplate', { static: true }) moduleTemplate: TemplateRef<any>;
    @ViewChild('agreementTemplate', { static: true }) agreementTemplate: TemplateRef<any>;
    @ViewChild('suspendPracticeTemplate', { static: true }) suspendPracticeTemplate: TemplateRef<any>;
    @ViewChild('amendPracticeTemplate', { static: true }) amendPracticeTemplate: TemplateRef<any>;
    @ViewChild('practicesPickerTemplate', { static: true }) practicesPickerTemplate: TemplateRef<any>;
    @ViewChild('suppliesPickerTemplate', { static: true }) suppliesPickerTemplate: TemplateRef<any>;
    @ViewChild('addOrderTemplate', { static: true }) addOrderTemplate: TemplateRef<any>;
    @ViewChild('updateAuthorizationTemplate', { static: true }) updateAuthorizationTemplate: TemplateRef<any>;
    @ViewChild('suspendCaseModuleTemplate', { static: true }) suspendCaseModuleTemplate: any;
    @ViewChild('modifyOrderTemplate', { static: true }) modifyOrderTemplate: TemplateRef<any>;
    @ViewChild('popUpMessage', { static: true }) popUpMessage: TemplateRef<any>;
    @ViewChild('suppliesExportBudget', { read: MatMenuTrigger }) showSuppliesBudgetMenu: MatMenuTrigger;
    @ViewChild('suppliesExportQuote', { read: MatMenuTrigger }) showSuppliesQuoteMenu: MatMenuTrigger;
    @ViewChild('orderDeliveryPond') orderDeliveryPond?: FilePondComponent;
    @ViewChild('authPond') authPond?: FilePondComponent;
    @ViewChild('savingOrderTemplate', { static: true }) savingOrderTemplate: TemplateRef<unknown>;
    private savingOrderDialog: MatDialogRef<unknown> | null = null;

    isLoadingUpdatingCaseModule$: Observable<boolean>;
    _isLoadingUpdatingCaseModule: boolean;
    _isLoadingCreatingCaseModule: boolean;
    _isLoadingCreatingOrder: boolean;
    _isLoadingUpdatingOrder: boolean;

    latestRangeDate: CaseDateFilters=null

    // ===== Table state / UI state =====
    provisions: Provision[] = [];
    modulesFooterColumns: string[] = ['modulesFooter'];
    practicesDS = new Map<number, MatTableDataSource<ProvisionRow>>();
    expandedChild: ProvisionRow | null = null;
    isExpanded: boolean = true;
    showAlertMsg: boolean = false;
    provisionFees: ProvisionFee[] = [];
    expandedElement: CaseModuleComposed | null = null;
    selectionModules = new SelectionModel<CaseModuleComposed>(true, []);
    selectionProvisions = new SelectionModel<Provision>(true, []);
    selectionSupplies = new SelectionModel<Provision>(true, []);
    selectionAgreements = new SelectionModel<Agreement>(true, []);
    selectionOrders = new SelectionModel<Order>(true, []);
    // (Se mantiene para el resto del flujo del componente / diálogo)
    allModulesWithoutFilter: Module[] = [];
    //Selection counters
    modules_trueChecks: number = 0;
    practices_RowChecks_trueChecks: number = 0;
    agreements_RowChecks_trueChecks: number = 0;
    private _initialSelectionDone = false;
    orders_trueChecks: number = 0;
    // Single ModuleSelection Mode
    singleSelectedModuleId: number | null = null;
    ordersRadioSelected: boolean = false;  // Radio de todos los pedidos
    // Tabla: ahora consume CaseModuleComposed[]
    dataSource = new MatTableDataSource<CaseModuleComposed>([]);
    isAddMode = false;
    isEditMode = false;
    isRePostMode: boolean = false; //renovar
    caseModuleSelected: CaseModuleComposed | null = null;
    // En edición del diálogo, guardamos el módulo del caso (row) para poder
    // preseleccionar prácticas/insumos según lo ya guardado (row.provisions.checked)
    private editingModuleRow: CaseModuleComposed | null = null;
    title = '';
    checkboxAuthorizationText = '';
    _rePostFromDateSub: Subscription;
    _historyModeDate: Subscription;
    _provisionsDisplayedOnScreen: Subscription;

    // ===== Forms (dialogs) =====
    formModule: FormGroup;
    practicesFormArray: FormArray;
    suppliesFormArray: FormArray;
    modulesFilteredByFinancier: Module[] = [];
    moduleSelected: Module | null = null;
    moduleSelectedHasSupplies: boolean = false;
    moduleSelectedHasPractices: boolean = false;
    arrangementSelected: Arrangement;
    isRetroactive: boolean;
    isShowRetroactive: boolean;
    isShowAuth: boolean = true;
    toDay: Date = moment(new Date(), 'YYYY-MM-DD').toDate();
    swalWithCustomizeButtons: any;
    swalAutoClose: any;
    provisionFeeToWork: ProvisionFee;
    _dialogRef;
    formAuthorization: FormGroup;
    formAgreement: FormGroup;
    formAmendPractice: FormGroup;
    case: Case;
    formFrequency: FormGroup;
    formSuspend: FormGroup;
    formAddOrder: FormGroup;
    caseModulePractices: Provision[] = [];
    caseModuleSupplies: Provision[] = [];
    isAddingAuthorization: boolean = false;
    showTxtWithOutArrangement: boolean = false;
    provisionAgreementsInput: ProvisionAgreementFilters;
    provisionToWork: Provision;
    practiceSelected: Practice;
    practicesToShow: Practice[];
    modulesToShow: Module[];
    practicesPanelExpanded = true;
    suppliesPanelExpanded = true;
    practices: Practice[];
    ViewManagementEntities = ViewManagementEntities;
    arrangementItemsByModule: Partial<ModuleArrangement>[];
    existPriceList: boolean;
    hasMissingPriceRange: boolean = false;
    attentionsINRange: Attention[] = [];

    totalValueFooter: number = 0;
    totalInvoicedFooter: number = 0;
    totalDebitedFooter: number = 0;
    totalDeliveredFooter: number = 0;
    message: string;
    txtConfirmBtn: string;
    txtCancelBtn: string = 'Ok';
    showConfirmBtn: boolean = false;
    _popupConfirmCallback: (() => void) | null = null;
    closeAll: boolean = false;
    arrayTable = [];
    displayedColumnsPopUp: string[] = ['supplyName', 'quantity', 'provider'];

    hoveredModuleTooltip: CaseModuleComposed | null = null;
    provisionFeeIdforHover;

    // ===== Agreements (crear) =====
    providers: Provider[] = [];
    isAddModeAgreement: boolean = false;
    isUpdateModeAgreement: boolean = false;
    actionAgreement: string = '';
    agreementToWork: any = null;
    enableMarginField: boolean = false;
    isLoadingCreatingAgreement: boolean = false;
    isLoadingUpdatingAgreement: boolean = false;

    _dialogRefAgreementTemplate: MatDialogRef<any>;

    // ===== Selection models (dialog) =====
    // Selection inside the selected module (dialog)
    practicesSelection = new SelectionModel<ModuleItems>(true, []);
    suppliesSelection = new SelectionModel<ModuleItems>(true, []);

    // ===== UI nueva: selección de prácticas en diálogo apilado =====
    provisionsFrequencyUnit = [
        {
            id: ProvisionsFrequencyUnit.DIA,
            name: this.experienceI18n.translate('modules.cases.modules-scheme.dia', undefined, 'Dia'),
        },
        {
            id: ProvisionsFrequencyUnit.SEMANA,
            name: this.experienceI18n.translate('modules.cases.modules-scheme.semana', undefined, 'Semana'),
        },
        {
            id: ProvisionsFrequencyUnit.MES,
            name: this.experienceI18n.translate('modules.cases.modules-scheme.mes', undefined, 'Mes'),
        },
    ];
    practicesPickerSearchControl = new FormControl('');
    practicesPickerForm: FormGroup;
    filteredPracticePickerRowVM: Array<{ index: number; fg: FormGroup }> = [];
    modulePracticesSelected: ModulePracticeSelected[] = [];
    // Cuando abrimos el picker desde un CaseModule ya existente, bloqueamos las prácticas ya cargadas
    // (quedan checked y no se pueden deschequear ni editar)
    private lockedPracticeIdsForPicker = new Set<number>();
    private lockedPracticePrevByIdForPicker = new Map<number, ModulePracticeSelected>();
    private _practicesDialogRef: any;
    private practicesPickerSearchSub?: Subscription;

    // ===== UI nueva: selección de insumos en diálogo apilado =====
    public readonly provisionsUnit = Object.values(ProvisionsUnit);
    public readonly provisionsUnitOptions = [
        { id: ProvisionsUnit.UNIDAD, name: this.experienceI18n.translate('modules.cases.modules-scheme.unidad', undefined, 'Unidad') },
        { id: ProvisionsUnit.BOLSA_BOLSON, name: this.experienceI18n.translate('modules.cases.modules-scheme.bolsa-bolson', undefined, 'Bolsa/Bolsón') },
        { id: ProvisionsUnit.PAQUETE, name: this.experienceI18n.translate('modules.cases.modules-scheme.paquete', undefined, 'Paquete') },
        { id: ProvisionsUnit.CAJA, name: this.experienceI18n.translate('modules.cases.modules-scheme.caja', undefined, 'Caja') },
    ];
    suppliesPickerSearchControl = new FormControl('');
    suppliesPickerForm: FormGroup;
    filteredSuppliesPickerRowVM: Array<{ index: number; fg: FormGroup }> = [];
    suppliesPickerEditMode: boolean = false;
    suppliesPickerDialogTitle: string = this.experienceI18n.translate('modules.cases.modules-scheme.cargar-insumos', undefined, 'Cargar insumos');
    // Almacena los valores originales de los insumos al entrar en modo edición
    private originalSuppliesForEdit: ModuleSupplySelected[] = [];
    // Providers por índice para el picker de insumos (como supply-list)
    supplyPickerProvidersOnSelect: (Provider | null)[][] = [[]];
    supplyPickerDefaultProviders: (string | null)[] = [null];

    // ===== Crear pedido (replica SupplyList) =====
    public readonly ORDER_PROVIDER = 'providerId';
    public readonly ORDER_PROVIDER_SEARCHING = 'providerSearching';
    public readonly ORDER_DATE = 'orderDate';
    public readonly ORDER_OBSERVATIONS = 'observations';
    public readonly ORDER_ITEMS = 'items';

    public readonly ITEM_QUANTITY = 'quantityOrdered';
    public readonly ITEM_PRICE = 'priceOrdered';
    public readonly ITEM_PROVISIONFEEID = 'provisionFeeId';
    public readonly ITEM_UNIT = 'unit';
    public readonly ITEM_PRACTICE = 'practice';
    public readonly ITEM_PROVISION = 'provision';

    // ===== Entrega de pedido (replica OrderList) =====
    public readonly ORDER_DATE_DELIVERY = 'deliveryDate';
    public readonly ORDER_CONFIRMED = 'state';
    public readonly ITEM_ID = 'id';
    public readonly ITEM_QUANTITY_DELIVERY = 'quantityDelivered';
    public readonly ITEM_PRICE_DELIVERY = 'priceDelivered';
    public readonly ITEM_PRACTICE_DELIVERY = 'practiceItemDelivered';
    public readonly ITEM_PRACTICE_SEARCHING = 'practiceItemSearching';
    public readonly IS_BILLABLE = 'isBillable';
    public readonly MODULE_NAME = 'moduleName';
    public readonly ITEM_CASE_MODULE_ID = 'caseModuleId';

    // Delivery form & state
    formModifyOrder: FormGroup;
    dataSourceSupplies = new MatTableDataSource<any>([]);
    actualOrder: Order;
    deliveringOrder: boolean = false;
    editingOrder: boolean = false;
    isBillable: boolean = false;
    photoInvoice: string = null;
    fileChanged: boolean = false;
    isLoadingUpdatingOrder$: Observable<boolean>;
    private _dialogRefModifyOrder: MatDialogRef<any>;

    displayedColumnsSuppliesDeliveringOrder: string[] = [
        'moduleColumn',
        'supplyColumn',
        'quantityColumn',
        'priceColumn',
        'quantityDeliveredColumn',
        'priceDeliveredColumn'
    ];
    displayedColumnsSuppliesForOrder: string[] = ['supplyRequested', 'quantityRequested', 'priceRequested'];
    orderItemsDataSource = new MatTableDataSource<any>([]);
    providerOrderOnSelect: Provider[][] = [[]];
    actualProviderOrderSelected: any[] = [null];
    isLoadingCreatingOrder$ = this.casesFacade.isLoadingCreatingOrder$();
    private _dialogRefOrder: MatDialogRef<any>;
    formSuspendCaseModule: FormGroup;
    private suppliesPickerSearchSub?: Subscription;
    private _dialogRefSuspendCaseModule: MatDialogRef<any>;
    caseModuleToWork: CaseModuleComposed

    // Cuando abrimos el picker de insumos desde un CaseModule, queremos avisar si el insumo ya está agregado
    // (pero permitir seleccionarlo igualmente)
    private existingSupplyIdsForPicker = new Set<number>();

    isFinalizingCaseModule = false;

    practicePickerDisplayedColumns: string[] = ['selected', 'practice', 'frequency', 'attentions'];
    supplyPickerDisplayedColumns: string[] = ['selected', 'supply', 'provider', 'quantity', 'unit', 'unitCost'];
    moduleSuppliesSelected: ModuleSupplySelected[] = [];
    private _suppliesDialogRef: any;
    _orders: Subscription;
    _allOrdersWithoutFilter: Subscription;
    private _caseSub?: Subscription;
    orders: Order[] = [];
    allOrdersWithoutFilter: Order[] = [];
    // dateRange: { start: Date; end: Date };
    dateRange: CaseDateFilters;
    get pondOptions() {
        return {
            class: 'my-filepond',
            multiple: false,
            labelIdle: this.experienceI18n.translate('modules.cases.modules-scheme.pond-label-idle', undefined, 'Adjunte <u>aquí</u> o arrastre la autorización'),
            labelFileTypeNotAllowed: this.experienceI18n.translate('modules.cases.modules-scheme.pond-label-file-type-not-allowed', undefined, 'Tipo de archivo inválido'),
            fileValidateTypeLabelExpectedTypes: this.experienceI18n.translate('modules.cases.modules-scheme.pond-label-expected-types', undefined, 'Formatos aceptados: jpg, jpeg, png, pdf'),
            acceptedFileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'],
            allowImagePreview: true,
            imagePreviewMaxHeight: 150,
            maxFileSize: this.experienceI18n.translate('modules.cases.modules-scheme.max-file-size', undefined, '10MB')
        };
    }
    provisionsToUpdate: Provision[];
    provisionFeeSelectedToAutorize: ProvisionFee = null;
    private provisionFeesToAuthorize: ProvisionFee[] = [];
    ordersDataSource = new MatTableDataSource<Order>([]);
    expandedOrder: Order | null = null;
    ordersFooterColumns: string[] = ['ordersFooter'];
    photoEvolutionB64: string = null;

    private caseModuleToAuthorize: CaseModuleComposed | null = null;
    loadingAddFile: boolean = false;

    ORDERS_STATES = OrdersStates;
    set frequencyUnitForm(value: string) {
        !!value ?
            this.formFrequency.controls[this.UNIT].setValue(value) :
            this.formFrequency.controls[this.UNIT].reset();
    }
    _formModule: Subscription;
    private _moduleDatesSub: Subscription;
    modulesColumns: string[] = [
        'module',
        'modulesMasterCheck',
        'moduleFrequency',
        'moduleFee',
        'modulesTotalScheduledCount',
        'modulesStates',
        'moduleVigency',
        'moduleAutorization',
        'moduleActions',
    ];
    modulesColumnsToDisplayExpanded: string[] = [
        'module',
        'modulesMasterCheck',
        'moduleFrequency',
        'moduleFee',
        'modulesTotalScheduledCount',
        'billingModulesCount',
        'debitedModulesCount',
        'informedModulesCount',
        'moduleVigency',
        'moduleAutorization',
        'moduleActions',
    ];
    modulesStatesColumns: string[] = ['billingModulesCount', 'debitedModulesCount', 'informedModulesCount'];

    // ===== Columnas colapsadas (agenda / facturación) =====
    modulesColumnsCollapsed: string[] = [
        'module',
        'modulesMasterCheck',
        'moduleFrequency',
        'moduleFee',
    ];
    modulesRowColumnsCollapsed: string[] = [
        'module',
        'modulesMasterCheck',
        'moduleFrequency',
        'moduleFee',
    ];
    provisionsColumnsCollapsed: string[] = [
        'provisionCode',
        'provisionName',
        'provisionsMasterCheck',
        'provisionFrequency',
    ];
    provisionsRowColumnsCollapsed: string[] = [
        'provisionCode',
        'provisionName',
        'provisionsMasterCheck',
        'provisionFrequency',
    ];
    agreementsColumnsCollapsed: string[] = [
        'agreement',
        'agreementCost',
    ];
    ordersColumnsCollapsed: string[] = [
        'ordersExpand',
        'ordersRadio',
        'ordersName',
        'ordersProvider',
        'ordersQuantity',
    ];
    orderItemsColumnsCollapsed: string[] = [
        'orderItemModule',
        'orderItemName',
        'orderItemQuantity',
        'orderItemUnitCost',
    ];
    suppliesColumnsCollapsed: string[] = [
        'supplyCode',
        'supplyName',
        'supplyMasterCheck',
        'supplyQuantity',
        'supplyTotalCost',
    ];
    suppliesRowColumnsCollapsed: string[] = [
        'supplyCode',
        'supplyName',
        'supplyMasterCheck',
        'supplyQuantity',
        'supplyTotalCost',
    ];

    get suspendModuleFromDate() {
        return this.formSuspendCaseModule.get(this.VALIDITYMODULE_FROM_DATE).value;
    }
    get suspendModuleToDate() {
        return this.formSuspendCaseModule.get(this.VALIDITYMODULE_TO_DATE).value;
    }

    /** Getters que resuelven las columnas activas según isCollapsed */
    get activeModulesHeaderColumns(): string[] {
        return this.isCollapsed ? this.modulesColumnsCollapsed : this.modulesColumns;
    }
    get activeModulesRowColumns(): string[] {
        return this.isCollapsed ? this.modulesRowColumnsCollapsed : this.modulesColumnsToDisplayExpanded;
    }
    get activeProvisionsHeaderColumns(): string[] {
        return this.isCollapsed ? this.provisionsColumnsCollapsed : this.provisionsColumns;
    }
    get activeProvisionsRowColumns(): string[] {
        return this.isCollapsed ? this.provisionsRowColumnsCollapsed : this.provisionsRowColumns;
    }
    get activeAgreementsColumns(): string[] {
        return this.isCollapsed ? this.agreementsColumnsCollapsed : this.agreementsHeaderColumns;
    }
    get activeOrdersColumns(): string[] {
        return this.isCollapsed ? this.ordersColumnsCollapsed : this.ordersColumns;
    }
    get activeOrderItemsColumns(): string[] {
        return this.isCollapsed ? this.orderItemsColumnsCollapsed : this.orderItemsColumns;
    }
    get activeSuppliesHeaderColumns(): string[] {
        return this.isCollapsed ? this.suppliesColumnsCollapsed : this.suppliesColumns;
    }
    get activeSuppliesRowColumns(): string[] {
        return this.isCollapsed ? this.suppliesRowColumnsCollapsed : this.suppliesRowColumns;
    }

    agreementsHeaderColumns: string[] = [
        'agreement',
        'agreementsTotalScheduledCount',
        'agreementCost',
        'doneAgreementsCount',
        'notDoneAgreementsCount',
        'billableAgreementsCount',
        'debitableAgreementsCount',
        'reportedAgreementsCount',
        'agreementVigency',
        'agreementActions',
    ];
    provisionsColumns: string[] = [
        'provisionCode',
        'provisionName',
        'provisionsMasterCheck',
        'provisionFrequency',
        'provisionTotalScheduled',
        'provisionStates',
        'addProvision',
    ];
    provisionsStatesColumns: string[] = [
        'realizedProvisionsCount',
        'billingProvisionsCount',
        'debitedProvisionsCount',
        'informedProvisionsCount',
    ];

    provisionsRowColumns: string[] = [
        'provisionCode',
        'provisionName',
        'provisionsMasterCheck',
        'provisionFrequency',
        'provisionTotalScheduled',
        'realizedProvisionsCount',
        'billingProvisionsCount',
        'debitedProvisionsCount',
        'informedProvisionsCount',
        'addProvision',
    ];
    // ===== Supplies grouped datasource =====
    suppliesHeaderColumns: string[] = [
        'supplyCode',
        'supplyName',
        'supplyMasterCheck',
        'supplyQuantity',
        'supplyVendor',
        'supplyTotalCost',
        'supplyOrdered',
        'suppliesStates',
        'supplyActions',
    ];

    suppliesColumns: string[] = [
        'supplyCode',
        'supplyName',
        'supplyMasterCheck',
        'supplyQuantity',
        'supplyVendor',
        'supplyTotalCost',
        'supplyOrdered',
        'suppliesStates',
        'supplyActions',
    ];
    suppliesStatesColumns: string[] = [
        'deliveredSuppliesCount',
        'notDeliveredSuppliesCount',
        'billedSuppliesCount',
        'billableSuppliesCount',
        'informedSuppliesCount',
    ];
    suppliesRowColumns: string[] = [
        'supplyCode',
        'supplyName',
        'supplyMasterCheck',
        'supplyQuantity',
        'supplyVendor',
        'supplyTotalCost',
        'supplyOrdered',
        ...this.suppliesStatesColumns,
        'supplyActions',
    ];
    ordersColumns: string[] = [
        'ordersExpand',
        'ordersRadio',
        'ordersName',
        'ordersProvider',
        'ordersQuantity',
        'ordersDate',
        'orderDelivered',
        'orderState',
        'orderActions',
    ];

    orderItemsColumns: string[] = [
        'orderItemModule',
        'orderItemCode',
        'orderItemName',
        'orderItemQuantity',
        'orderItemUnitCost',
    ];

    // ===== Supplies grouping (vista agrupada por tipo) =====
    readonly supplyCategories: string[] = ['Descartables', 'Medicamentos', 'Equipamientos', 'Otros'];
    private expandedSupplyGroups = new Set<string>();

    getmodulesQPS: modulesGetQPS = {
        active: true,
        financierId: null,
        composeItems: true,
    }
    _attentions: Subscription;
    attentions: Attention[] = [];

    _caseArrangements: Subscription;
    caseArrangements: CaseArrangement[];
    caseArrangementsWithModules: CaseArrangement[] = [];
    caseArrangementsForSelection: CaseArrangement[] = [];

    AUTHORIZATION_COLORS = AUTHORIZATION_COLORS;
    AUTHORIZATION_BACKGROUND_COLORS = AUTHORIZATION_BACKGROUND_COLORS;

    public readonly AGREEMENT_CHECK = 'agreementCheck';
    public readonly AGREEMENT_FROM_DATE = 'agreementFromDate';
    public readonly AGREEMENT_MARGIN = 'marginFee';
    public readonly AGREEMENT_PROVISION_FEE = 'providerFee';
    public readonly AGREEMENT_TO_DATE = 'agreementToDate';
    public readonly AMOUNT = 'amount';
    public readonly ARRANGEMENT = 'arrangementId';
    public readonly ARRANGEMENT_FEE = 'arrangementFee';
    public readonly ARRANGEMENT_MARGIN = 'arrangementMargin';
    public readonly ARRANGEMENT_SEARCHING = 'arrangementSearching';
    public readonly AUTHORIZATION = 'authorization';
    public readonly AUTHORIZATION_FROM_DATE = 'fromDate';
    public readonly AUTHORIZATION_NUMBER = 'authorizationNumber';
    public readonly AUTHORIZATION_OBSERVATIONS = 'observations';
    public readonly AUTHORIZATION_MEDIAURLS = 'authorizationMediaUrls';
    public readonly AUTHORIZATION_TO_DATE = 'toDate';
    public readonly FILTERED_BY_REGION = 'isFilteredByRegion';
    public readonly FEE = 'fee';
    public readonly FEE_TXT = 'fee_txt';
    public readonly MODULE_FREQUENCY = 'frequency';
    public readonly HOURS = 'allowedHours';
    public readonly IS_ADDING_AUTHORIZATION = 'is_adding_authorization';
    public readonly IS_MANUAL_VALUE = 'is_manual_value';
    public readonly MARGIN = 'profitMargin';
    public readonly MARGIN_ADDED = 'profitMarginAdded';
    public readonly MAX_QUANTITY = 'maxQuantity';
    public readonly MIN_QUANTITY = 'minQuantity';
    public readonly MODULE = 'module';
    public readonly MODULE_SEARCHING = 'moduleSearching';
    public readonly NO_ARRANGEMENT_ID = -1;
    public readonly PRACTICES = 'practiceIds';
    public readonly PRACTICE_ITEMS = 'practiceItems';
    public readonly SUPPLY_ITEMS = 'supplyItems';
    public readonly PROVISION_FREQUENCY_UNIT_DEFAULT = ProvisionsFrequencyUnit.DIA;
    public readonly PROVIDER = 'providerId';
    public readonly PROVIDER_FEE = 'providerFee';
    public readonly PROVIDER_NAME = 'providerName';
    public readonly PROVIDER_SEARCHING = 'providerSearching';
    public readonly SUPPLIES = 'supplyIds';
    public readonly TOTAL_AMOUNT = 'totalAmount';
    public readonly UNIT = 'unit';
    public readonly UPDATE_FEE = 'fee';
    public readonly VALIDITY_FROM_DATE = 'fromDate';
    public readonly VALIDITY_TO_DATE = 'toDate';
    public readonly VALIDITYMODULE_FROM_DATE = 'moduleFromDate';
    public readonly VALIDITYMODULE_TO_DATE = 'moduleToDate';

    // ===== Practice (provisionFee) actions =====
    public readonly PRACTICE_NAME = 'practiceName';

    // ===== Lifecycle / DI =====
    constructor(
        private modulesFacade: ModulesFacade,
        private provisionsFacade: ProvisionsFacade,
        private formBuilder: FormBuilder,
        public nomenclatorService: NomenclatorService,
        public dialog: MatDialog,
        public casesFacade: CasesFacade,
        private entitiesFacade: EntitiesFacade,
        private attentionsService: AttentionsService,
        public generalService: GeneralService,
        private casesService: CasesService,
        private modulesService: ModulesService,
        private evolutionsFacade: EvolutionsFacade,
        private providersService: ProvidersService,
        private _bottomSheet: MatBottomSheet,
        public orderSharedActionsService: OrderSharedActionsService,
        private fileUtilitiesService: FileUtilitiesService,
        private experienceI18n: ExperienceI18nService,
        private alertService: AlertService
    ) { }

    // ===== Lifecycle =====
    ngOnInit() {

        this._caseArrangements = this.casesFacade.getCaseArrangements$().subscribe(arrangements => {
            if (arrangements) {
                this.setCaseArrangements(arrangements);
                if (this.isRePostMode && this.caseModuleSelected?.arrangementId != null) {
                    this.arrangementSelected = this.caseArrangements.find(
                        arrangement => Number(arrangement.id) === Number(this.caseModuleSelected.arrangementId)
                    );
                    this.formModule?.get(this.ARRANGEMENT)?.setValue(this.caseModuleSelected.arrangementId, { emitEvent: false });
                    this.saveArrangementItemsByModule();
                }
            }
        });

        this.modulesFacade.isLoadingUpdatingCaseModule$().subscribe(isLoading => {
            this._isLoadingUpdatingCaseModule = isLoading;
        });

        this.modulesFacade.isLoadingCreatingCaseModule$().subscribe(isLoading => {
            this._isLoadingCreatingCaseModule = isLoading;
        });

        this.casesFacade.isLoadingCreatingOrder$().subscribe(isLoading => {
            this._isLoadingCreatingOrder = isLoading;
        });

        this.casesFacade.isLoadingUpdatingOrder$().subscribe(isLoading => {
            this._isLoadingUpdatingOrder = isLoading;
        });

        this._historyModeDate = this.casesFacade.getCaseDateFilters$().subscribe(rangeDate => {
            if (!!rangeDate && rangeDate !== this.latestRangeDate) {
                this.latestRangeDate = rangeDate;
                this.selectionOrders.clear();
                this.clearCheckboxesSelection();
                this.singleSelectedModuleId = null;
                this.singleSelectedModuleChanged.emit({ id: null, name: null });
                this.ordersRadioSelected = false;
                this.ordersRadioSelectedChanged.emit(false);
            }
        });

        this._caseSub = this.casesFacade.getCase$().subscribe((c) => {
            if (c) this.case = c;
        });

        this.casesFacade.getCaseDateFilters$().subscribe(range => {
            if (!!range) {
                this.dateRange = { historyFromDate: range.historyFromDate, historyToDate: range.historyToDate };
                // Reaplica el filtrado por rango de fechas sobre los módulos ya cargados
                this.setTableData(this.caseModulesComposed);
            }
        })
        // Importante: sin esto `swalWithCustomizeButtons` queda undefined y no se ve ningún mensaje.
        this.swalWithCustomizeButtons = Swal.mixin({
            customClass: {
                confirmButton: 'btnSwalConfirm',
                cancelButton: 'btnSwalCancel',
            },
            buttonsStyling: true,
        });

        // Para mensajes informativos (sin preguntas): se cierra solo en 3s y muestra progress bar.
        this.swalAutoClose = Swal.mixin({
            customClass: {
                confirmButton: 'btnSwalConfirm',
                cancelButton: 'btnSwalCancel',
            },
            buttonsStyling: true,
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
        this.suscribeToData();
        this.setTableData(this.caseModulesComposed);
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Cuando singleSelectionMode cambia a false (salimos de modo listado), resetear radio seleccionado
        if (changes['singleSelectionMode'] &&
            !changes['singleSelectionMode'].firstChange &&
            changes['singleSelectionMode'].currentValue === false) {
            this.singleSelectedModuleId = null;
            this.singleSelectedModuleChanged.emit({ id: null, name: null });
            // Emitir filtro actualizado (sin módulo de radio)
            this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
        }

        // Pulsado desde provision-scheme cada vez que cambia el modo de vista (calendario/listado/facturar)
        if (changes['resetChecks'] && changes['resetChecks'].currentValue) {
            // Resetear radio buttons de módulo y órdenes
            this.singleSelectedModuleId = null;
            this.singleSelectedModuleChanged.emit({ id: null, name: null });
            this.ordersRadioSelected = false;
            this.ordersRadioSelectedChanged.emit(false);
            // Colapsar cualquier fila expandida
            this.expandedElement = null;
            this.expandedChild = null;
            this.expandedOrder = null;
            // selectionOrders no está cubierto por clearCheckboxesSelection
            this.selectionOrders.clear();
            // Limpia selectionModules/Provisions/Supplies/Agreements + flags en dataSource.data + emite filtros
            this.clearCheckboxesSelection();
            // También limpiar módulos fuera de dataSource.data (filtrados por rango de fechas)
            for (const cm of (this.caseModulesComposed ?? [])) {
                cm.selected = false;
                for (const prov of (cm?.provisions ?? [])) {
                    (prov as any).checked = false;
                    for (const ag of ((prov as any)?.agreements ?? [])) {
                        (ag as any).checked = false;
                    }
                }
            }
            this.emitSelectedCaseModuleIds();
        }

        // When caseModulesComposed is updated from the parent, update the table data (include is empty)
        if (changes['caseModulesComposed'] && changes['caseModulesComposed'].currentValue.length >= 0) {
            this.buildDataSource();
        }
    }

    // ===== Bootstrap / Subscriptions =====
    suscribeToData() {
        this._attentions = this.modulesFacade.getModulesAttentions$().subscribe((attentions) => {
            this.attentions = attentions;
            this.attentionsINRange = attentions.filter(att => moment(att.fromDate).isBetween(this.dateRange.historyFromDate, this.dateRange.historyToDate, undefined, '[]'));
            this.getTotalsFooter();
            // this.setTableData(this.caseModulesComposed);
            // this.getTotalsFooter();
        })
        this.getModulesFilteredByFinancier();
        this.buildDataSource()
        this._allOrdersWithoutFilter = this.casesFacade.getOrders$().subscribe(or => {
            if (!!or) {
                this.allOrdersWithoutFilter = or;
            }
        });

        this._orders = this.casesFacade.getOrdersDisplayedOnScreen$().subscribe((orders: Order[]) => {
            if (orders?.length == 0) {
                this.ordersDataSource.data = [];
                return;
            }
            let ordersFiltered = orders?.filter(order => !!order.items[0]?.caseModuleId);
            if (!!ordersFiltered) {
                this.orders = ordersFiltered;

                ordersFiltered.forEach(order => {
                    if (order.state.id == 2) {
                        order.paintedType = ViewManagementEntities.SUPPLIES_NOT_DELIVERED;
                    } else {
                        order.paintedType = null;
                    }
                });

                this.setOrderComposeWithProvisionFee();

                // >>>> importante para que la tabla de pedidos se llene
                this.ordersDataSource.data = [...this.orders];
            }
        });

        // Componer orders.items.provisionFee con las provisionFees cargadas en pantalla (igual que en OrderListComponent)
        this._provisionsDisplayedOnScreen = this.provisionsFacade.getProvisionsDisplayedOnScreen$().subscribe(provs => {
            if (!!provs && provs.length > 0) {
                this.provisionFees = provs
                    .filter(prov => prov.isSupply)
                    .flatMap(prov => {
                        const fees = prov.provisionFees || [];
                        fees.forEach(pf => {
                            if (!pf.agreement && prov.agreements?.length) {
                                pf.agreement = prov.agreements.find(ag => ag.provisionFeeId === pf.id) ?? null;
                            }
                        });
                        return fees;
                    });
                this.setOrderComposeWithProvisionFee();
            }
        });


    }

    ngOnDestroy() {
        this.closeSavingOrderDialog();
        if (!!this._caseArrangements) {
            this._caseArrangements.unsubscribe();
        }
        this._caseSub?.unsubscribe();
        this._orders?.unsubscribe();
        this._allOrdersWithoutFilter?.unsubscribe();
        this._provisionsDisplayedOnScreen?.unsubscribe();
        this._moduleDatesSub?.unsubscribe();

        // Limpiar SelectionModels
        this.selectionModules.clear();
        this.selectionProvisions.clear();
        this.selectionSupplies.clear();
        this.selectionAgreements.clear();
        this.selectionOrders.clear();

        // Limpiar el flag `selected` de los módulos (mutado directamente en los objetos Input)
        for (const cm of (this.caseModulesComposed ?? [])) {
            cm.selected = false;
            // Limpiar el flag `checked` de las provisions/agreements
            for (const prov of (cm?.provisions ?? [])) {
                (prov as any).checked = false;
                for (const ag of ((prov as any)?.agreements ?? [])) {
                    (ag as any).checked = false;
                }
            }
        }
    }

    getModulesFilteredByFinancier() {
        this.getmodulesQPS.financierId = this.financierId;
        this.modulesFacade.loadModuleByCode$(this.getmodulesQPS)
            .subscribe(async (moduleToUpdate) => {
                this.modulesFilteredByFinancier = moduleToUpdate.data;

            })
    }

    getTotalsFooter() {
        // TOTAL AGENDADO
        this.totalValueFooter = parseFloat(this.attentionsINRange?.
            reduce((acum, attention) => acum + (!!this.practiceCountedByHours(attention?.practice)
                ? (attention.toDate && attention.fromDate) ? Math.round(moment(attention.toDate).diff(moment(attention.fromDate)) / (1000 * 60 * 60)) : 1 // Attention by hours
                : 1) // Attention by unit
                * attention.provisionFee, 0
            ).toFixed(2))

        // TOTAL FACTURABLE
        this.totalInvoicedFooter = parseFloat(this.attentionsINRange?.
            filter(att => att.state.id === AttentionStates.FACTURABLE).
            reduce((accum, attention) => accum + (!!this.practiceCountedByHours(attention.practice)
                ? attention.quantity ? attention.quantity : 1
                : 1)
                * attention.provisionFee, 0
            ).toFixed(2))

        // TOTAL DEBITABLE
        this.totalDebitedFooter = parseFloat(this.attentionsINRange?.
            filter(att => att.state.id === AttentionStates.DEBITADA).
            reduce((accum, attention) => accum + (!!this.practiceCountedByHours(attention.practice)
                ? attention.quantity ? attention.quantity : 1
                : 1)
                * attention.provisionFee, 0
            ).toFixed(2))
        // TOTAL INFORMABLE
        this.totalDeliveredFooter = parseFloat(this.attentionsINRange?.
            filter(att => att.state.id === AttentionStates.INFORMADA).
            reduce((accum, attention) => accum + (!!this.practiceCountedByHours(attention.practice)
                ? attention.quantity ? attention.quantity : 1
                : 1)
                * attention.provisionFee, 0
            ).toFixed(2))
    }

    // ===== Orders: actions menu (replica OrderList) =====
    onClickConfirmOrDeliverOrder(order: Order): void {
        if (!order?.id || !order?.state?.id) return;
        const caseId = this.case?.id ?? this.caseId;
        if (!caseId) return;

        // Confirmar pedido (CREADO -> EN_CURSO)
        if (order.state.id === this.ORDERS_STATES.CREADO) {
            const patch: PatchOrderBody = { state: { id: this.ORDERS_STATES.EN_CURSO } as any };
            this.casesFacade.updateOrder(patch, order.id, caseId).subscribe({
                next: () => {
                    (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.pedido-confirmado', undefined, 'Pedido confirmado correctamente'), icon: 'success' });
                },
            });
            return;
        }

        // Entregar pedido (EN_CURSO -> ENTREGADO) - abre diálogo de delivery
        if (order.state.id === this.ORDERS_STATES.EN_CURSO) {
            this.openModifyOrderDialog(order, true);
        }
    }

    onClickCancelOrder(order: Order): void {
        if (!order?.id) return;
        const caseId = this.case?.id ?? this.caseId;
        if (!caseId) return;

        const patch: PatchOrderBody = { state: { id: this.ORDERS_STATES.CANCELADO } as any };
        this.swalWithCustomizeButtons.fire({
            title: this.experienceI18n.translate('modules.cases.modules-scheme.atencion', undefined, 'Atención'),
            text: this.experienceI18n.translate('modules.cases.modules-scheme.esta-seguro-que-desea-cancelar', undefined, '¿Está seguro que desea cancelar el pedido?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: (this.experienceI18n.translate('common.confirm', undefined, 'Confirmar')),
            cancelButtonText: (this.experienceI18n.translate('common.cancel', undefined, 'Cancelar')),
            reverseButtons: true,
            showLoaderOnConfirm: true,
            preConfirm: () => this.casesFacade.updateOrder(patch, order.id, caseId).toPromise(),
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((r) => {
            if (r.isConfirmed) {
                (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.pedido-cancelado', undefined, 'Pedido cancelado correctamente'), icon: 'success' });
                this.provisionsFacade.loadAllProvisionsForCase(caseId);
            }
        });
    }

    private validateIsAbleToModifyOrder(order: Order): boolean {
        this.arrayTable = this.orderSharedActionsService.getBlockedOrderItems(order, this.attentions);
        return this.arrayTable.length === 0;
    }

    revertState(order: Order): void {
        if (this.savingOrderDialog || !order?.id) return;

        const caseId = this.case?.id ?? this.caseId;
        if (!caseId) return;

        this.openMessageDialogComponent(`${this.experienceI18n.translate('modules.cases.modules-scheme.esta-seguro-que-desea-revertir', undefined, '¿Está seguro que desea revertir el estado del pedido')} ${order.id}?`);
        const confirmationDialogRef = this._dialogRef;

        confirmationDialogRef?.componentInstance?.ok?.pipe(take(1)).subscribe((confirmed: boolean) => {
            if (!confirmed) {
                confirmationDialogRef.close();
                return;
            }

            const notInformedItems = this.orderSharedActionsService.getBlockedOrderItems(
                order,
                (this.attentions ?? []).filter(
                    attention => attention?.state?.id === AttentionStates.NO_INFORMADA
                )
            );

            if (notInformedItems.length > 0) {
                this.arrayTable = notInformedItems;
                this.showBlockedRevertPopup(
                    this.experienceI18n.translate('modules.cases.modules-scheme.popup.supplies-not-informed', undefined, 'No se puede modificar el estado del pedido porque hay insumos en estado NO INFORMADO, pasalos a estado FACTURABLE para poder revertir la entrega.'),
                    confirmationDialogRef
                );
                return;
            }

            if (!this.validateIsAbleToModifyOrder(order)) {
                this.showBlockedRevertPopup(
                    this.experienceI18n.translate('modules.cases.modules-scheme.los-siguientes-insumos-ya-estan-informados', undefined, 'Los siguientes insumos ya están informados. No se puede modificar el estado del pedido.'),
                    confirmationDialogRef
                );
                return;
            }

            if (order.provider && order.provider.active === false) {
                this.swalWithCustomizeButtons.fire({
                    icon: 'warning',
                    text: this.experienceI18n.translate('modules.cases.modules-scheme.messages.prestador-inhabilitado', undefined, 'No se puede acceder al prestador con el que se quiere operar. Contacte a un superior.'),
                });
                confirmationDialogRef.close();
                return;
            }

            this.swalWithCustomizeButtons.fire({
                icon: 'warning',
                title: this.experienceI18n.translate('modules.cases.modules-scheme.advertencia-de-flujo-normal', undefined, 'Advertencia de flujo anormal'),
                text: this.experienceI18n.translate('modules.cases.modules-scheme.por-favor-escriba-un-motivo', undefined, 'Por favor, escriba un motivo'),
                input: 'textarea',
                inputPlaceholder: this.experienceI18n.translate('modules.cases.modules-scheme.escriba-un-motivo', undefined, 'Escriba un motivo...'),
                inputValidator: (value) => {
                    if (!value || value.trim().length < 10) {
                        return this.experienceI18n.translate('modules.cases.modules-scheme.el-motivo-debe-tener-mas-de-diez-caracteres', undefined, 'El motivo debe tener más de 10 caracteres.');
                    }
                    return '';
                },
                confirmButtonText: this.experienceI18n.translate('common.confirm', undefined, 'Confirmar'),
                cancelButtonText: this.experienceI18n.translate('common.cancel', undefined, 'Cancelar'),
                showLoaderOnConfirm: true,
                showCancelButton: true,
                reverseButtons: true,
                allowOutsideClick: () => !Swal.isLoading(),
            }).then((result) => {
                if (!result.isConfirmed) {
                    confirmationDialogRef.close();
                    return;
                }

                this.openSavingOrderDialog();

                const patchOrderBody: PatchOrderBody = {
                    stateReversed: true,
                    reasonForRevertingOrderStatus: result.value,
                };

                this.casesFacade.updateOrder(patchOrderBody, order.id, caseId).subscribe({
                    next: () => {
                        this.swalWithCustomizeButtons.fire({
                            text: this.experienceI18n.translate('modules.cases.modules-scheme.el-pedido-se-modifico-correctamente', undefined, 'El pedido se modificó correctamente'),
                            icon: 'success',
                            didOpen: () => this.closeSavingOrderDialog(),
                        }).then(() => {
                            confirmationDialogRef.close();
                            this.provisionsFacade.loadAllProvisionsForCase(caseId);
                        });
                    },
                    error: (err: HttpErrorResponse) => {
                        this.closeSavingOrderDialog();
                        confirmationDialogRef.close();

                        if (err.status === 418) {
                            const errorContent: HttpBaseResponseErrorTeaPot = err.error;
                            this.swalWithCustomizeButtons.fire({
                                title: errorContent.message,
                                text: errorContent.data.message,
                                icon: 'error',
                            });
                        }
                    },
                });
            });
        });
    }

    private showBlockedRevertPopup(message: string, confirmationDialogRef: MatDialogRef<any>): void {
        this.message = message;
        this.showConfirmBtn = false;
        this.closeAll = false;
        this._popupConfirmCallback = null;
        this.txtCancelBtn = this.experienceI18n.translate('common.ok', undefined, 'Ok');
        this.openDialog(
            this.experienceI18n.translate('modules.cases.modules-scheme.atencion', undefined, 'Atención'),
            this.popUpMessage,
            { minWidth: '30%', maxHeight: '70vh' },
            () => confirmationDialogRef.close()
        );
    }

    private openMessageDialogComponent(message: string): void {
        this._dialogRef = this.dialog.open(MessageDialogComponent, {
            disableClose: false,
            maxHeight: '20%',
            maxWidth: '50vh',
            data: {
                message,
                icon: 'WarningCircle',
                disableConfirm: true,
            },
        });
    }

    private openSavingOrderDialog(): void {
        this.savingOrderDialog = this.dialog.open(this.savingOrderTemplate, {
            width: '100px',
            height: '100px',
            panelClass: 'open-saving-order-spinner',
            disableClose: true,
            autoFocus: 'dialog',
            delayFocusTrap: false,
            restoreFocus: false,
            enterAnimationDuration: 0,
            exitAnimationDuration: 0,
        });
    }

    private closeSavingOrderDialog(): void {
        this.savingOrderDialog?.close();
        this.savingOrderDialog = null;
    }

    showDeliveryNote(order: Order): void {
        this.orderSharedActionsService.getDeliveryNoteContext(order, this.attentions)
            .then(({ attention, deliveryNote }) => {
                if (!attention?.id) {
                    (this.swalAutoClose ?? Swal).fire({
                        text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-encontro-la-atencion-asociada-al-pedido', undefined, 'No se encontró la atención asociada al pedido.'),
                        icon: 'info',
                    });
                    return;
                }

                if (!deliveryNote) {
                    (this.swalAutoClose ?? Swal).fire({
                        text: this.experienceI18n.translate('modules.cases.modules-scheme.el-pedido-no-tiene-remito-disponible', undefined, 'El pedido no tiene remito disponible.'),
                        icon: 'info',
                    });
                    return;
                }

                attention.evolutions = [deliveryNote];
                this._bottomSheet.open(EvolutionsDialogComponent, {
                    panelClass: 'bottomSheetPanelClass',
                    data: {
                        evolutions: [deliveryNote],
                        name: this.case.patient.name,
                        surname: this.case.patient.surname,
                        attentions: [attention],
                    },
                });
            })
            .catch(() => {
                (this.swalAutoClose ?? Swal).fire({
                    text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-obtener-el-remito-del-pedido', undefined, 'No se pudo obtener el remito del pedido.'),
                    icon: 'error',
                });
            });
    }

    onClickGenerateDeliveryNote(order: Order): void {
        this.generateDeliveryNote(order);
    }

    onClickDeleteOrder(order: Order): void {
        this.onClickCancelOrder(order);
    }

    onClickCancel(): void {
        this._popupConfirmCallback = null;
        this._dialogRef?.close();
        if (this.closeAll) {
            this.dialog.closeAll();
        }
    }

    onClickPopupConfirm(): void {
        if (this._popupConfirmCallback) {
            const cb = this._popupConfirmCallback;
            this._popupConfirmCallback = null;
            this._dialogRef?.close();
            cb();
        } else {
            this.onClickUpdateOrder();
        }
    }

    generateDeliveryNote(order: Order) {
        this.orderSharedActionsService.downloadDeliveryNotePdf(order, this.case?.patient?.id);
    }

    public generateDeliveryNotePDF(order: Order) {
        this.orderSharedActionsService.downloadDeliveryNotePdf(order, this.case?.patient?.id);
    }

    // ===== Delivery Order Dialog (replica order-list) =====
    openModifyOrderDialog(order: Order, edition: boolean = true): void {
        this.isBillable = false;
        this.actualOrder = order;
        this.deliveringOrder = (order.state.id === this.ORDERS_STATES.EN_CURSO || order.state.id === this.ORDERS_STATES.ENTREGADO);
        this.editingOrder = edition;
        this.formModifyOrder = this.createModifyOrderForm(order);

        let title: string;
        if (this.deliveringOrder && this.editingOrder && order.state.id !== this.ORDERS_STATES.ENTREGADO) {
            title = this.experienceI18n.translate('modules.cases.modules-scheme.cargar-entrega', undefined, 'Cargar entrega');
        } else if (this.deliveringOrder && this.editingOrder && order.state.id === this.ORDERS_STATES.ENTREGADO) {
            title = this.experienceI18n.translate('modules.cases.modules-scheme.editar-entrega', undefined, 'Editar entrega');
        } else if (!this.editingOrder) {
            title = this.experienceI18n.translate('modules.cases.modules-scheme.ver-pedido', undefined, 'Ver pedido');
        } else {
            title = this.experienceI18n.translate('modules.cases.modules-scheme.modificar-pedido', undefined, 'Modificar Pedido');
        }

        this.dataSourceSupplies.data = this.itemsFormArrayModify.controls;

        if ((this.editingOrder && this.validateIsAbleToModifyOrder(order)) || !this.editingOrder) {
            setTimeout(() => {
                this._dialogRefModifyOrder = this.dialog.open(DialogComponent, {
                    disableClose: true,
                    minWidth: this.deliveringOrder ? '90%' : '50%',
                    maxHeight: '95vh',
                    data: { template: this.modifyOrderTemplate, title },
                });
                this._dialogRefModifyOrder.afterClosed().subscribe(() => {
                    this.deliveringOrder = false;
                });
            }, 200);
            return;
        }

        this.message = this.experienceI18n.translate('modules.cases.modules-scheme.los-siguientes-insumos-ya-estan-informados', undefined, 'Los siguientes insumos ya están informados. No se puede modificar el estado del pedido.');
        this.showConfirmBtn = false;
        this.closeAll = false;
        this.txtCancelBtn = this.experienceI18n.translate('modules.cases.modules-scheme.ok', undefined, 'Ok');
        this.openDialog(this.experienceI18n.translate('modules.cases.modules-scheme.atencion', undefined, 'Atención'), this.popUpMessage, { minWidth: '30%', maxHeight: '70vh' }, () => { });
    }

    createModifyOrderForm(order: Order): FormGroup {
        const form = this.formBuilder.group({
            [this.ORDER_PROVIDER]: [order.provider?.id?.toString() ?? '', [CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.proveedor-requerido', undefined, 'Proveedor requerido'))]],
            [this.ORDER_PROVIDER_SEARCHING]: [''],
            [this.ORDER_DATE]: [order.orderDate ?? '', [CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida'))]],
            [this.ORDER_DATE_DELIVERY]: [order.deliveryDate ?? order.orderDate ?? ''],
            [this.ORDER_OBSERVATIONS]: [order.observations ?? ''],
            [this.ORDER_CONFIRMED]: [order.state?.id === this.ORDERS_STATES.EN_CURSO],
            [this.ORDER_ITEMS]: this.formBuilder.array((order.items ?? []).map((item) => this.createModifyOrderItemForm(item))),
            [this.IS_BILLABLE]: [''],
        });

        if (this.deliveringOrder) {
            form.get(this.ORDER_PROVIDER)?.disable();
            form.get(this.ORDER_DATE)?.disable();
            form.get(this.ORDER_CONFIRMED)?.disable();
            form.get(this.ORDER_DATE_DELIVERY)?.setValidators(CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-de-entrega-requerida', undefined, 'Fecha de entrega requerida')));
        }

        if (!this.editingOrder) {
            form.get(this.ORDER_OBSERVATIONS)?.disable();
            form.get(this.ORDER_DATE_DELIVERY)?.disable();
            form.get(this.ORDER_ITEMS)?.disable();
        }

        return form;
    }

    private createModifyOrderItemForm(item: Item): FormGroup {
        const moduleName = this.getModuleNameForItem(item);

        const form = this.formBuilder.group({
            [this.ITEM_ID]: [item?.id],
            [this.ITEM_QUANTITY]: [item?.quantityOrdered, CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.cantidad-requerida', undefined, 'Cantidad requerida'))],
            [this.ITEM_PRICE]: [item?.priceOrdered, CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.precio-requerido', undefined, 'Precio requerido'))],
            [this.ITEM_QUANTITY_DELIVERY]: [item?.quantityDelivered ?? item?.quantityOrdered ?? ''],
            [this.ITEM_PRICE_DELIVERY]: [item?.priceDelivered ?? item?.priceOrdered ?? ''],
            [this.ITEM_UNIT]: [item?.unit],
            [this.ITEM_PRACTICE_DELIVERY]: [item?.practice?.id?.toString() ?? ''],
            [this.ITEM_PRACTICE_SEARCHING]: [''],
            [this.ITEM_PRACTICE]: [item?.practice],
            [this.MODULE_NAME]: [moduleName],
            [this.ITEM_CASE_MODULE_ID]: [item?.caseModuleId ?? item?.provisionFee?.caseModuleId ?? null],
        });

        if (this.deliveringOrder) {
            form.get(this.ITEM_PRACTICE)?.disable();
            form.get(this.ITEM_QUANTITY)?.disable();
            form.get(this.ITEM_PRICE)?.disable();
            form.get(this.ITEM_PRICE_DELIVERY)?.setValidators([CustomValidators.minValue(0, this.experienceI18n.translate('modules.cases.modules-scheme.precio-requerido', undefined, 'Precio requerido'))]);
            form.get(this.ITEM_QUANTITY_DELIVERY)?.setValidators([CustomValidators.minValue(0, this.experienceI18n.translate('modules.cases.modules-scheme.cantidad-requerida', undefined, 'Cantidad requerida'))]);
        }
        if (this.actualOrder?.state?.id === this.ORDERS_STATES.ENTREGADO) {
            form.get(this.ITEM_PRACTICE_DELIVERY)?.disable();
        }
        if (!this.editingOrder) {
            form.get(this.ITEM_PRICE_DELIVERY)?.disable();
            form.get(this.ITEM_QUANTITY_DELIVERY)?.disable();
            form.get(this.ITEM_PRACTICE_DELIVERY)?.disable();
        }
        return form;
    }

    getModuleNameForItem(item: Item): string {
        // El item puede tener caseModuleId directo (orders) o en provisionFee
        const caseModuleId = item?.caseModuleId ?? item?.provisionFee?.caseModuleId;
        if (!caseModuleId) return '-';
        const cm = this.caseModulesComposed?.find(m => m.id === caseModuleId);
        return cm?.name ?? '-';
    }

    /** Obtiene el nombre del módulo para un item del form de delivery usando el index */
    getModuleNameForDeliveryItem(index: number): string {
        const ctrl = this.getItemsFormArrayDelivery(index);
        if (!ctrl) return '-';
        const caseModuleId = ctrl.get(this.ITEM_CASE_MODULE_ID)?.value;
        if (!caseModuleId) return '-';
        const cm = this.caseModulesComposed?.find(m => m.id === caseModuleId);
        return cm?.name ?? '-';
    }

    get itemsFormArrayModify(): FormArray {
        return this.formModifyOrder?.get(this.ORDER_ITEMS) as FormArray;
    }

    getItemsFormArrayDelivery(index: number): FormGroup {
        return this.itemsFormArrayModify?.at(index) as FormGroup;
    }

    onClickCloseModifyOrderDialog(): void {
        this._dialogRefModifyOrder?.close();
        this.dialog.closeAll();
        this.deliveringOrder = false;
    }

    onClickConfirmDelivery(): void {
        if (this.deliveringOrder && this.isBillable) {
            const itemsWithCost0 = this.formModifyOrder.getRawValue()?.items?.filter((i: any) => Number(i?.priceDelivered ?? 0) === 0);
            if (itemsWithCost0?.length > 0) {
                this.swalWithCustomizeButtons.fire({
                    title: this.experienceI18n.translate('modules.cases.modules-scheme.atencion', undefined, 'Atención'),
                    text: this.experienceI18n.translate('modules.cases.modules-scheme.algunos-insumos-tienen-costo-cero', undefined, 'Algunos insumos tienen costo 0. ¿Desea continuar?'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: this.experienceI18n.translate('common.continue', undefined, 'Continuar'),
                    cancelButtonText: this.experienceI18n.translate('common.cancel', undefined, 'Cancelar'),
                    reverseButtons: true,
                }).then((r: any) => {
                    if (r.isConfirmed) this.onClickUpdateOrder();
                });
                return;
            }
        }
        this.onClickUpdateOrder();
    }

    onClickUpdateOrder(): void {
        const caseId = this.case?.id ?? this.caseId;
        if (!caseId || !this.actualOrder?.id) return;

        const formValue: any = { ...this.formModifyOrder.value };

        if (this.deliveringOrder) {
            // Manejo de foto de remito
            if (!this.fileChanged) {
                delete formValue.invoicePhoto;
            } else {
                if (this.photoInvoice) {
                    formValue.invoicePhoto = this.photoInvoice;
                } else {
                    delete formValue.invoicePhoto;
                }
            }

            formValue.state = { id: this.ORDERS_STATES.ENTREGADO };

            // Procesar items igual que order-list: partir del value y limpiar campos innecesarios
            formValue.items = (formValue.items ?? []).map((item: any) => {
                const rawItem = this.formModifyOrder.getRawValue()?.items?.find((it: any) => it.id === item.id);

                // Eliminar campos que no van al backend
                delete item.unit;
                delete item.practiceItemSearching;
                delete item.practiceItemDelivered;
                delete item.moduleName;
                delete item.practice;

                // Agregar caseModuleId desde rawValue si existe
                if (rawItem?.caseModuleId) {
                    item.caseModuleId = rawItem.caseModuleId;
                }

                item.fee = rawItem?.fee ?? 0;
                item.profitMargin = rawItem?.profitMargin ?? 0;
                item.fixedPrice = rawItem?.fixedPrice ?? false;

                return item;
            });

            // Si isBillable es false, no enviarlo
            if (!this.isBillable) {
                delete formValue.isBillable;
            }
        } else {
            // Modo edición (no delivering)
            if (formValue.items) {
                formValue.items = (formValue.items ?? []).map((item: any) => {
                    const rawItem = this.formModifyOrder.getRawValue()?.items?.find((it: any) => it.id === item.id);

                    item.practiceId = item.practice?.id;

                    // Agregar caseModuleId desde rawValue si existe
                    if (rawItem?.caseModuleId) {
                        item.caseModuleId = rawItem.caseModuleId;
                    }

                    item.fee = rawItem?.fee ?? 0;
                    item.profitMargin = rawItem?.profitMargin ?? 0;
                    item.fixedPrice = rawItem?.fixedPrice ?? false;

                    delete item.practice;
                    delete item.practiceItemDelivered;
                    delete item.practiceItemSearching;
                    delete item.priceDelivered;
                    delete item.quantityDelivered;
                    delete item.moduleName;

                    return item;
                });
            }

            formValue.providerId = parseInt(formValue.providerId);
            if (formValue.state) {
                formValue.state = { id: this.ORDERS_STATES.EN_CURSO };
            } else {
                delete formValue.state;
            }
        }

        // Cleanup general
        delete formValue.providerSearching;
        delete formValue.deliveryNote;

        // Limpiar propiedades vacías excepto observations
        for (const property in formValue) {
            if (!formValue[property] && property !== this.ORDER_OBSERVATIONS) {
                delete formValue[property];
            }
        }

        // Si estamos editando un pedido ya entregado
        if (this.editingOrder && this.actualOrder.state.id === this.ORDERS_STATES.ENTREGADO) {
            delete formValue.state;
            formValue.edition = true;
        }

        this.casesFacade.updateOrder(formValue, this.actualOrder.id, caseId).subscribe({
            next: () => {
                this.swalWithCustomizeButtons.fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.el-pedido-se-modifico-correctamente', undefined, 'Pedido modificado correctamente'), icon: 'success' }).then(() => {
                    this._dialogRefModifyOrder?.close();
                    this.dialog.closeAll();
                    this.provisionsFacade.loadAllProvisionsForCase(caseId);
                });
            },
            error: () => {
                this._dialogRefModifyOrder?.close();
            },
        });
    }

    onChangeCheckIsBillable(isChecked: boolean): void {
        this.isBillable = isChecked;
    }

    orderDeliveryPondHandleInit(): void {
        this.photoInvoice = null;
        this.fileChanged = false;
        const caseId = this.case?.id ?? this.caseId;
        if (!caseId || !this.actualOrder?.id) return;

        this.casesService.getInvoicePhotoByOrderId(this.actualOrder.id, caseId).subscribe({
            next: (media: any) => {
                if (media?.mediaUrl) {
                    this.photoInvoice = media.mediaUrl;
                    // FilePond se cargará por referencia desde el template
                }
            },
            error: () => {
                this.photoInvoice = null;
            },
        });
    }

    orderDeliveryPondHandleAddFile(event: any): void {
        if (!event.error) {
            if (this.photoInvoice === null) {
                const b64 = event.file.getFileEncodeBase64String();
                const extension = event.file.fileExtension;
                const mimeType = mime.getType(extension);
                this.photoInvoice = `data:${mimeType};base64,${b64}`;
                this.fileChanged = true;
            }
        } else {
            this.photoInvoice = null;
        }
        this.loadingAddFile = false;
    }

    orderDeliveryPondHandleRemoveFile(): void {
        this.photoInvoice = null;
        this.fileChanged = true;
    }

    orderDeliveryPondHandleProcessFile(): void {
        this.loadingAddFile = true;
    }

    // ===== Table data =====
    buildDataSource() {
        this.caseModulesComposed.forEach(cm => {
            const supplyProvisions = cm.provisions.filter(prov => prov?.isSupply);
            const practiceProvisions = cm.provisions.filter(prov => !prov?.isSupply);

            cm.provisions = [...practiceProvisions, ...supplyProvisions];
        });

        this.setTableData(this.caseModulesComposed);
    }

    private setTableData(modules: CaseModuleComposed[]): void {
        // Guardar IDs de filas expandidas antes de refrescar
        const prevExpandedId = this.expandedElement?.id ?? null;
        const prevExpandedChildId = this.expandedChild?.id ?? null;
        const prevSupplyGroups = new Set(this.expandedSupplyGroups);

        //this.dataSource.data = this.filterModulesByDateRange(modules ?? []);
        this.dataSource.data = this.modulesService.filterModulesByDateRange(this.dateRange, modules);
        // Sincroniza selection model desde el flag `selected`
        this.selectionModules.clear();
        for (const m of this.dataSource.data) {
            if (m?.selected) this.selectionModules.select(m);
        }

        // Limpiar caches de datasources hijos (se reconstruyen lazy)
        this.clearChildCache();

        // Restaurar expansiones por ID
        this.expandedElement = prevExpandedId != null
            ? this.dataSource.data.find(m => m.id === prevExpandedId) ?? null
            : null;

        if (this.expandedElement && prevExpandedChildId != null) {
            const childRows = this.getChildDataSource(this.expandedElement).data;
            this.expandedChild = childRows.find(c => c.id === prevExpandedChildId) ?? null;
        } else {
            this.expandedChild = null;
        }

        // Restaurar supply groups expandidos
        this.expandedSupplyGroups = prevSupplyGroups;

        // Al primer cargado con datos, emitir filtro (comporta como todo seleccionado)
        if (!this._initialSelectionDone && this.dataSource.data.length > 0) {
            this._initialSelectionDone = true;
            this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
        }
    }

    // ===== Table selection =====
    selectModule(row: CaseModuleComposed, masterToggle: boolean = false) {
        if (!masterToggle) {
            this.selectionModules.toggle(row);
        } else {
            if (!this.selectionModules.selected.some(sel => sel.id === row.id)) {
                this.selectionModules.select(row);
            }
        }

        // Mantiene el flag del modelo actualizado
        row.selected = this.isModuleSelected(row);
        this.emitSelectedCaseModuleIds();

        // Emitir filtros actualizados
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
    }

    isModuleSelected(row: CaseModuleComposed) {
        return this.selectionModules.selected.map(sel => sel.id).includes(row.id);
    }

    isAllSelected(): boolean {
        const total = this.dataSource.data.length;
        const selected = this.selectionModules.selected.length;
        return total > 0 && selected === total;
    }

    masterToggle() {
        if (this.isAllSelected()) {
            this.clearCheckboxesSelection();
        } else {
            this.dataSource.data.forEach(row => this.selectModule(row, true));
        }
        this.emitSelectedCaseModuleIds();

        // Emitir filtros actualizados
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
    }

    clearCheckboxesSelection() {
        this.clearCheckboxesWithoutEmit();
        // Emitir filtros actualizados
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
    }

    /** Limpia checkboxes sin emitir filtros (para uso interno cuando el caller ya emite) */
    private clearCheckboxesWithoutEmit(): void {
        this.selectionModules.clear();
        this.selectionProvisions.clear();
        this.selectionSupplies.clear();
        this.selectionAgreements.clear();
        this.selectionOrders.clear();
        this.dataSource.data.forEach(m => {
            m.selected = false;
            for (const prov of (m?.provisions ?? [])) {
                (prov as any).checked = false;
                for (const agr of ((prov as any)?.agreements ?? [])) {
                    (agr as any).checked = false;
                }
            }
        });
    }

    verifyModuleSelection(module: CaseModuleComposed): boolean {
        return this.selectionModules.selected.map(mod => mod.id).includes(module.id);
    }

    private clearSelectedModuleFromForm(): void {
        if (!this.formModule) return;
        // Limpia el input de módulo + searching
        this.formModule.get(this.MODULE)?.patchValue(null);
        // Limpia estado UI relacionado al módulo seleccionado
        this.moduleSelected = null;
        this.moduleSelectedHasPractices = false;
        this.moduleSelectedHasSupplies = false;
        this.practicesPanelExpanded = true;
        this.suppliesPanelExpanded = true;
        this.practicesSelection.clear();
        this.suppliesSelection.clear();

        this.editingModuleRow = null;
    }

    private resolveModuleFromComposedRow(row: CaseModuleComposed): Module | null {
        const byId = this.modulesFilteredByFinancier?.find(m => m?.id === row?.id);
        if (byId) return byId;

        const rowName = String(row?.name ?? '').trim().toLowerCase();
        if (!rowName) return null;

        const byName = this.modulesFilteredByFinancier?.find(
            m => String(m?.name ?? '').trim().toLowerCase() === rowName
        );
        return byName ?? null;
    }

    toggleRow(row: CaseModuleComposed): void {
        this.moduleSelected = row;
        const isExpanding = this.expandedElement !== row;
        this.expandedElement = isExpanding ? row : null;

        if (!this.formModule) return;

        // Al colapsar, limpiamos el input de módulo y el estado relacionado
        if (!isExpanding) {
            this.clearSelectedModuleFromForm();
            return;
        }

        // Al expandir, sincroniza la selección del módulo en el input del form (y el texto de búsqueda)
        const resolvedModule = this.resolveModuleFromComposedRow(row);
        const moduleId = resolvedModule?.id ?? row.id;

        this.formModule.get(this.MODULE)?.patchValue(moduleId);
        this.formModule.get(this.MODULE_SEARCHING)?.patchValue(row.name);

        // Reusa el flujo normal de selección (carga items + sincroniza moduleItemIds)
        this.onSelectModule(moduleId);
    }

    trackByPracticePickerRow = (_: number, item: { index: number; fg: FormGroup }) => {
        return item.fg.get('practiceId')?.value ?? item.index;
    };

    childDS = new Map<number, MatTableDataSource<ProvisionRow>>();

    getChildDataSource(row: CaseModuleComposed): MatTableDataSource<ProvisionRow> {
        const moduleId = row.id ?? -1;

        const existing = this.childDS.get(moduleId);
        if (existing) return existing;

        const children: ProvisionRow[] = (row.provisions ?? []).map((p: Provision) => this.mapProvisionToChildRow(p));
        const ds = new MatTableDataSource<ProvisionRow>(children);

        this.childDS.set(moduleId, ds);
        return ds;
    }

    private coalesceFiniteNumber(...values: unknown[]): number {
        for (const v of values) {
            const n = Number(v);
            if (Number.isFinite(n)) return n;
        }
        return 0;
    }

    private isSupplyProvision(prov: Provision): boolean {
        return (prov as any)?.isSupply === true;
    }

    private getCheckedPracticeIdsFromModuleRow(row: CaseModuleComposed): Set<number> {
        const practiceProvisions = (row?.provisions ?? []).filter(p => !this.isSupplyProvision(p));
        const selectAll = !this.hasSelectedProvisionInModuleRow(row);

        const ids = practiceProvisions
            .filter(p => {
                // Sin selección en ninguna categoría, se renueva el módulo completo.
                if (selectAll) return true;
                return !!(p as any)?.checked;
            })
            .map(p => this.coalesceFiniteNumber((p as any)?.practiceId, (p as any)?.practice?.id))
            .filter(n => Number.isFinite(n) && n > 0);
        return new Set(ids);
    }

    private getCheckedSupplyIdsFromModuleRow(row: CaseModuleComposed): Set<number> {
        const supplyProvisions = (row?.provisions ?? []).filter(p => this.isSupplyProvision(p));
        const selectAll = !this.hasSelectedProvisionInModuleRow(row);

        const ids = supplyProvisions
            .filter(p => {
                // Si se seleccionó algo en la otra categoría, este conjunto queda vacío.
                if (selectAll) return true;
                return !!(p as any)?.checked;
            })
            .map(p => this.coalesceFiniteNumber((p as any)?.practiceId, (p as any)?.practice?.id))
            .filter(n => Number.isFinite(n) && n > 0);
        return new Set(ids);
    }

    private hasSelectedProvisionInModuleRow(row: CaseModuleComposed): boolean {
        return (row?.provisions ?? []).some(p => !!(p as any)?.checked);
    }

    private getExistingSupplyIdsFromCaseModuleRow(row: CaseModuleComposed): Set<number> {
        const provisions = row?.provisions ?? [];
        const ids = provisions
            .filter(p => this.isSupplyProvision(p))
            .map(p => this.coalesceFiniteNumber((p as any)?.practiceId, (p as any)?.practice?.id))
            .filter(n => Number.isFinite(n) && n > 0);
        return new Set(ids);
    }

    isSupplyAlreadyAddedInCaseModuleForPicker(practiceId: any): boolean {
        const id = Number(practiceId);
        return Number.isFinite(id) && this.existingSupplyIdsForPicker.has(id);
    }

    private buildLockedPracticesFromCaseModuleRow(row: CaseModuleComposed): Map<number, ModulePracticeSelected> {
        const provisions = row?.provisions ?? [];
        const map = new Map<number, ModulePracticeSelected>();

        for (const p of provisions) {
            if (this.isSupplyProvision(p)) continue;

            const anyP = p as any;
            const pr = anyP?.practice ?? {};

            const practiceId = this.coalesceFiniteNumber(anyP?.practiceId, pr?.id);
            if (!Number.isFinite(practiceId) || practiceId <= 0) continue;

            const freq = anyP?.frequency ?? {};
            const amount = freq?.amount ?? anyP?.frequencyAmount ?? null;
            const unit = freq?.unit ?? anyP?.frequencyUnit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT;
            const allowedHours = freq?.allowedHours ?? anyP?.allowedHours ?? null;

            map.set(practiceId, {
                practiceId,
                code: pr?.code ?? pr?.nomenclatorCode ?? String(anyP?.practiceId ?? ''),
                name: pr?.name ?? anyP?.practiceName ?? pr?.description ?? '—',
                frequencyAmount: this.toFiniteOrNull(amount),
                frequencyUnit: unit,
                allowedHours: this.toFiniteOrNull(allowedHours),
                minAttentions: this.toFiniteOrNull(anyP?.minAttentions),
                maxAttentions: this.toFiniteOrNull(anyP?.maxAttentions),
                restrictionPatient: isPatientPracticeRestriction(pr?.restriction),
            });
        }

        return map;
    }

    private hydrateSelectedListsFromEditingRow(row: CaseModuleComposed): void {
        const provisions = row?.provisions ?? [];

        const checkedPracticeIds = this.getCheckedPracticeIdsFromModuleRow(row);
        const checkedSupplyIds = this.getCheckedSupplyIdsFromModuleRow(row);

        // PRACTICES
        this.modulePracticesSelected = provisions
            .filter(p => !this.isSupplyProvision(p))
            .filter(p => checkedPracticeIds.has(this.coalesceFiniteNumber((p as any)?.practiceId, (p as any)?.practice?.id)))
            .map(p => {
                const anyP = p as any;
                const pr = anyP?.practice ?? {};
                const freq = anyP?.frequency ?? {};
                const amount = freq?.amount ?? anyP?.frequencyAmount ?? null;
                const unit = freq?.unit ?? anyP?.frequencyUnit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT;

                return {
                    practiceId: this.coalesceFiniteNumber(anyP?.practiceId, pr?.id),
                    code: pr?.code ?? pr?.nomenclatorCode ?? String(anyP?.practiceId ?? ''),
                    name: pr?.name ?? anyP?.practiceName ?? pr?.description ?? '—',
                    frequencyAmount: this.toFiniteOrNull(amount),
                    frequencyUnit: unit,
                    allowedHours: this.toNumberOrNull(freq?.allowedHours ?? anyP?.allowedHours),
                    restrictionId: this.toNumberOrNull(pr?.restriction?.id ?? anyP?.restrictionId),
                    restrictionRequiredTime: isGuardiaLikeRestriction(pr?.restriction),
                    minAttentions: this.toFiniteOrNull(anyP?.minAttentions) ?? 0,
                    maxAttentions: this.toFiniteOrNull(anyP?.maxAttentions),
                    restrictionPatient: isPatientPracticeRestriction(pr?.restriction),
                } as ModulePracticeSelected;
            });

        // SUPPLIES
        this.moduleSuppliesSelected = provisions
            .filter(p => this.isSupplyProvision(p))
            .filter(p => checkedSupplyIds.has(this.coalesceFiniteNumber((p as any)?.practiceId, (p as any)?.practice?.id)))
            .map(p => {
                const anyP = p as any;
                const pr = anyP?.practice ?? {};
                const freq = anyP?.frequency ?? {};
                const amount = freq?.amount ?? anyP?.frequencyAmount ?? null;
                const unit = freq?.unit ?? anyP?.frequencyUnit ?? ProvisionsUnit.UNIDAD;

                const totalCost = this.toFiniteOrNull(anyP?.totalCost ?? anyP?.totalValue ?? anyP?.totalAmount ?? anyP?.fee);
                const qty = this.toFiniteOrNull(amount);
                const unitCost = this.toFiniteOrNull(anyP?.unitCost) ?? (totalCost != null && qty ? totalCost / qty : null);

                return {
                    practiceId: this.coalesceFiniteNumber(anyP?.practiceId, pr?.id),
                    code: pr?.code ?? pr?.nomenclatorCode ?? String(anyP?.practiceId ?? ''),
                    name: pr?.name ?? anyP?.practiceName ?? pr?.description ?? '—',
                    providerId: this.toFiniteOrNull(anyP?.providerId) ?? null,
                    provider: null,
                    providerName: this.resolveSupplyProviderNameFromProvision(p),
                    quantity: qty,
                    unit: unit,
                    unitCost: unitCost,
                } as ModuleSupplySelected;
            });

        // Mantener el form centralizado en sync
        if (this.formModule?.get?.(this.PRACTICE_ITEMS)) {
            this.syncPracticeItemsToForm(this.modulePracticesSelected);
        }
        if (this.formModule?.get?.(this.SUPPLY_ITEMS)) {
            this.syncSupplyItemsToForm(this.moduleSuppliesSelected);
        }
    }

    private resolveSupplyProviderNameFromProvision(prov: Provision): string | null {
        const anyProv = prov as any;

        const currentProvisionFee = this.resolveProvisionFeeForDisplay(prov) as any;
        const agreement = currentProvisionFee?.agreement ?? anyProv?.agreements?.[0] ?? null;
        const provider = agreement?.provider as Provider | undefined;
        const fromAgreement = String(
            provider?.fullName ||
            (provider?.name || provider?.surname ? `${provider?.name ?? ''} ${provider?.surname ?? ''}`.trim() : '')
        ).trim();
        if (fromAgreement) return fromAgreement;

        const fromFields = String(
            anyProv?.vendor ??
            anyProv?.providerName ??
            currentProvisionFee?.providerName ??
            ''
        ).trim();
        return fromFields || null;
    }


    private getModuleId(module: CaseModuleComposed): number {
        return module?.id ?? -1;
    }

    private getSupplyGroupKey(module: CaseModuleComposed, category: string): string {
        return `${this.getModuleId(module)}|${category}`;
    }

    isSupplyGroupRow = (_: number, row: any): boolean => typeof row === 'string';
    isSupplyItemRow = (_: number, row: any): boolean => typeof row !== 'string';

    /**
     * Key estable para SelectionModel (no depende de la referencia del objeto).
     * Si hay id numérico usamos id, si no caemos a un identificador por práctica (code+name).
     */
    private getPracticesByModule(module: CaseModuleComposed): Provision[] {
        return (module?.provisions ?? []).filter(p => !this.isSupplyProvision(p));
    }

    private getSuppliesByModule(module: CaseModuleComposed): Provision[] {
        return (module?.provisions ?? []).filter(p => this.isSupplyProvision(p));
    }

    hasPracticesOrSupplies(module: CaseModuleComposed): boolean {
        return (module?.provisions?.length ?? 0) > 0;
    }

    /** Datasource simple para la tabla de prácticas (solo no-insumos) */
    getPracticesTableData(module: CaseModuleComposed): Provision[] {
        return this.getPracticesByModule(module);
    }

    // ====== PRACTICAS (tabla hija) ======
    isAllPracticesRowsSelected(module: CaseModuleComposed): boolean {
        const items = this.getPracticesByModule(module);
        if (items.length === 0) return false;
        return items.every(p => this.selectionProvisions.selected.some(sel => sel.id === (p as any).id));
    }

    isSomePracticesRowsSelected(module: CaseModuleComposed): boolean {
        const items = this.getPracticesByModule(module);
        if (items.length === 0) return false;
        const selectedCount = items.filter(p => this.selectionProvisions.selected.some(sel => sel.id === (p as any).id)).length;
        return selectedCount > 0 && selectedCount < items.length;
    }

    toggleAllPracticesRows(module: CaseModuleComposed, checked: boolean): void {
        const items = this.getPracticesByModule(module);
        if (checked) {
            for (const p of items) {
                if (!this.selectionProvisions.selected.some(sel => sel.id === (p as any).id)) {
                    this.selectionProvisions.select(p);
                }
            }
        } else {
            for (const p of items) {
                const existing = this.selectionProvisions.selected.find(sel => sel.id === (p as any).id);
                if (existing) this.selectionProvisions.deselect(existing);
                this.deselectAgreementsForPractice(p);
            }
        }
        for (const p of items) (p as any).checked = checked;
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
    }

    isPracticeRowSelected(module: CaseModuleComposed, prov: Provision): boolean {
        return this.selectionProvisions.selected.some(sel => sel.id === (prov as any).id);
    }

    togglePracticeRow(module: CaseModuleComposed, prov: Provision, checked: boolean): void {
        if (checked) {
            if (!this.selectionProvisions.selected.some(sel => sel.id === (prov as any).id)) {
                this.selectionProvisions.select(prov);
            }
        } else {
            const existing = this.selectionProvisions.selected.find(sel => sel.id === (prov as any).id);
            if (existing) this.selectionProvisions.deselect(existing);
            this.deselectAgreementsForPractice(prov);
        }
        (prov as any).checked = checked;
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
    }

    private deselectAgreementsForPractice(prov: Provision): void {
        const agreements = (prov as any)?.agreements ?? [];
        for (const agreement of agreements) {
            const id = this.getAgreementId(agreement);
            if (id != null) {
                const existing = this.selectionAgreements.selected.find(sel => sel.id === id);
                if (existing) this.selectionAgreements.deselect(existing);
                (agreement as any).checked = false;
            }
        }
    }

    // ====== INSUMOS (tabla hija) ======
    // Clave por provisionFeeId (para deduplicar insumos con mismo practiceId)
    private getSupplyKey(prov: Provision): string {
        return (prov as any)?.provisionFeeId?.toString() ?? '';
    }

    isAllSuppliesRowsSelected(module: CaseModuleComposed): boolean {
        const items = this.getSuppliesByModule(module);
        if (items.length === 0) return false;
        return items.every(p => this.selectionSupplies.selected.some(sel => (sel as any).provisionFeeId === (p as any).provisionFeeId));
    }

    isSomeSuppliesRowsSelected(module: CaseModuleComposed): boolean {
        const items = this.getSuppliesByModule(module);
        if (items.length === 0) return false;
        const selectedCount = items.filter(p =>
            this.selectionSupplies.selected.some(sel => (sel as any).provisionFeeId === (p as any).provisionFeeId)
        ).length;
        return selectedCount > 0 && selectedCount < items.length;
    }

    toggleAllSuppliesRows(module: CaseModuleComposed, checked: boolean): void {
        const items = this.getSuppliesByModule(module);
        if (checked) {
            for (const p of items) {
                if (!this.selectionSupplies.selected.some(sel => (sel as any).provisionFeeId === (p as any).provisionFeeId)) {
                    this.selectionSupplies.select(p);
                }
            }
        } else {
            for (const p of items) {
                const pfid = (p as any).provisionFeeId;
                const existing = this.selectionSupplies.selected.find(sel => (sel as any).provisionFeeId === pfid);
                if (existing) this.selectionSupplies.deselect(existing);
            }
        }
        for (const p of items) (p as any).checked = checked;
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
    }

    isSupplyRowSelected(module: CaseModuleComposed, prov: Provision): boolean {
        return this.selectionSupplies.selected.some(sel => (sel as any).provisionFeeId === (prov as any).provisionFeeId);
    }

    toggleSupplyRow(module: CaseModuleComposed, prov: Provision, checked: boolean): void {
        const pfid = (prov as any).provisionFeeId;
        if (!pfid) return;
        if (checked) {
            if (!this.selectionSupplies.selected.some(sel => (sel as any).provisionFeeId === pfid)) {
                this.selectionSupplies.select(prov);
            }
        } else {
            const existing = this.selectionSupplies.selected.find(sel => (sel as any).provisionFeeId === pfid);
            if (existing) this.selectionSupplies.deselect(existing);
        }
        (prov as any).checked = checked;
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
    }

    // ====== AGREEMENTS (dentro de práctica) ======
    private getAgreementId(agreement: any): number | null {
        const id = Number(agreement?.id ?? agreement?.agreementId);
        return Number.isFinite(id) && id > 0 ? id : null;
    }

    isAgreementRowSelected(module: CaseModuleComposed, child: Provision, agreement: any): boolean {
        const id = this.getAgreementId(agreement);
        return id != null ? this.selectionAgreements.selected.some(sel => sel.id === id) : false;
    }

     toggleAgreementRow(module: CaseModuleComposed, child: Provision, agreement: any, checked: boolean): void {
        const id = this.getAgreementId(agreement);
        if (id == null) return;
        if (checked) {
            if (!this.selectionAgreements.selected.some(sel => sel.id === id)) {
                this.selectionAgreements.select(agreement as Agreement);
            }
        } else {
            const existing = this.selectionAgreements.selected.find(sel => sel.id === id);
            if (existing) this.selectionAgreements.deselect(existing);
        }
        (agreement as any).checked = checked;
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
    }

    isAllAgreementsRowsSelected(module: CaseModuleComposed, child: Provision): boolean {
        const agreements = this.getVisibleAgreementsForProvision(child);
        if (agreements.length === 0) return false;
        return agreements.every((a: any) => {
            const id = this.getAgreementId(a);
            return id != null && this.selectionAgreements.selected.some(sel => sel.id === id);
        });
    }

    isSomeAgreementsRowsSelected(module: CaseModuleComposed, child: Provision): boolean {
        const agreements = this.getVisibleAgreementsForProvision(child);
        if (agreements.length === 0) return false;
        const selectedCount = agreements.filter((a: any) => {
            const id = this.getAgreementId(a);
            return id != null && this.selectionAgreements.selected.some(sel => sel.id === id);
        }).length;
        return selectedCount > 0 && selectedCount < agreements.length;
    }

    toggleAllAgreementsRows(module: CaseModuleComposed, child: Provision, checked: boolean): void {
        const agreements = this.getVisibleAgreementsForProvision(child);
        for (const agreement of agreements) {
            const id = this.getAgreementId(agreement);
            if (id == null) continue;
            if (checked) {
                if (!this.selectionAgreements.selected.some(sel => sel.id === id)) {
                    this.selectionAgreements.select(agreement as Agreement);
                }
            } else {
                const existing = this.selectionAgreements.selected.find(sel => sel.id === id);
                if (existing) this.selectionAgreements.deselect(existing);
            }
            (agreement as any).checked = checked;
        }
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
    }


    mapProvisionToChildRow(prov: Provision): ProvisionRow {
        const anyProv = prov as any;
        const practice = anyProv?.practice ?? {};

        const id = this.coalesceFiniteNumber(anyProv?.id, anyProv?.provisionId, anyProv?.practiceId, practice?.id);
        const codigo = String(
            practice?.code ??
            practice?.nomenclatorCode ??
            anyProv?.practiceId ??
            practice?.id ??
            anyProv?.id ??
            ''
        );
        const practica = String(practice?.name ?? anyProv?.practiceName ?? practice?.description ?? '—');

        const freq = anyProv?.frequency ?? {};
        const amount = freq?.amount ?? anyProv?.frequencyAmount;
        const unit = freq?.unit ?? anyProv?.frequencyUnit;
        const allowedHours = freq?.allowedHours ?? anyProv?.allowedHours;

        let frecuencia = this.formatFrequency(amount, unit);
        if (allowedHours) {
            frecuencia = `${frecuencia} (${allowedHours} hs)`;
        }

        return {
            id: id || undefined,
            codigo,
            practica,
            checked: !!anyProv?.checked,
            frecuencia,
            totalAgendado: this.coalesceFiniteNumber(
                anyProv?.totalAgendado,
                anyProv?.totalScheduled,
                anyProv?.scheduled,
                anyProv?.totalAmount
            ),
            realizado: this.coalesceFiniteNumber(anyProv?.realizado, anyProv?.realized, anyProv?.totalRealized),
            facturable: this.coalesceFiniteNumber(
                anyProv?.facturable,
                anyProv?.billable,
                anyProv?.billed,
                anyProv?.invoiced
            ),
            debitable: this.coalesceFiniteNumber(anyProv?.debitable, anyProv?.debited),
            informado: this.coalesceFiniteNumber(anyProv?.informado, anyProv?.informed, anyProv?.reported),
            raw: prov,
        };
    }

    formatFrequency(amount?: number, unit?: FrequencyUnit): string {
        if (!amount && !unit) return '-';
        if (amount && unit) return `${amount} ${this.experienceI18n.translate('modules.cases.modules-scheme.por', undefined, 'por')} ${unit}`;
        if (amount) return `${amount}`;
        return `${unit}`;
    }

    clearChildCache(): void {
        this.childDS.clear();
        this.practicesDS.clear();
        this.grandDS.clear();
        this.expandedSupplyGroups.clear();
    }


    grandDS = new Map<string, MatTableDataSource<AgreementRow>>();

    private getGrandKey(child: ProvisionRow): string {
        return String(child?.id ?? child?.codigo ?? child?.practica ?? 'row');
    }

    private formatDateRange(from?: any, to?: any): string {
        if (!from && !to) return '—';
        const fromStr = from ? moment(from).format('DD/MM/YYYY') : '—';
        const toStr = to ? moment(to).format('DD/MM/YYYY') : '—';
        return `${fromStr} - ${toStr}`;
    }

    private mapAgreementToGrandRow(agreement: any): AgreementRow {
        const anyAgr = agreement as any;

        const totalScheduled = this.coalesceFiniteNumber(anyAgr?.totalScheduled, anyAgr?.scheduled, anyAgr?.totalAmount);
        const done = this.coalesceFiniteNumber(anyAgr?.done, anyAgr?.realized, anyAgr?.totalRealized);
        const notDone = this.coalesceFiniteNumber(anyAgr?.notDone, (totalScheduled || 0) - (done || 0));

        const cost = this.coalesceFiniteNumber(anyAgr?.cost, anyAgr?.fee, anyAgr?.price, anyAgr?.providerFee);
        const billable = this.coalesceFiniteNumber(anyAgr?.billable, anyAgr?.facturable, anyAgr?.billed);
        const debitable = this.coalesceFiniteNumber(anyAgr?.debitable, anyAgr?.debited);
        const reported = this.coalesceFiniteNumber(anyAgr?.reported, anyAgr?.informed);

        const name = String(anyAgr?.name ?? anyAgr?.agreement ?? anyAgr?.arrangementName ?? anyAgr?.description ?? '—');
        const vigency = String(anyAgr?.vigency ?? this.formatDateRange(anyAgr?.fromDate, anyAgr?.toDate));

        return {
            checked: !!anyAgr?.checked,
            agreement: name,
            totalScheduled,
            cost,
            done,
            notDone,
            billable,
            debitable,
            reported,
            vigency,
        };
    }

    getGrandDataSource(child: ProvisionRow): MatTableDataSource<AgreementRow> {
        const key = this.getGrandKey(child);
        const existing = this.grandDS.get(key);
        if (existing) return existing;

        const agreements = (child?.raw as any)?.agreements;
        const rows: AgreementRow[] = Array.isArray(agreements)
            ? agreements.map(a => this.mapAgreementToGrandRow(a))
            : [];

        const ds = new MatTableDataSource(rows);
        this.grandDS.set(key, ds);
        return ds;
    }

    // ===== Table: expand/collapse child rows =====
    toggleChildRow(row: ProvisionRow) {
        this.expandedChild = this.expandedChild === row ? null : row;
    }

    private getModuleItemId(item: ModuleItems): number | null {
        // Ajustá el orden si en tu modelo el id real se llama distinto
        const anyItem = item as any;
        return (anyItem.id ?? anyItem.moduleItemId ?? item.practiceId ?? null) as number | null;
    }

    /** Datasource simple para la tabla de insumos (solo isSupply = true) */
    getSuppliesTableData(module: CaseModuleComposed): Provision[] {
        return this.getSuppliesByModule(module);
    }

    private getSupplyCategoryFromCode(codeRaw: unknown): string {
        const code = String(codeRaw ?? '');
        if (/^IN/.test(code)) return 'Descartables';
        if (/^ME/.test(code)) return 'Medicamentos';
        if (/^EQ/.test(code)) return 'Equipamientos';
        return 'Otros';
    }

    getSuppliesByCategory(module: CaseModuleComposed, category: string): Provision[] {
        const supplies = this.getSuppliesByModule(module);
        return supplies.filter(p => {
            const anyP = p as any;
            const pr = anyP?.practice ?? {};
            const code = pr?.code ?? pr?.nomenclatorCode ?? anyP?.practiceCode;
            return this.getSupplyCategoryFromCode(code) === category;
        });
    }

    getSupplyGroupLabel(module: CaseModuleComposed, category: string): string {
        const count = this.getSuppliesByCategory(module, category).length;
        const labels: Record<string, string> = {
            Descartables: 'Descartables',
            Medicamentos: 'Medicamentos',
            Equipamientos: 'Equipamientos',
            Otros: 'Otros',
        };
        const label = this.experienceI18n.translate(`modules.cases.modules-scheme.supply-category.${category.toLowerCase()}`, undefined, labels[category]);
        return `${label} (${count})`;
    }

    isSupplyGroupExpanded(module: CaseModuleComposed, category: string): boolean {
        return this.expandedSupplyGroups.has(this.getSupplyGroupKey(module, category));
    }

    toggleSupplyGroup(module: CaseModuleComposed, category: string): void {
        const key = this.getSupplyGroupKey(module, category);
        if (this.expandedSupplyGroups.has(key)) this.expandedSupplyGroups.delete(key);
        else this.expandedSupplyGroups.add(key);
    }

    /** Datasource de insumos agrupado por tipo (sin tipos/VMs nuevos). */
    getSuppliesGroupedTableData(module: CaseModuleComposed): Array<string | Provision> {
        const rows: Array<string | Provision> = [];
        for (const category of this.supplyCategories) {
                const categorySupplies = this.getSuppliesByCategory(module, category);
                if (categorySupplies.length === 0) {
                    continue;
                }

                rows.push(category);
                if (this.isSupplyGroupExpanded(module, category)) {
                    rows.push(...categorySupplies);
            }
        }
        return rows;
    }

    isAllSuppliesRowsSelectedByCategory(module: CaseModuleComposed, category: string): boolean {
        const items = this.getSuppliesByCategory(module, category);
        if (items.length === 0) return false;
        return items.every(p => this.selectionSupplies.selected.some(sel => (sel as any).provisionFeeId === (p as any).provisionFeeId));
    }

    isSomeSuppliesRowsSelectedByCategory(module: CaseModuleComposed, category: string): boolean {
        const items = this.getSuppliesByCategory(module, category);
        if (items.length === 0) return false;
        const selectedCount = items.filter(p =>
            this.selectionSupplies.selected.some(sel => (sel as any).provisionFeeId === (p as any).provisionFeeId)
        ).length;
        return selectedCount > 0 && selectedCount < items.length;
    }

    toggleAllSuppliesRowsByCategory(module: CaseModuleComposed, category: string, checked: boolean): void {
        const items = this.getSuppliesByCategory(module, category);
        if (checked) {
            for (const p of items) {
                if (!this.selectionSupplies.selected.some(sel => (sel as any).provisionFeeId === (p as any).provisionFeeId)) {
                    this.selectionSupplies.select(p);
                }
            }
        } else {
            for (const p of items) {
                const pfid = (p as any).provisionFeeId;
                const existing = this.selectionSupplies.selected.find(sel => (sel as any).provisionFeeId === pfid);
                if (existing) this.selectionSupplies.deselect(existing);
            }
        }
        for (const p of items) (p as any).checked = checked;
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
    }

    formatQuantity(amount?: number, unit?: FrequencyUnit): string {
        if (!amount && !unit) return '-';
        if (amount && unit) return `${amount} ${unit}`;
        if (amount) return `${amount}`;
        return `${unit}`;
    }

    private normalizeCaseModuleFrequencyUnit(unitRaw: any): string | null {
        if (unitRaw == null) return null;

        if (typeof unitRaw === 'string') {
            const u = unitRaw.trim();
            return u ? u : null;
        }

        const n = Number(unitRaw);
        if (!Number.isFinite(n)) return String(unitRaw);

        const byZeroBased: Record<number, string> = {
            0: ProvisionsFrequencyUnit.DIA,
            1: ProvisionsFrequencyUnit.SEMANA,
            2: ProvisionsFrequencyUnit.MES,
        };
        const byOneBased: Record<number, string> = {
            1: ProvisionsFrequencyUnit.DIA,
            2: ProvisionsFrequencyUnit.SEMANA,
            3: ProvisionsFrequencyUnit.MES,
        };

        return byZeroBased[n] ?? byOneBased[n] ?? String(unitRaw);
    }

    private formatCaseModuleFrequency(cm: CaseModule | null | undefined): string {
        const anyCM = cm as any;
        if (!anyCM) return '-';

        const amount = anyCM?.frequencyAmount ?? anyCM?.frequency?.amount;
        const unitRaw = anyCM?.frequencyUnit ?? anyCM?.frequency?.unit;
        const unit = this.normalizeCaseModuleFrequencyUnit(unitRaw);

        const hasAmount = amount != null && amount !== '' && Number.isFinite(Number(amount));
        if (!hasAmount && !unit) return '-';
        if (hasAmount && unit) return `${Number(amount)} ${this.experienceI18n.translate('modules.cases.modules-scheme.por', undefined, 'por').toLowerCase()} ${unit}`;
        if (hasAmount) return `${Number(amount)}`;
        return `${unit}`;
    }

    getCaseModuleFrequencyLabel(row: CaseModuleComposed): string {
        const cm = (this.caseModules ?? []).find((x) => x?.id === row?.id);
        const fromRaw = this.formatCaseModuleFrequency(cm);
        if (fromRaw && fromRaw !== '-') return fromRaw;
        const fromRow = String((row as any)?.frequency ?? '').trim();
        return fromRow || '-';
    }

    private resolveProvisionFeeForDisplay(prov: Provision): ProvisionFee | null {
        const fees = ((prov as any)?.provisionFees ?? []) as ProvisionFee[];
        if (!Array.isArray(fees) || fees.length === 0) return null;

        const rangeStart = this.coerceToDate(this.dateRange?.historyFromDate);
        const rangeEnd = this.coerceToDate(this.dateRange?.historyToDate);

        // Si hay rango de fechas, preferimos el fee vigente en ese rango.
        if (rangeStart && rangeEnd) {
            const startMs = rangeStart.getTime();
            const endMs = rangeEnd.getTime();

            const overlapping = fees.filter((f) => {
                const from = this.coerceToDate((f as any)?.fromDate);
                const to = this.coerceToDate((f as any)?.toDate);

                const fromMs = from?.getTime() ?? -Infinity;
                const toMs = to?.getTime() ?? Infinity;

                // Overlap check: [from,to] intersects [start,end]
                return fromMs <= endMs && toMs >= startMs;
            });

            if (overlapping.length > 0) {
                return overlapping.reduce<ProvisionFee>((best, fee) => {
                    if (!best) return fee;
                    const bestFrom = this.coerceToDate((best as any)?.fromDate)?.getTime() ?? -1;
                    const feeFrom = this.coerceToDate((fee as any)?.fromDate)?.getTime() ?? -1;
                    if (feeFrom !== bestFrom) return feeFrom > bestFrom ? fee : best;
                    return (fee?.id ?? 0) > (best?.id ?? 0) ? fee : best;
                }, overlapping[0]);
            }
        }

        // Fallback al criterio existente (provisionFeeId o más reciente)
        return this.resolveCurrentProvisionFee(prov);
    }

    private rangesOverlapInclusive(
        fromA?: Date | null,
        toA?: Date | null,
        fromB?: Date | null,
        toB?: Date | null,
    ): boolean {
        const fromAMs = fromA?.getTime() ?? -Infinity;
        const toAMs = toA?.getTime() ?? Infinity;
        const fromBMs = fromB?.getTime() ?? -Infinity;
        const toBMs = toB?.getTime() ?? Infinity;

        return fromAMs <= toBMs && toAMs >= fromBMs;
    }

    private getAgreementsVisibleInDateRange(prov: Provision): Agreement[] {
        const agreements = ((prov as any)?.agreements ?? []) as Agreement[];
        if (!Array.isArray(agreements) || agreements.length === 0) {
            return [];
        }

        const rangeStart = this.coerceToDate(this.dateRange?.historyFromDate);
        const rangeEnd = this.coerceToDate(this.dateRange?.historyToDate);
        if (!rangeStart || !rangeEnd) {
            return agreements;
        }

        return agreements.filter((agreement) => {
            if (!agreement?.active) {
                return false;
            }

            const agreementFrom = this.coerceToDate((agreement as any)?.fromDate);
            const agreementTo = this.coerceToDate((agreement as any)?.toDate);
            return this.rangesOverlapInclusive(agreementFrom, agreementTo, rangeStart, rangeEnd);
        });
    }

    getVisibleAgreementsForProvision(prov: Provision): Agreement[] {
        return this.getAgreementsVisibleInDateRange(prov);
    }

    private resolveDisplayFrequencyFromProvision(prov: Provision): { amount?: number; unit?: FrequencyUnit; allowedHours?: number } | null {
        if (!prov) return null;

        const provision = prov as any;
        const currentFee = this.resolveProvisionFeeForDisplay(prov);
        const fromFee = (currentFee as any)?.frequency;
        if (fromFee && (fromFee.amount != null || fromFee.unit != null || fromFee.allowedHours != null)) {
            return fromFee;
        }

        const fromProvision = provision?.frequency;
        if (fromProvision && (fromProvision.amount != null || fromProvision.unit != null || fromProvision.allowedHours != null)) {
            return fromProvision;
        }

        const flatAmount = provision?.frequencyAmount;
        const flatUnit = provision?.frequencyUnit;
        const flatHours = provision?.allowedHours;
        if (flatAmount != null || flatUnit != null || flatHours != null) {
            return { amount: flatAmount, unit: flatUnit, allowedHours: flatHours } as any;
        }

        return null;
    }

    getProvisionFrequencyLabel(prov: Provision): string {
        const freq = this.resolveDisplayFrequencyFromProvision(prov);
        if (!freq) return '-';

        const amount = (freq as any)?.amount;
        const unit = (freq as any)?.unit;
        const allowedHoursRaw = (freq as any)?.allowedHours;
        const allowedHours = Number(allowedHoursRaw);

        // Si la práctica se mide por horas diarias, evitamos mostrar algo confuso como "3 por día"
        // y lo expresamos como horas.
        if (Number.isFinite(allowedHours) && allowedHours > 0) {
            const nAmount = Number(amount);
            const unitStr = String(unit ?? '').toLowerCase();
            const isDayUnit =
                unit === ProvisionsFrequencyUnit.DIA ||
                unitStr === 'dia' ||
                unitStr === 'día' ||
                unitStr.includes('dia') ||
                unitStr.includes('día');

            // Heurística: si no hay amount o es 0/1, asumimos que la señal real es allowedHours.
            if (!Number.isFinite(nAmount) || nAmount <= 1) {
                return this.formatAllowedHoursLabel(allowedHours, isDayUnit);
            }
        }

        const label = this.formatFrequency(amount as any, unit as any);
        return Number.isFinite(allowedHours) && allowedHours > 0 ? `${label} (${allowedHours} hs)` : label;
    }

    private formatAllowedHoursLabel(allowedHours: number, isDayUnit: boolean): string {
        if (!Number.isFinite(allowedHours) || allowedHours <= 0) return '-';
        if (isDayUnit) {
            return allowedHours === 1 ? `${allowedHours} ${this.experienceI18n.translate('modules.cases.modules-scheme.hs-por-dia', undefined, 'hs por día').toLowerCase()}` : `${allowedHours} ${this.experienceI18n.translate('modules.cases.modules-scheme.hs-diarias', undefined, 'hs diarias').toLowerCase()}`;
        }
        return `${allowedHours} ${this.experienceI18n.translate('modules.cases.modules-scheme.hs', undefined, 'hs').toLowerCase()}`;
    }

    // ===== Helpers de display para insumos (mantener template simple) =====
    getSupplyQuantityLabel(prov: Provision): string {
        const freq = this.resolveDisplayFrequencyFromProvision(prov) ?? ({} as any);
        const amount = (freq as any)?.amount;
        const unit = (freq as any)?.unit;
        return this.formatQuantity(amount, unit);
    }

    getSupplyVendorLabel(prov: Provision): string {
        return this.resolveSupplyProviderNameFromProvision(prov) ?? '-';
    }

    getSupplyTotalCost(prov: Provision): number {
        const anyProv = prov as any;
        return this.coalesceFiniteNumber(anyProv?.totalCost, anyProv?.totalValue, anyProv?.totalAmount, anyProv?.fee);
    }

    getSupplyOrderedCount(provFee: ProvisionFee): number {
        let items = this.orders?.filter(or => or.state.id != OrdersStates.CANCELADO)?.flatMap((order) => order.items.filter((it) => it.provisionFeeId == provFee.id));
        let quantityOrdered: number = 0;

        if (items != null && items.length != 0) {
            items?.forEach((it) => {
                quantityOrdered += it.quantityOrdered;
            });
            return quantityOrdered;
        }
        return 0
    }

    getDelivered(provFee: ProvisionFee) {
        let deliveredAttentions = this.attentionsINRange?.filter(
            (att) => att.state?.id === AttentionStates.REALIZADA && att.provisionFeeId == provFee.id,
        );
        let totalDelivered: number = 0;
        if (deliveredAttentions != null && deliveredAttentions.length != 0) {
            deliveredAttentions.forEach((att) => {
                totalDelivered += att.quantity;
            });
            return totalDelivered;
        }
        return 0;
    }

    getNotDelivered(provFee: ProvisionFee) {
        let items = this.orders?.filter(or => or.state.id != OrdersStates.CANCELADO).flatMap((order) =>
            order.items.filter((it) => it.provisionFeeId == provFee.id),
        );
        let quantityDelivered: number = 0;
        if (items != null && items.length != 0) {
            items?.forEach((it) => {
                quantityDelivered += it.quantityDelivered;
            });
            return this.getSupplyOrderedCount(provFee) - quantityDelivered;
        }
        return 0;
    }

    getInvoiced(provFee: ProvisionFee) {
        let deliveredAttentions = this.attentionsINRange?.filter(
            (att) => att.state.id === AttentionStates.FACTURABLE && att.provisionFeeId == provFee.id,
        );
        let totalInvoiced: number = 0;
        if (deliveredAttentions != null && deliveredAttentions.length != 0) {
            deliveredAttentions.forEach((att) => {
                totalInvoiced += att.quantity;
            });
            return totalInvoiced;
        }
        return 0;
    }

    getDebited(provFee: ProvisionFee) {
        let deliveredAttentions = this.attentionsINRange?.filter(
            (att) => att.state.id === AttentionStates.DEBITADA && att.provisionFeeId == provFee.id,
        );
        let totalDebited: number = 0;
        if (deliveredAttentions != null && deliveredAttentions.length != 0) {
            deliveredAttentions.forEach((att) => {
                totalDebited += att.quantity;
            });
            return totalDebited;
        }
        return 0;
    }

    getInformed(provFee: ProvisionFee) {
        let deliveredAttentions = this.attentionsINRange?.filter(
            (att) => att.state.id === AttentionStates.INFORMADA && att.provisionFeeId == provFee.id,
        );
        let totalInformed: number = 0;
        if (deliveredAttentions != null && deliveredAttentions.length != 0) {
            deliveredAttentions.forEach((att) => {
                totalInformed += att.quantity;
            });
            return totalInformed;
        }
        return 0;
    }

    get modulesFooterSummary(): ModulesFooterSummary {
        const modules = this.getModulesForFooterSummary();
        const totalScheduledAmount = modules.reduce(
            (acc, m) => acc + (this.totalOfModule(m) * this.coalesceFiniteNumber(m?.fee)),
            0
        );
        const totalBillableAmount = modules.reduce(
            (acc, m) => acc + (this.invoicedOfModule(m) * this.coalesceFiniteNumber(m?.fee)),
            0
        );
        const totalDebitableAmount = modules.reduce(
            (acc, m) => acc + (this.debitedOfModule(m) * this.coalesceFiniteNumber(m?.fee)),
            0
        );
        const totalInformedAmount = modules.reduce(
            (acc, m) => acc + (this.fullyDeliveredOfModule(m) * this.coalesceFiniteNumber(m?.fee)),
            0
        );

        return {
            modulesCount: modules.length,
            totalScheduledAmount,
            totalBillableAmount,
            totalDebitableAmount,
            totalInformedAmount,
        };
    }

    private getModulesForFooterSummary(): CaseModuleComposed[] {
        const modules = this.dataSource?.data ?? [];

        if (this.singleSelectionMode && this.singleSelectedModuleId != null) {
            return modules.filter(module => module?.id === this.singleSelectedModuleId);
        }

        const selectedIds = new Set(
            (this.selectionModules?.selected ?? [])
                .map(module => module?.id)
                .filter(id => id != null)
        );

        if (selectedIds.size > 0) {
            return modules.filter(module => selectedIds.has(module?.id));
        }

        return modules;
    }

    private minMaxValidator(minKey: string, maxKey: string, message = this.experienceI18n.translate('modules.cases.modules-scheme.el-maximo-debe-ser-mayor-o-igual-al-minimo', undefined, 'El máximo debe ser mayor o igual al mínimo')): ValidatorFn {
        return (control: AbstractControl) => {
            const minCtrl = control.get(minKey);
            const maxCtrl = control.get(maxKey);
            const minRaw = minCtrl?.value;
            const maxRaw = maxCtrl?.value;

            // Si alguno está vacío, no validamos relación
            if (minRaw == null || maxRaw == null || minRaw === '' || maxRaw === '') return null;

            const min = Number(minRaw);
            const max = Number(maxRaw);

            if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
            return max >= min ? null : { minMax: message };
        };
    }

    private setControlEnabled(control: AbstractControl | null | undefined, enabled: boolean): void {
        if (!control) return;
        if (enabled) {
            if (control.disabled) control.enable({ emitEvent: false });
        } else {
            if (control.enabled) control.disable({ emitEvent: false });
        }
    }

    private setPracticeRowEnabledState(row: FormGroup, selected: boolean): void {
        // minAttentions y maxAttentions siempre deshabilitados
        this.setControlEnabled(row.get('minAttentions'), false);
        this.setControlEnabled(row.get('maxAttentions'), false);

        this.setControlEnabled(row.get('frequencyAmount'), selected);
        this.setControlEnabled(row.get('frequencyUnit'), selected);
        // Solo habilitar allowedHours si la práctica es tipo guardia (requiredTime)
        this.setControlEnabled(row.get('allowedHours'), selected && this.rowRequiresAllowedHours(row));
    }

    private setSupplyRowEnabledState(row: FormGroup, selected: boolean): void {
        this.setControlEnabled(row.get('providerId'), selected);
        this.setControlEnabled(row.get('providerSearching'), selected);
        this.setControlEnabled(row.get('quantity'), selected);
        this.setControlEnabled(row.get('unit'), selected);
        this.setControlEnabled(row.get('unitCost'), selected);
    }

    private setPracticeRowValidators(row: FormGroup, selected: boolean): void {
        const freqAmount = row.get('frequencyAmount');
        const freqUnit = row.get('frequencyUnit');
        const minAtt = row.get('minAttentions');
        const maxAtt = row.get('maxAttentions');

        if (selected) {
            freqAmount?.setValidators([
                CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.required', undefined, 'Requerido')),
                CustomValidators.minValue(1, this.experienceI18n.translate('modules.cases.modules-scheme.debe-estar-entre-uno-y-mil', undefined, 'Debe estar entre 1 y 1000')),
                CustomValidators.maxValue(1000, this.experienceI18n.translate('modules.cases.modules-scheme.debe-estar-entre-uno-y-mil', undefined, 'Debe estar entre 1 y 1000')),
            ]);
            freqUnit?.setValidators([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.requerido', undefined, 'Requerido'))]);
            // min/max no son requeridos; si se cargan, deben ser >= 0
            minAtt?.setValidators([Validators.min(0)]);
            maxAtt?.setValidators([Validators.min(0)]);

            if (row.get('frequencyUnit')?.value == null) {
                row.get('frequencyUnit')?.setValue(this.PROVISION_FREQUENCY_UNIT_DEFAULT, { emitEvent: false });
            }
        } else {
            freqAmount?.clearValidators();
            freqUnit?.clearValidators();
            minAtt?.clearValidators();
            maxAtt?.clearValidators();
        }

        freqAmount?.updateValueAndValidity({ emitEvent: false });
        freqUnit?.updateValueAndValidity({ emitEvent: false });
        minAtt?.updateValueAndValidity({ emitEvent: false });
        maxAtt?.updateValueAndValidity({ emitEvent: false });

        const allowedHours = row.get('allowedHours');
        if (selected && this.rowRequiresAllowedHours(row)) {
            allowedHours?.setValidators([
                CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.requerido', undefined, 'Requerido')),
                CustomValidators.minValue(1, this.experienceI18n.translate('modules.cases.modules-scheme.debe-estar-entre-uno-y-veinticuatro', undefined, 'Debe estar entre 1 y 24')),
                CustomValidators.maxValue(24, this.experienceI18n.translate('modules.cases.modules-scheme.debe-estar-entre-uno-y-veinticuatro', undefined, 'Debe estar entre 1 y 24')),
            ]);
        } else {
            allowedHours?.clearValidators();
        }
        allowedHours?.updateValueAndValidity({ emitEvent: false });

        this.setPracticeRowEnabledState(row, selected);
        row.updateValueAndValidity({ emitEvent: false });
    }

    private setSupplyRowValidators(row: FormGroup, selected: boolean): void {
        const qty = row.get('quantity');
        const unit = row.get('unit');
        const unitCost = row.get('unitCost');

        if (selected) {
            qty?.setValidators([
                CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.cantidad-requerida', undefined, 'Cantidad requerida')),
                CustomValidators.minValue(0, this.experienceI18n.translate('modules.cases.modules-scheme.debe-ser-un-valor-positivo', undefined, 'Debe ser un valor positivo')),
            ]);
            unit?.setValidators([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.unidad-requerida', undefined, 'Unidad requerida'))]);

            // opcional, pero si viene debe ser >=0
            unitCost?.setValidators([Validators.min(0)]);

            // if (row.get('unit')?.value == null) {
            //     row.get('unit')?.setValue(ProvisionsUnit.UNIDAD, { emitEvent: false });
            // }
        } else {
            qty?.clearValidators();
            unit?.clearValidators();
            unitCost?.clearValidators();
        }

        qty?.updateValueAndValidity({ emitEvent: false });
        unit?.updateValueAndValidity({ emitEvent: false });
        unitCost?.updateValueAndValidity({ emitEvent: false });

        this.setSupplyRowEnabledState(row, selected);
        row.updateValueAndValidity({ emitEvent: false });
    }

    get practicesPickerRowsFA(): FormArray {
        return this.practicesPickerForm?.get('practiceRows') as FormArray;
    }

    get suppliesPickerRowsFA(): FormArray {
        return this.suppliesPickerForm?.get('supplyRows') as FormArray;
    }

    private refreshPracticePickerFilter(): void {
        const q = String(this.practicesPickerSearchControl?.value ?? '').trim().toLowerCase();
        const rows = (this.practicesPickerRowsFA?.controls ?? []) as FormGroup[];
        const vm = rows.map((fg, index) => ({ fg, index }));

        this.filteredPracticePickerRowVM = !q
            ? vm
            : vm.filter(r => {
                const code = String(r.fg.get('code')?.value ?? '').toLowerCase();
                const name = String(r.fg.get('name')?.value ?? '').toLowerCase();
                return code.includes(q) || name.includes(q);
            });
    }

    private refreshSuppliesPickerFilter(): void {
        const q = String(this.suppliesPickerSearchControl?.value ?? '').trim().toLowerCase();
        const rows = (this.suppliesPickerRowsFA?.controls ?? []) as FormGroup[];
        const vm = rows.map((fg, index) => ({ fg, index }));

        this.filteredSuppliesPickerRowVM = !q
            ? vm
            : vm.filter(r => {
                const code = String(r.fg.get('code')?.value ?? '').toLowerCase();
                const name = String(r.fg.get('name')?.value ?? '').toLowerCase();
                return code.includes(q) || name.includes(q);
            });
    }

    getFilteredPracticePickerRowVM(): Array<{ index: number; fg: FormGroup }> {
        return this.filteredPracticePickerRowVM;
    }

    getFilteredSuppliesPickerRowVM(): Array<{ index: number; fg: FormGroup }> {
        return this.filteredSuppliesPickerRowVM;
    }


    private createPracticePickerRow(pr: Practice, prev?: ModulePracticeSelected, locked: boolean = false): FormGroup {
        const restrictionId = (pr as any)?.restrictionId ?? pr.restriction?.id ?? prev?.restrictionId ?? null;
        const restrictionRequiredTime = !!pr.restriction?.requiredTime;
        // Resolve default frequency from the module catalog item
        const catalogItem = (this.moduleSelected?.moduleItems ?? []).find(i => !i.isSupply && i.practiceId === pr.id);
        const row = this.formBuilder.group(
            {
                selected: [locked ? true : !!prev],
                practiceId: [pr.id],
                code: [pr.code ?? ''],
                name: [pr.name ?? ''],

                frequencyAmount: [prev?.frequencyAmount ?? catalogItem?.frequencyAmount ?? null],
                frequencyUnit: [prev?.frequencyUnit ?? catalogItem?.frequencyUnit ?? null],

                minAttentions: [prev?.minAttentions ?? null],
                maxAttentions: [prev?.maxAttentions ?? null],

                allowedHours: [prev?.allowedHours ?? catalogItem?.allowedHours ?? null],
                restrictionId: [restrictionId],
                restrictionRequiredTime: [prev?.restrictionRequiredTime ?? restrictionRequiredTime],
                restrictionPatient: [prev?.restrictionPatient ?? isPatientPracticeRestriction(pr.restriction)],
            },
            { validators: [this.minMaxValidator('minAttentions', 'maxAttentions')] }
        );

        if (locked) {
            // Quedan checked y no editables
            row.get('selected')?.setValue(true, { emitEvent: false });
            // Evitamos que validaciones de min/max bloqueen el submit del picker
            row.clearValidators();
            row.updateValueAndValidity({ emitEvent: false });

            // Deshabilita campos editables
            this.setPracticeRowEnabledState(row, false);
            // No queremos que el usuario pueda deschequear
            this.setControlEnabled(row.get('selected'), false);
            return row;
        }

        this.setPracticeRowValidators(row, !!prev);

        // minAttentions y maxAttentions siempre deshabilitados
        row.get('minAttentions')?.disable({ emitEvent: false });
        row.get('maxAttentions')?.disable({ emitEvent: false });

        row.get('selected')?.valueChanges.subscribe((isSel: boolean) => {
            this.setPracticeRowValidators(row, !!isSel);
            if (isSel) {
                this.markPracticeRowInvalidControlsAsTouched(row);
            }
        });

        return row;
    }

    private buildPracticesPickerForm(sourcePractices: Practice[]): void {
        const selectedById = new Map<number, ModulePracticeSelected>();
        for (const p of this.modulePracticesSelected ?? []) selectedById.set(p.practiceId, p);

        this.practicesPickerForm = this.formBuilder.group({
            practiceRows: this.formBuilder.array([]),
        });
        for (const pr of sourcePractices) {
            const lockedPrev = this.lockedPracticePrevByIdForPicker.get(pr.id);
            const isLocked = this.lockedPracticeIdsForPicker.has(pr.id);
            const prev = lockedPrev ?? selectedById.get(pr.id);
            this.practicesPickerRowsFA.push(this.createPracticePickerRow(pr, prev, isLocked));
        }

        this.practicesPickerSearchControl.setValue('');

        this.practicesPickerSearchSub?.unsubscribe();
        this.practicesPickerSearchSub = this.practicesPickerSearchControl.valueChanges.subscribe(() => {
            this.refreshPracticePickerFilter();
        });
        this.refreshPracticePickerFilter();
    }

    private buildSuppliesPickerForm(sourceSupplies: Practice[]): void {
        // Mapa por provisionFeeId (clave única) cuando existe, fallback a practiceId
        const selectedByKey = new Map<string, ModuleSupplySelected>();
        for (const s of this.moduleSuppliesSelected ?? []) {
            // Usar provisionFeeId si existe, sino practiceId como fallback
            const key = s.provisionFeeId != null ? `pf_${s.provisionFeeId}` : `pr_${s.practiceId}`;
            selectedByKey.set(key, s);
        }

        this.suppliesPickerForm = this.formBuilder.group({
            supplyRows: this.formBuilder.array([]),
        });

        // Resetear arrays de providers
        this.supplyPickerProvidersOnSelect = [];
        this.supplyPickerDefaultProviders = [];

        // Si estamos en modo edición y hay moduleSuppliesSelected con provisionFeeId,
        // usamos esos directamente en vez de sourceSupplies (del catálogo)
        const useModuleSuppliesAsSource = this.suppliesPickerEditMode &&
            (this.moduleSuppliesSelected ?? []).some(s => s.provisionFeeId != null);

        if (useModuleSuppliesAsSource) {
            // En modo edición con provisionFeeId, cada item de moduleSuppliesSelected es una fila
            let idx = 0;
            for (const modSup of this.moduleSuppliesSelected ?? []) {
                // Crear un Practice falso para la fila
                const fakePractice: Practice = {
                    id: modSup.practiceId,
                    code: modSup.code ?? '',
                    name: modSup.name ?? '',
                } as Practice;

                // Popular arrays de providers (como supply-list)
                this.supplyPickerProvidersOnSelect[idx] = modSup.provider ? [modSup.provider] : [];
                this.supplyPickerDefaultProviders[idx] = modSup.providerId?.toString() ?? null;

                this.suppliesPickerRowsFA.push(this.createSupplyPickerRowFromSelected(fakePractice, modSup));
                idx++;
            }
        } else {
            // Modo tradicional: iterar sourceSupplies del catálogo
            let idx = 0;
            for (const sup of sourceSupplies) {
                const prev = selectedByKey.get(`pr_${sup.id}`);

                // Popular arrays de providers
                this.supplyPickerProvidersOnSelect[idx] = prev?.provider ? [prev.provider] : [];
                this.supplyPickerDefaultProviders[idx] = prev?.providerId?.toString() ?? null;

                this.suppliesPickerRowsFA.push(this.createSupplyPickerRow(sup, prev));
                idx++;
            }
        }

        this.suppliesPickerSearchControl.setValue('');

        this.suppliesPickerSearchSub?.unsubscribe();
        this.suppliesPickerSearchSub = this.suppliesPickerSearchControl.valueChanges.subscribe(() => {
            this.refreshSuppliesPickerFilter();
        });
        this.refreshSuppliesPickerFilter();
    }


    private createSupplyPickerRow(sup: Practice, prev?: ModuleSupplySelected): FormGroup {
        const row = this.formBuilder.group({
            selected: [!!prev],
            practiceId: [sup.id],
            provisionFeeId: [prev?.provisionFeeId ?? null],
            code: [sup.code ?? ''],
            name: [sup.name ?? ''],
            caseModuleId: [this.caseModuleSelected?.id ?? null],
            providerId: [prev?.providerId ?? null],
            providerSearching: [prev?.providerName ?? ''], // Mostrar nombre del proveedor en el autocomplete
            providerName: [prev?.providerName ?? null],

            quantity: [prev?.quantity ?? null],
            unit: [prev?.unit ?? null],
            unitCost: [prev?.unitCost ?? null],
        });

        this.setSupplyRowValidators(row, !!prev);

        row.get('selected')?.valueChanges.subscribe((isSel: boolean) => {
            this.setSupplyRowValidators(row, !!isSel);
        });

        return row;
    }

    /**
     * Crea una fila del picker desde un ModuleSupplySelected (modo edición con provisionFeeId).
     * La fila viene pre-seleccionada y con todos los datos ya cargados.
     */
    private createSupplyPickerRowFromSelected(sup: Practice, modSup: ModuleSupplySelected): FormGroup {
        const row = this.formBuilder.group({
            selected: [true], // En modo edición filtrado, todos vienen seleccionados
            practiceId: [modSup.practiceId],
            provisionFeeId: [modSup.provisionFeeId ?? null],
            code: [modSup.code ?? sup.code ?? ''],
            name: [modSup.name ?? sup.name ?? ''],
            caseModuleId: [this.caseModuleSelected?.id ?? null],
            providerId: [modSup.providerId ?? null],
            providerSearching: [modSup.providerName ?? ''], // Mostrar nombre del proveedor en el autocomplete
            providerName: [modSup.providerName ?? null],

            quantity: [modSup.quantity ?? null],
            unit: [modSup.unit ?? ProvisionsUnit.UNIDAD],
            unitCost: [modSup.unitCost ?? null],
        });

        this.setSupplyRowValidators(row, true);

        row.get('selected')?.valueChanges.subscribe((isSel: boolean) => {
            this.setSupplyRowValidators(row, !!isSel);
        });

        return row;
    }

    openPracticesPickerForCaseModule(cmRow: CaseModuleComposed): void {
        if (!cmRow) return;

        // Este flujo modifica el CaseModule existente, no es Add.
        this.isAddMode = false;
        this.isEditMode = true;
        this.isRePostMode = false;
        this.caseModuleSelected = cmRow;
        this.editingModuleRow = cmRow;

        const moduleId = cmRow.moduleId;

        // Bloquea prácticas ya cargadas en el CaseModule (checked + disabled)
        const lockedMap = this.buildLockedPracticesFromCaseModuleRow(cmRow);
        this.lockedPracticePrevByIdForPicker = lockedMap;
        this.lockedPracticeIdsForPicker = new Set(Array.from(lockedMap.keys()));
        // Además, arrancamos el picker con estas prácticas ya seleccionadas
        this.modulePracticesSelected = Array.from(lockedMap.values());

        this.moduleSelected = this.modulesFilteredByFinancier?.find(m => m.id == moduleId);
        this.openPracticesPickerDialog();

        this._practicesDialogRef?.afterClosed()?.subscribe(() => {
            this.moduleSelected = null;

            // Limpieza: no queremos que el lock afecte al picker del alta/edición de módulo
            this.lockedPracticeIdsForPicker = new Set<number>();
            this.lockedPracticePrevByIdForPicker = new Map<number, ModulePracticeSelected>();
        });
    }

    openSuppliesPickerForCaseModule(cmRow: CaseModuleComposed): void {
        if (!cmRow) return;

        // Este flujo modifica el CaseModule existente, no es Add.
        this.isAddMode = false;
        this.isEditMode = true;
        this.isRePostMode = false;
        this.caseModuleSelected = cmRow;
        this.editingModuleRow = cmRow;


        const prevModuleSelected = this.moduleSelected;

        const moduleId = cmRow.moduleId;
        if (!moduleId) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-resolver-el-modulo-para-agregar-insumos', undefined, 'No se pudo resolver el módulo para agregar insumos.'), icon: 'error' });
            return;
        }

        // En este flujo NO queremos preseleccionar lo ya cargado; solo avisar si está duplicado.
        this.moduleSuppliesSelected = [];
        this.existingSupplyIdsForPicker = this.getExistingSupplyIdsFromCaseModuleRow(cmRow);
        this.suppliesPickerEditMode = false;
        this.suppliesPickerDialogTitle = this.experienceI18n.translate('modules.cases.modules-scheme.cargar-insumos', undefined, 'Cargar insumos');

        const module = this.modulesFilteredByFinancier?.find(m => m.id == moduleId);
        if (!module) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-cargar-el-modulo-seleccionado', undefined, 'No se pudo cargar el módulo seleccionado.'), icon: 'error' });
            return;
        }

        this.moduleSelected = module;
        this.openSuppliesPickerDialog();

        this._suppliesDialogRef?.afterClosed()?.subscribe(() => {
            this.moduleSelected = prevModuleSelected;
            this.existingSupplyIdsForPicker = new Set<number>();
            this.suppliesPickerEditMode = false;
            this.suppliesPickerDialogTitle = this.experienceI18n.translate('modules.cases.modules-scheme.cargar-insumos', undefined, 'Cargar insumos');
        });
    }

    private buildSuppliesSelectedFromCaseModuleRow(cmRow: CaseModuleComposed): ModuleSupplySelected[] {
        const provisions = (cmRow as any)?.provisions ?? [];
        return this.buildSuppliesSelectedFromProvisions(provisions as Provision[]);
    }

    /**
     * Convierte un array de Provision[] (insumos) a ModuleSupplySelected[].
     * Incluye provisionFeeId para poder diferenciar múltiples insumos del mismo practiceId.
     */
    private buildSuppliesSelectedFromProvisions(provisions: Provision[]): ModuleSupplySelected[] {
        return provisions
            .filter(p => this.isSupplyProvision(p))
            .map(p => {
                const anyP = p as any;
                const pr = anyP?.practice ?? {};
                const currentProvisionFee = this.resolveProvisionFeeForDisplay(p) as any;
                const agreement = currentProvisionFee?.agreement ?? anyP?.agreements?.[0] ?? null;

                const freq = this.resolveDisplayFrequencyFromProvision(p) ?? ({} as any);
                const amount = (freq as any)?.amount ?? anyP?.quantity ?? anyP?.frequencyAmount ?? null;
                const unit = (freq as any)?.unit ?? anyP?.unit ?? anyP?.frequencyUnit ?? ProvisionsUnit.UNIDAD;
                const qty = this.toFiniteOrNull(amount);

                // Costo unitario: priorizar acuerdo (providerFee) y luego fee del provisionFee
                let unitCost = this.toFiniteOrNull(
                    agreement?.providerFee ??
                    currentProvisionFee?.fee ??
                    anyP?.unitCost ??
                    anyP?.fee
                );

                // Fallback: si no hay costo unitario, intentar derivarlo de un total conocido
                if (unitCost == null && qty != null && qty > 0) {
                    const totalCost = this.toFiniteOrNull(
                        currentProvisionFee?.totalCost ??
                        currentProvisionFee?.totalValue ??
                        currentProvisionFee?.totalAmount ??
                        anyP?.totalCost ??
                        anyP?.totalValue ??
                        anyP?.totalAmount
                    );
                    unitCost = totalCost != null ? totalCost / qty : null;
                }

                const providerFromAgreement = (agreement?.provider as Provider | undefined) ?? null;
                const providerId =
                    this.toFiniteOrNull(providerFromAgreement?.id) ??
                    this.toFiniteOrNull(currentProvisionFee?.providerId) ??
                    this.toFiniteOrNull(anyP?.providerId) ??
                    null;

                // provisionFeeId es la clave única para cada instancia de insumo
                const provisionFeeId = this.toFiniteOrNull(currentProvisionFee?.id ?? anyP?.provisionFeeId) || null;

                return {
                    practiceId: this.coalesceFiniteNumber(anyP?.practiceId, pr?.id),
                    provisionFeeId,
                    code: pr?.code ?? pr?.nomenclatorCode ?? String(anyP?.practiceId ?? ''),
                    name: pr?.name ?? anyP?.practiceName ?? pr?.description ?? '—',
                    providerId,
                    provider: providerFromAgreement, // Provider completo para mostrar en el picker
                    providerName: this.resolveSupplyProviderNameFromProvision(p),
                    quantity: qty,
                    unit: unit,
                    unitCost: unitCost,
                } as ModuleSupplySelected;
            })
            .filter(s => Number.isFinite(Number(s.practiceId)) && Number(s.practiceId) > 0);
    }

    /**
     * Devuelve los insumos del módulo que deben actuar como "selección efectiva":
     * si hay insumos con check en ese módulo, los usa; si no, usa todos los insumos del módulo.
     */
    private getSuppliesForMenuAction(cmRow: CaseModuleComposed): Provision[] {
        const allSupplies = this.getSuppliesByModule(cmRow);
        const checkedSupplies = allSupplies.filter(p =>
            this.selectionSupplies.selected.some(sel => (sel as any).provisionFeeId === (p as any).provisionFeeId)
        );
        return checkedSupplies.length > 0 ? checkedSupplies : allSupplies;
    }

    private mapSuppliesForLoadPrice(cmRow: CaseModuleComposed): ProvisionFee[] {
        return this.getSuppliesForMenuAction(cmRow)
            .map(p => {
                const fee = this.resolveProvisionFeeForDisplay(p);
                if (!fee) return null;

                return {
                    ...fee,
                    practice: (p as any)?.practice ?? fee.practice,
                    agreement: fee.agreement ?? (p as any)?.agreements?.[0] ?? null,
                    orders: Array.isArray((fee as any)?.orders) ? (fee as any).orders : [],
                } as ProvisionFee;
            })
            .filter((fee): fee is ProvisionFee => fee != null);
    }

    isSomeSupplyWithoutUnitForModule(cmRow: CaseModuleComposed): boolean {
        const supplies = this.getSuppliesForMenuAction(cmRow);
        return supplies.some(p => {
            const fee = this.resolveProvisionFeeForDisplay(p) as any;
            return fee?.frequency?.unit == null || fee?.frequency?.amount == null;
        });
    }

    getProvisionFeesWithAgreement(provFees: ProvisionFee[]): ProvisionFee[] {
        return provFees.filter(pF => pF.agreement?.id > 0);
    }

    getProvisionFeesDynamicTableWithAgreement(provisionFeesSelected: ProvisionFee[]): any[] {
        let _array = [];
        provisionFeesSelected.forEach(pFee => {
            _array.push({
                supplyName: pFee.practice?.name,
                quantity: pFee.frequency?.amount ? `${pFee.frequency?.amount} ${pFee.frequency?.unit}` : '-',
                provider: `${pFee.agreement?.provider?.surname ?? ''} ${pFee.agreement?.provider?.name ?? ''}`.trim()
            });
        });
        return _array;
    }

    openLoadPriceForModule(cmRow: CaseModuleComposed): void {
        const provisionFees = this.mapSuppliesForLoadPrice(cmRow);
        if (provisionFees.length === 0) return;

        this.loadPriceRequested.emit(provisionFees);
 /*        const supplies = this.getSuppliesForMenuAction(cmRow);
        const provFees = supplies
            .map(p => this.resolveProvisionFeeForDisplay(p))
            .filter((f): f is ProvisionFee => f != null);

        const withActiveOrder = this.getProvisionFeesWithActiveOrder(provFees);
        if (withActiveOrder.length > 0) {
            this.message = this.experienceI18n.translate('modules.cases.modules-scheme.los-siguientes-insumos-son-parte-de-un-pedido-y-no-se-pueden-modificar', undefined, 'Los siguientes insumos son parte de un pedido y no se pueden modificar');
            this.arrayTable = this.getProvisionFeesDynamicTableWithActiveOrders(withActiveOrder);
            this.showConfirmBtn = false;
            this.txtCancelBtn = this.experienceI18n.translate('modules.cases.modules-scheme.ok', undefined, 'Ok');
            this.openDialog(this.experienceI18n.translate('modules.cases.modules-scheme.atencion', undefined, 'Atención'), this.popUpMessage, { minWidth: '30%', maxHeight: '70vh' }, () => { });
            return;
        } */

        // En este contexto el agreement está en Provision.agreements[], no en ProvisionFee.agreement
/*         const suppliesWithAgreement = supplies.filter(p => (p.agreements?.length ?? 0) > 0);
        if (suppliesWithAgreement.length > 0) {
            const feesWithAgreement = suppliesWithAgreement.map(p => {
                const fee = this.resolveProvisionFeeForDisplay(p) ?? {} as ProvisionFee;
                return { ...fee, practice: (p as any).practice ?? fee.practice, agreement: p.agreements?.[0] } as ProvisionFee;
            });
            this.message = this.experienceI18n.translate('modules.cases.modules-scheme.los-siguientes-insumos-ya-tienen-una-cotizacion-cargada', undefined, 'Los siguientes insumos ya tienen una cotización cargada y si continúa con el proceso se reemplazarán');
            this.arrayTable = this.getProvisionFeesDynamicTableWithAgreement(feesWithAgreement);
            this.txtCancelBtn = this.experienceI18n.translate('common.cancel', undefined, 'Cancelar');
            this.txtConfirmBtn = this.experienceI18n.translate('common.accept', undefined, 'Aceptar');
            this.showConfirmBtn = true;
            this._popupConfirmCallback = () => this.openSuppliesEditPickerForCaseModule(cmRow, this.experienceI18n.translate('modules.cases.modules-scheme.cargar-cotizacion', undefined, 'Cargar cotización'));
            this.openDialog(this.experienceI18n.translate('modules.cases.modules-scheme.atencion', undefined, 'Atención'), this.popUpMessage, { minWidth: '30%', maxHeight: '70vh' }, () => {
                this._popupConfirmCallback = null;
            });
            return;
        }

        this.openSuppliesEditPickerForCaseModule(cmRow, this.experienceI18n.translate('modules.cases.modules-scheme.cargar-cotizacion', undefined, 'Cargar cotización')); */
    }

    generateBudgetPDFForModule(cmRow: CaseModuleComposed, isPdf: boolean): void {
        const supplies = this.getSuppliesForMenuAction(cmRow);
        const suppliesForBudget = supplies.map(prov => {
            const provFee: any = { ...(this.resolveProvisionFeeForDisplay(prov) ?? {}), practice: (prov as any).practice };
            delete provFee.orders;
            delete provFee.agreement;
            delete provFee.invoiced;
            delete provFee.debited;
            delete provFee.delivered;
            delete provFee.total;
            delete provFee.totalAmount;
            delete provFee.totalDebited;
            delete provFee.totalDelivered;
            delete provFee.totalInvoiced;
            delete provFee.totalRealized;
            delete provFee.totalValue;
            delete provFee.userId;
            delete provFee.provider;
            delete provFee.authorization;
            return provFee;
        });
        const budgetBody: BudgetBody = {
            provisionFees: suppliesForBudget,
            patientId: this.case?.patient?.id,
            financierId: this.case?.financier?.id,
            caseId: this.case?.id,
            isPdf
        };
        this.provisionsFacade.generateBudgetPDF(budgetBody).subscribe(res => this.fileUtilitiesService.downloadFile(res));
    }

    generateQuotePDFForModule(cmRow: CaseModuleComposed, isPdf: boolean): void {
        const supplies = this.getSuppliesForMenuAction(cmRow);
        const suppliesForQuote = supplies.map(prov => {
            const provFee: any = { ...(this.resolveProvisionFeeForDisplay(prov) ?? {}), practice: (prov as any).practice };
            delete provFee.orders;
            delete provFee.agreement;
            delete provFee.invoiced;
            delete provFee.debited;
            delete provFee.delivered;
            delete provFee.total;
            delete provFee.totalAmount;
            delete provFee.totalDebited;
            delete provFee.totalDelivered;
            delete provFee.totalInvoiced;
            delete provFee.totalRealized;
            delete provFee.totalValue;
            delete provFee.userId;
            delete provFee.provider;
            delete provFee.authorization;
            return provFee;
        });
        const quoteBody: QuoteBody = {
            provisionFees: suppliesForQuote,
            patientId: this.case?.patient?.id,
            isPdf
        };
        this.provisionsFacade.generateQuotePDF(quoteBody).subscribe(res => this.fileUtilitiesService.downloadFile(res));
    }

    onDeleteSuppliesFromMenu(cmRow: CaseModuleComposed): void {
        const suppliesToDelete = this.getSuppliesForMenuAction(cmRow);
        const provFees = suppliesToDelete
            .map(p => this.resolveProvisionFeeForDisplay(p))
            .filter((f): f is ProvisionFee => f != null);
        if (provFees.length === 0) return;
        this.deleteSupply(provFees);
    }

    openSuppliesEditPickerForCaseModule(cmRow: CaseModuleComposed, dialogTitle: string = this.experienceI18n.translate('modules.cases.modules-scheme.modificar-insumos', undefined, 'Modificar insumos')): void {
        if (!cmRow) return;

        // Este flujo modifica el CaseModule existente, no es Add.
        this.isAddMode = false;
        this.isEditMode = true;
        this.isRePostMode = false;
        this.caseModuleSelected = cmRow;
        this.editingModuleRow = cmRow;


        const prevModuleSelected = this.moduleSelected;

        const moduleId = cmRow.moduleId;
        if (!moduleId) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-resolver-el-modulo-para-modificar-insumos', undefined, 'No se pudo resolver el módulo para modificar insumos.'), icon: 'error' });
            return;
        }

        // Si hay insumos seleccionados (con check) en este módulo, usar solo esos; si no, usar todos.
        const suppliesToEdit = this.getSuppliesForMenuAction(cmRow);

        // En edición sí queremos preseleccionar lo ya cargado para poder quitar/editar.
        this.suppliesPickerEditMode = true;
        this.suppliesPickerDialogTitle = dialogTitle;
        this.existingSupplyIdsForPicker = new Set<number>();
        this.moduleSuppliesSelected = this.buildSuppliesSelectedFromProvisions(suppliesToEdit);
        // Guardar valores originales para comparar al aplicar
        this.originalSuppliesForEdit = JSON.parse(JSON.stringify(this.moduleSuppliesSelected));

        const module = this.modulesFilteredByFinancier?.find(m => m.id == moduleId);
        if (!module) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-cargar-el-modulo-seleccionado', undefined, 'No se pudo cargar el módulo seleccionado.'), icon: 'error' });
            return;
        }

        this.moduleSelected = module;
        this.openSuppliesPickerDialog();

        this._suppliesDialogRef?.afterClosed()?.subscribe(() => {
            this.moduleSelected = prevModuleSelected;
            this.suppliesPickerEditMode = false;
            this.suppliesPickerDialogTitle = this.experienceI18n.translate('modules.cases.modules-scheme.cargar-insumos', undefined, 'Cargar insumos');
            this.existingSupplyIdsForPicker = new Set<number>();
            this.originalSuppliesForEdit = []; // Limpiar valores originales
        });
    }


    // ===== Dialogs: create/edit module =====
    openAddModuleDialog() {
        this.caseModuleSelected = null;
        this.isAddMode = true;
        this.isEditMode = false;
        this.isRePostMode = false;
        this.editingModuleRow = null;
        this.modulePracticesSelected = [];
        this.moduleSuppliesSelected = [];
        this.title = this.experienceI18n.translate('modules.cases.modules-scheme.agregar-modulo', undefined, 'Agregar módulo');
        this.checkboxAuthorizationText = !!this.caseModuleSelected?.authorization ? this.experienceI18n.translate('modules.cases.modules-scheme.reemplazar-autorizacion', undefined, 'Reemplazar autorización') : this.experienceI18n.translate('modules.cases.modules-scheme.agregar-autorizacion', undefined, 'Agregar autorización');
        this.addModuleForm(this.moduleSelected?.moduleItems ? null : undefined);
        this.formModule.setValidators(CustomValidators.fromToDate(this.VALIDITY_FROM_DATE, this.VALIDITY_TO_DATE));
    }

    deleteCaseModule(cmId: number): void {
        const deleteCaseModule = (force: boolean = false) =>
            this.modulesFacade.deleteCaseModule(this.caseId, cmId, force).toPromise();

        (this.swalWithCustomizeButtons ?? Swal)
            .fire({
                text: this.experienceI18n.translate('modules.cases.modules-scheme.confirma-que-desea-eliminar-este-modulo', undefined, '¿Confirma que desea eliminar este módulo?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.experienceI18n.translate('modules.cases.modules-scheme.si-eliminar', undefined, 'Sí, eliminar'),
                cancelButtonText: this.experienceI18n.translate('modules.cases.modules-scheme.no-cancelar', undefined, 'No, cancelar'),
                showLoaderOnConfirm: true,
                reverseButtons: true,
                preConfirm: () => {
                    return deleteCaseModule()
                        .then((result: DeleteCaseModuleResult) => {
                            this.showModuleDeleted(result);
                        })
                        .catch(error => {
                            if (error.status === 418) {
                                const errorContent: HttpBaseResponseErrorTeaPot = error.error;
                                // It's a promise, waiting the user
                                this.attentionsService.processErrorCountAttentions(errorContent, 'error').then(res => {
                                    if (!res) {
                                        // Nothing to do
                                    } else {
                                        // User confirm force
                                        deleteCaseModule(true)
                                            .then((result: DeleteCaseModuleResult) => {
                                                this.showModuleDeleted(result);
                                            })
                                            .catch(() => {
                                                (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-eliminar-el-modulo', undefined, 'No se pudo eliminar el módulo. Intente nuevamente.'), icon: 'error' });
                                            });
                                    }
                                });
                            } else {
                                (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-eliminar-el-modulo', undefined, 'No se pudo eliminar el módulo. Intente nuevamente.'), icon: 'error' });
                            }
                        });
                },
                allowOutsideClick: () => !Swal.isLoading(),
            });
    }

    private showModuleDeleted(result: DeleteCaseModuleResult): void {
        (this.swalAutoClose ?? Swal).fire({
            text: this.experienceI18n.translate('modules.cases.modules-scheme.modulo-eliminado-correctamente', undefined, 'Módulo eliminado correctamente.'),
            icon: 'success',
        });

        if (result?.refreshError) {
            this.alertService.openWarn(this.experienceI18n.translate(
                'modules.cases.modules-scheme.no-se-pudo-actualizar-prestaciones',
                undefined,
                'El módulo se eliminó correctamente, pero no se pudo actualizar la lista de prestaciones.',
            ));
        }
    }

    openEditModuleDialog(row?: CaseModuleComposed) {
        const target = row ?? this.expandedElement;
        if (!target) return;
        this.isAddMode = false;
        this.isEditMode = true;
        this.isRePostMode = false;
        this.caseModuleSelected = target;

        // Podés ajustar el título según lo que quieras editar
        this.title = this.experienceI18n.translate('modules.cases.modules-scheme.editar-modulo', undefined, 'Editar módulo');
        this.checkboxAuthorizationText = !!this.caseModuleSelected.authorization ? this.experienceI18n.translate('modules.cases.modules-scheme.reemplazar-autorizacion', undefined, 'Reemplazar autorización') : this.experienceI18n.translate('modules.cases.modules-scheme.agregar-autorizacion', undefined, 'Agregar autorización');

        // IMPORTANTE: resolver el módulo de catálogo (no usar target.id)
        const catalogModuleId = target.moduleId;
        if (!catalogModuleId) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-resolver-el-modulo-de-catalogo-para-editar', undefined, 'No se pudo resolver el módulo de catálogo para editar.'), icon: 'error' });
            return;
        }

        this.arrangementSelected = target.arrangementId != null
            ? this.caseArrangements.find(arr => arr.id === target.arrangementId)
            : undefined;
        this.moduleSelected = target;

        // Crea el form antes de usar formModule/formFrequency.
        // En edición, addModuleForm deja seleccionado el módulo y carga items.
        this.addModuleForm(undefined, undefined, target);

        // ---- Lo que faltaba: hidratar valores del módulo en el form ----
        const sourceCaseModule = this.caseModules?.find(caseModule => caseModule.id === target.id);
        const parsedVigency = this.parseVigencyToDates(target.vigency);
        const from = sourceCaseModule?.fromDate ?? target.fromDate ?? parsedVigency.from;
        const to = sourceCaseModule?.toDate ?? target.toDate ?? parsedVigency.to;
        if (from) this.formModule.get(this.VALIDITY_FROM_DATE)?.setValue(from, { emitEvent: true });
        if (to) this.formModule.get(this.VALIDITY_TO_DATE)?.setValue(to, { emitEvent: true });

        const fee = sourceCaseModule?.fee ?? target.fee;
        if (fee != null) {
            this.formModule.get(this.FEE)?.setValue(fee, { emitEvent: false });
        }

        const parsedFrequency = this.parseFrequencyString(target.frequency);
        const amount = sourceCaseModule?.frequency?.amount ?? parsedFrequency.amount;
        const unit = sourceCaseModule?.frequency?.unit ?? parsedFrequency.unit;
        if (amount != null) this.formFrequency.get(this.AMOUNT)?.setValue(amount, { emitEvent: false });
        if (unit != null) this.formFrequency.get(this.UNIT)?.setValue(unit, { emitEvent: false });

        // Si querés que en edición NO se pueda cambiar el módulo:
        this.setControlEnabled(this.formModule.get(this.MODULE), false);
        this.setControlEnabled(this.formModule.get(this.MODULE_SEARCHING), false);

        // Remove validator because in edit mode, if there's already an arrangement, we might not require fee to be filled again
        if (target.arrangementId != null) {
            this.formModule.get(this.FEE)?.clearValidators();
        }

        // Recalcular validaciones si aplica
        this.formModule.setValidators(CustomValidators.fromToDate(this.VALIDITY_FROM_DATE, this.VALIDITY_TO_DATE));
        this.formModule.updateValueAndValidity({ emitEvent: false });
        this.formFrequency.updateValueAndValidity({ emitEvent: false });
    }

    clearSelectedPractices(): void {
        this.modulePracticesSelected = [];
        this.practicesSelection.clear();

        if (this.formModule?.get(this.PRACTICE_ITEMS)) {
            this.syncPracticeItemsToForm([]);
            this.practiceItemsFA.markAsDirty();
        }
    }

    clearSelectedSupplies(): void {
        this.moduleSuppliesSelected = [];
        this.suppliesSelection.clear();

        if (this.formModule?.get(this.SUPPLY_ITEMS)) {
            this.syncSupplyItemsToForm([]);
            this.supplyItemsFA.markAsDirty();
        }
    }

    openPracticesPickerDialog(): void {
        if (!this.moduleSelected) return;

        const practiceNameById = new Map<number, string>();
        const restrictionIdByPracticeId = new Map<number, number>();
        for (const mi of (this.moduleSelected?.moduleItems ?? [])) {
            if ((mi as any)?.isSupply) continue;
            const pid = Number((mi as any)?.practiceId);
            const pname = String((mi as any)?.practiceName ?? '').trim();
            const restrictionId = Number((mi as any)?.restrictionId);
            if (Number.isFinite(pid) && pid > 0) {
                if (pname) practiceNameById.set(pid, pname);
                if (Number.isFinite(restrictionId)) restrictionIdByPracticeId.set(pid, restrictionId);
            }
        }

        const modulePracticeIds = Array.from(
            new Set(
                (this.moduleSelected?.moduleItems ?? [])
                    .filter(i => !i.isSupply)
                    .map(i => i.practiceId)
                    .filter((id): id is number => Number.isFinite(Number(id)) && Number(id) > 0)
            )
        );

        const openPicker = () => {
            const practiceById = new Map<number, Practice>();
            for (const p of this.practicesToShow ?? []) practiceById.set(p.id, p);
            for (const p of this.practices ?? []) practiceById.set(p.id, p);
            for (const prov of this.provisions ?? []) {
                const pr = (prov as any)?.practice as Practice | undefined;
                if (pr?.id != null) practiceById.set(pr.id, pr);
            }

            const sourcePractices: Practice[] = modulePracticeIds.length
                ? modulePracticeIds.map(id => {
                    const found = practiceById.get(id);
                    const fallbackName = practiceNameById.get(id);
                    const restrictionId = restrictionIdByPracticeId.get(id) ?? found?.restriction?.id;
                    if (found) {
                        const updates: any = { restrictionId };
                        if ((!found.name || !String(found.name).trim()) && fallbackName) {
                            updates.name = fallbackName;
                        }
                        return { ...found, ...updates };
                    }
                    return { id, name: fallbackName ?? this.experienceI18n.translate('modules.cases.modules-scheme.practica-id', { id }, `Práctica ${id}`), code: String(id), restrictionId } as any;
                })
                : (this.practicesToShow ?? this.practices ?? []);

            this.buildPracticesPickerForm(sourcePractices);
            this._practicesDialogRef = this.dialog.open(DialogComponent, {
                disableClose: true,
                width: '80%',
                maxHeight: '95vh',
                panelClass: 'practices-picker-dialog',
                data: { template: this.practicesPickerTemplate, title: this.experienceI18n.translate('modules.cases.modules-scheme.cargar-practicas', undefined, 'Cargar prácticas') },
            });
        };

        if (!modulePracticeIds.length) {
            openPicker();
            return;
        }

        const selectedModuleId = this.moduleSelected.id;
        this.nomenclatorService.getPractices({ ids: modulePracticeIds, active: true })
            .pipe(
                take(1),
                catchError(() => of(null)),
            )
            .subscribe(response => {
                if (this.moduleSelected?.id !== selectedModuleId) return;

                if (response?.data?.length) {
                    this.practices = response.data;
                    this.practicesToShow = response.data;
                }
                openPicker();
            });
    }

    getFrequencyUnitLabel(value: any): string {
        const opt = this.provisionsFrequencyUnit?.find(x => this.calculateIdUnit(x) === value || x.id === value);
        return opt ? this.calculateNameUnit(opt) : '—';
    }

    // ===== Supplies picker (Cargar insumos) =====
    getSupplyVendorOptions(): string[] {
        const vendors = new Set<string>();
        for (const prov of this.provisions ?? []) {
            const vendor = this.resolveSupplyProviderNameFromProvision(prov) ?? '';
            if (vendor) vendors.add(vendor);
        }
        return Array.from(vendors);
    }

    onSupplyPickerProviderSelected(row: FormGroup, provider: Provider | any): void {
        if (!row) return;

        if (!provider) {
            row.get('providerId')?.setValue(null, { emitEvent: false });
            row.get('providerName')?.setValue(null, { emitEvent: false });
            return;
        }

        // Normal case: the component emits the full entity
        if (typeof provider === 'object') {
            const p = provider as Provider;
            const providerId = (p as any)?.id ?? null;
            row.get('providerId')?.setValue(providerId, { emitEvent: false });
            row.get('providerName')?.setValue(providerId != null ? this.entitiesFacade.getProvidersFullName(p) : null, { emitEvent: false });
            return;
        }

        // Fallback: if it emits an id
        const id = Number(provider);
        row.get('providerId')?.setValue(Number.isFinite(id) ? id : null, { emitEvent: false });
    }

    openSuppliesPickerDialog(): void {
        if (!this.moduleSelected) return;

        const supplyNameById = new Map<number, string>();
        for (const mi of (this.moduleSelected?.moduleItems ?? [])) {
            if (!(mi as any)?.isSupply) continue;
            const pid = Number((mi as any)?.practiceId);
            const pname = String((mi as any)?.practiceName ?? '').trim();
            if (Number.isFinite(pid) && pid > 0 && pname) {
                supplyNameById.set(pid, pname);
            }
        }

        // Fuente de opciones: items del módulo seleccionado (solo insumos)
        const moduleSupplyIds = Array.from(
            new Set(
                (this.moduleSelected?.moduleItems ?? [])
                    .filter(i => i.isSupply)
                    .map(i => i.practiceId)
                    .filter((id): id is number => Number.isFinite(Number(id)) && Number(id) > 0)
            )
        );

        const supplyById = new Map<number, Practice>();
        for (const p of this.practicesToShow ?? []) supplyById.set(p.id, p);
        for (const p of this.practices ?? []) supplyById.set(p.id, p);
        for (const prov of this.provisions ?? []) {
            const pr = (prov as any)?.practice as Practice | undefined;
            if (pr?.id != null) supplyById.set(pr.id, pr);
        }

        const sourceSupplies: Practice[] = moduleSupplyIds.length
            ? moduleSupplyIds.map(id => {
                const found = supplyById.get(id);
                const fallbackName = supplyNameById.get(id);
                if (found) {
                    if ((!found.name || !String(found.name).trim()) && fallbackName) {
                        return { ...found, name: fallbackName };
                    }
                    return found;
                }
                return { id, name: fallbackName ?? this.experienceI18n.translate('modules.cases.modules-scheme.insumo-id', { id }, `Insumo ${id}`), code: String(id) };
            })
            : [];

        // Única fuente de verdad (evita duplicar FormArray + subs)
        this.buildSuppliesPickerForm(sourceSupplies);

        this._suppliesDialogRef = this.dialog.open(DialogComponent, {
            disableClose: true,
            minWidth: '85%',
            maxHeight: '95vh',
            panelClass: 'supplies-picker-dialog',
            data: { template: this.suppliesPickerTemplate, title: this.suppliesPickerDialogTitle || this.experienceI18n.translate('modules.cases.modules-scheme.cargar-insumos', undefined, 'Cargar insumos') },
        });
    }

    onPracticesPickerSearch(st: string): void {
        this.practicesPickerSearchControl.setValue(st ?? '');
    }

    onSuppliesPickerSearch(st: string): void {
        this.suppliesPickerSearchControl.setValue(st ?? '');
    }

    markPracticePickerControlTouched(row: FormGroup, controlName: string): void {
        const control = row?.get(controlName);
        if (!control || control.disabled) return;
        control.markAsTouched();
        control.updateValueAndValidity({ emitEvent: false });
    }

    private markPracticeRowInvalidControlsAsTouched(row: FormGroup): void {
        const controlsToCheck = ['frequencyAmount', 'frequencyUnit'];
        if (this.rowRequiresAllowedHours(row)) {
            controlsToCheck.push('allowedHours');
        }

        for (const name of controlsToCheck) {
            const control = row.get(name);
            if (!control || control.disabled) continue;
            control.updateValueAndValidity({ emitEvent: false });
            if (control.invalid) {
                control.markAsTouched();
            }
        }
    }

    isPracticePickerRowWithVisibleErrors(row: FormGroup): boolean {
        if (!row || row.get('selected')?.value !== true) return false;

        const controlsToCheck = ['frequencyAmount', 'frequencyUnit'];
        if (this.rowRequiresAllowedHours(row)) {
            controlsToCheck.push('allowedHours');
        }

        return controlsToCheck.some((name) => {
            const control = row.get(name);
            return !!control && control.enabled && control.invalid && (control.touched || control.dirty);
        });
    }

    closeSuppliesPickerDialog(): void {
        this._suppliesDialogRef?.close();
    }

    applySuppliesPickerSelection(): void {
        if (!this.suppliesPickerForm) return;
        if (this.suppliesPickerForm.invalid) {
            this.suppliesPickerForm.markAllAsTouched();
            return;
        }

        const rows = (this.suppliesPickerRowsFA?.getRawValue?.() ?? []) as any[];
        const selected = rows.filter(r => r.selected);
        this.moduleSuppliesSelected = selected.map(r => ({
            practiceId: r.practiceId,
            provisionFeeId: this.toFiniteOrNull(r.provisionFeeId) || null,
            code: r.code,
            caseModuleId: r.caseModuleId,
            name: r.name,
            providerId: r.providerId ?? null,
            provider: null,
            providerName: r.providerName ?? null,
            quantity: this.toFiniteOrNull(r.quantity),
            unit: r.unit ?? ProvisionsUnit.UNIDAD,
            unitCost: this.toFiniteOrNull(r.unitCost),
        }));

        // Flujo: "Cargar insumos" en CaseModule existente -> PATCH con datos del CaseModule + insumos.
        const isCaseModuleAddSuppliesFlow =
            !!this.caseModuleSelected &&
            this.isEditMode === true &&
            this.isAddMode === false &&
            this.isRePostMode === false &&
            this.suppliesPickerEditMode === false;

        if (isCaseModuleAddSuppliesFlow) {
            const existingIds = this.existingSupplyIdsForPicker ?? new Set<number>();

            // Separar insumos nuevos de los que ya existen
            const allSelectedIds = (this.moduleSuppliesSelected ?? [])
                .map(s => Number(s.practiceId))
                .filter(id => Number.isFinite(id) && id > 0);

            const newSelectedIds = new Set<number>(
                allSelectedIds.filter(id => !existingIds.has(id))
            );

            const duplicateSelectedIds = new Set<number>(
                allSelectedIds.filter(id => existingIds.has(id))
            );

            // Si no seleccionó nada, cerrar
            if (allSelectedIds.length === 0) {
                this.closeSuppliesPickerDialog();
                return;
            }

            // Si hay duplicados (todos o algunos), avisar y preguntar si quiere continuar
            if (duplicateSelectedIds.size > 0) {
                const duplicateNames = (this.moduleSuppliesSelected ?? [])
                    .filter(s => duplicateSelectedIds.has(Number(s.practiceId)))
                    .map(s => s.code ? `${s.code} - ${s.name}` : s.name)
                    .join(', ');

                const newNames = (this.moduleSuppliesSelected ?? [])
                    .filter(s => newSelectedIds.has(Number(s.practiceId)))
                    .map(s => s.code ? `${s.code} - ${s.name}` : s.name)
                    .join(', ');

                const allAreDuplicates = newSelectedIds.size === 0;

                const htmlContent = allAreDuplicates
                    ? `<p><strong>${this.experienceI18n.translate('modules.cases.modules-scheme.ya-cargados', undefined, 'Ya cargados')}:</strong> ${duplicateNames}</p>
                       <p>${this.experienceI18n.translate('modules.cases.modules-scheme.desea-agregarlos-de-todas-formas', undefined, '¿Desea agregarlos de todas formas?')}</p>`
                    : `<p><strong>${this.experienceI18n.translate('modules.cases.modules-scheme.ya-cargados', undefined, 'Ya cargados')}:</strong> ${duplicateNames}</p>
                       <p><strong>${this.experienceI18n.translate('modules.cases.modules-scheme.nuevos', undefined, 'Nuevos')}:</strong> ${newNames}</p>
                       <p>${this.experienceI18n.translate('modules.cases.modules-scheme.desea-continuar', undefined, '¿Desea continuar?')}</p>`;

                this.swalWithCustomizeButtons.fire({
                    title: this.experienceI18n.translate('modules.cases.modules-scheme.algunos-insumos-ya-existen', undefined, 'Algunos insumos ya existen'),
                    html: htmlContent,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: this.experienceI18n.translate('common.continue', undefined, 'Continuar'),
                    cancelButtonText: this.experienceI18n.translate('common.cancel', undefined, 'Cancelar'),
                    reverseButtons: true,
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Enviar TODOS los seleccionados (incluidos duplicados)
                        this.executePatchWithSupplies(new Set(allSelectedIds));
                    }
                });
                return;
            }

            // Solo insumos nuevos, proceder directamente
            this.executePatchWithSupplies(new Set(allSelectedIds));
            return;
        }

        // Flujo: "Modificar insumos" en CaseModule existente -> PATCH solo de items modificados
        const isCaseModuleEditSuppliesFlow =
            !!this.caseModuleSelected &&
            this.isEditMode === true &&
            this.isAddMode === false &&
            this.isRePostMode === false &&
            this.suppliesPickerEditMode === true;

        if (isCaseModuleEditSuppliesFlow) {
            this.executePatchForModifiedSupplies();
            return;
        }

        // Mantener el form centralizado en sync con el picker
        if (this.formModule?.get?.(this.SUPPLY_ITEMS)) {
            this.syncSupplyItemsToForm(this.moduleSuppliesSelected);
            // Importante: el sync recrea controles (quedan pristine). Marcamos como dirty si el usuario confirmó el picker.
            this.supplyItemsFA?.markAsDirty();
        }

        const selectedSupplyIds = new Set(this.moduleSuppliesSelected.map(s => s.practiceId));
        this.suppliesSelection.clear();
        const items = this.moduleSelected?.moduleItems ?? [];
        const selectedModuleItems = items.filter(i => i.isSupply && selectedSupplyIds.has(i.practiceId));
        if (selectedModuleItems.length) this.suppliesSelection.select(...selectedModuleItems);

        this.closeSuppliesPickerDialog();
    }

    /**
     * Ejecuta el PATCH para agregar los insumos seleccionados
     */
    private executePatchWithSupplies(selectedIds: Set<number>): void {
        const anyCM = this.caseModuleSelected as any;
        const { fromDate, toDate } = this.resolveCaseModulePatchDateRange(anyCM);

        if (!fromDate || !toDate) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-resolver-la-vigencia-del-modulo', undefined, 'No se pudo resolver la vigencia del módulo.'), icon: 'error' });
            return;
        }

        const freqFromString = this.parseFrequencyString(anyCM?.frequency);
        const freqAmount = this.toFiniteOrNull(anyCM?.frequencyAmount ?? freqFromString?.amount) ?? 0;
        const freqUnit = String(anyCM?.frequencyUnit ?? freqFromString?.unit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT);

        const suppliesToAdd = (this.moduleSuppliesSelected ?? []).filter(s => selectedIds.has(Number(s.practiceId)));
        const missingModuleItem = suppliesToAdd.find(s => this.resolveModuleItemIdForSupply(Number(s.practiceId)) == null);

        if (missingModuleItem) {
            const label = missingModuleItem.code
                ? `${missingModuleItem.code} - ${missingModuleItem.name}`
                : missingModuleItem.name;
            (this.swalAutoClose ?? Swal).fire({ text: `${this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-resolver-el-moduleitemid-del-insumo', undefined, 'No se pudo resolver el moduleItemId del insumo')}: ${label}.`, icon: 'error' });
            return;
        }

        const supplyItems = suppliesToAdd.map(s => {
            const moduleItemId = this.resolveModuleItemIdForSupply(Number(s.practiceId));
            return {
                moduleItemId,
                medicalSupply: {
                    provision: {
                        practiceId: Number(s.practiceId),
                        frequency: {
                            amount: Number(s.quantity) || 0,
                            unit: String(s.unit ?? ProvisionsUnit.UNIDAD),
                        },
                        ...(s.unitCost != null ? { fee: Number(s.unitCost) || 0 } : {}),
                    },
                    agreement: s.providerId != null
                        ? {
                            providerFee: Number(s.unitCost) || 0,
                            providerId: s.providerId,
                            fromDate,
                            toDate,
                        }
                        : null,
                },
            };
        });

        const patchBody: any = {
            fromDate,
            toDate,
            frequency: {
                amount: Number(freqAmount) || 0,
                unit: freqUnit,
            },
            supplyItems,
        };

        this.modulesFacade.patchCaseModule(patchBody, this.caseId, this.caseModuleSelected.id).subscribe({
            next: () => {
                this.caseModulesChanged.emit();
                const count = suppliesToAdd.length;
                const msg = count === 1
                    ? this.experienceI18n.translate('modules.cases.modules-scheme.insumo-agregado-con-exito', undefined, 'Insumo agregado con éxito')
                    : `${count} ${this.experienceI18n.translate('modules.cases.modules-scheme.insumos-agregados-con-exito', undefined, 'insumos agregados con éxito').toLowerCase()}`;
                (this.swalAutoClose ?? Swal).fire({ text: msg, icon: 'success' });
                this.closeSuppliesPickerDialog();
            },
            error: (error) => {
                const msg = error?.error?.message || this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudieron-agregar-los-insumos.', undefined, 'No se pudieron agregar los insumos.');
                (this.swalAutoClose ?? Swal).fire({ text: msg, icon: 'error' });
            },
        });
    }

    /**
     * Ejecuta el PATCH solo para los insumos que fueron modificados.
     * Compara los valores actuales con los originales (this.originalSuppliesForEdit).
     */
    private executePatchForModifiedSupplies(): void {
        const originalByKey = new Map<string, ModuleSupplySelected>();
        for (const orig of this.originalSuppliesForEdit ?? []) {
            const key = orig.provisionFeeId != null ? `pf_${orig.provisionFeeId}` : `pr_${orig.practiceId}`;
            originalByKey.set(key, orig);
        }

        // Detectar cuáles fueron modificados
        const modifiedSupplies: ModuleSupplySelected[] = [];
        for (const current of this.moduleSuppliesSelected ?? []) {
            const key = current.provisionFeeId != null ? `pf_${current.provisionFeeId}` : `pr_${current.practiceId}`;
            const orig = originalByKey.get(key);
            if (!orig) {
                // Es nuevo (no debería pasar en edit mode, pero por si acaso)
                continue;
            }
            // Comparar campos editables
            const hasChanges =
                current.providerId !== orig.providerId ||
                current.quantity !== orig.quantity ||
                current.unit !== orig.unit ||
                current.unitCost !== orig.unitCost;

            if (hasChanges) {
                modifiedSupplies.push(current);
            }
        }

        if (modifiedSupplies.length === 0) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-detectaron-cambios', undefined, 'No se detectaron cambios.'), icon: 'info' });
            this.closeSuppliesPickerDialog();
            this.originalSuppliesForEdit = [];
            return;
        }

        // Construir el array de PatchProvisionFeeBody
        // OJO: las provisions dentro de caseModulesComposed suelen estar “expandidas” (1 provisionFee),
        // por lo que NO deben usarse para actualizar el state global.
        const expandedModuleProvisions = (this.caseModuleSelected as any)?.provisions ?? [];
        const patchItems: any[] = [];

        for (const mod of modifiedSupplies) {
            if (mod.provisionFeeId == null) {
                console.warn('Insumo sin provisionFeeId, no se puede hacer PATCH:', mod);
                continue;
            }

            // Buscar la provision correspondiente para poder completar datos actuales del ProvisionFee
            const provision = expandedModuleProvisions.find((p: any) => {
                return Number(p?.provisionFeeId) === mod.provisionFeeId;
            });

            const currentProvisionFee = this.resolveCurrentProvisionFee(provision);

            patchItems.push({
                id: mod.provisionFeeId,
                // Mantener valores actuales para no alterar otros campos
                fee: currentProvisionFee?.fee ?? undefined,
                profitMargin: (currentProvisionFee as any)?.profitMargin ?? undefined,
                fixedPrice: (currentProvisionFee as any)?.fixedPrice ?? undefined,
                fromDate: currentProvisionFee?.fromDate ?? undefined,
                toDate: currentProvisionFee?.toDate ?? undefined,
                frequency: {
                    amount: Number(mod.quantity) || 0,
                    unit: String(mod.unit ?? ProvisionsUnit.UNIDAD),
                },
                providerId: mod.providerId ?? null,
                // El campo editable en el diálogo es el costo unitario (providerFee)
                providerFee: mod.unitCost ?? null,
            });
        }

        if (patchItems.length === 0) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudieron-preparar-los-cambios', undefined, 'No se pudieron preparar los cambios.'), icon: 'error' });
            this.closeSuppliesPickerDialog();
            this.originalSuppliesForEdit = [];
            return;
        }

        // En el flujo viejo (supply-list) el backend espera un DTO objeto, no un array.
        // Mantengo el facade como está y adapto el payload acá.
        const patchBody: any = {
            provisionFees: patchItems,
            update: true,
        };

        // Usar como source de merge el listado completo de provisiones del caso (como supply-list),
        // para evitar que el state pise provisionFees con arrays parciales.
        this.provisionsFacade.getAllProvisionsCase$().pipe(take(1)).subscribe((allProvisions) => {
            const provisionsForMerge = (Array.isArray(allProvisions) && allProvisions.length > 0)
                ? allProvisions
                : expandedModuleProvisions;

            this.provisionsFacade.updateProvisionFees(patchBody as any, this.caseId, provisionsForMerge).subscribe({
                next: () => {
                    this.caseModulesChanged.emit();
                    const count = patchItems.length;
                    const msg = count === 1
                        ? this.experienceI18n.translate('modules.cases.modules-scheme.insumo-modificado-con-exito', undefined, 'Insumo modificado con éxito')
                        : `${count} ${this.experienceI18n.translate('modules.cases.modules-scheme.insumos-modificado-con-exito', undefined, 'insumos modificados con éxito').toLowerCase()}`;
                    (this.swalAutoClose ?? Swal).fire({ text: msg, icon: 'success' });
                    this.closeSuppliesPickerDialog();
                    this.originalSuppliesForEdit = [];
                },
                error: (error) => {
                    const msg = error?.error?.message || this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudieron-modificar-los-insumos', undefined, 'No se pudieron modificar los insumos.');
                    (this.swalAutoClose ?? Swal).fire({ text: msg, icon: 'error' });
                },
            });
        });
    }

    getSelectedPickerSuppliesCount(): number {
        const rows = (this.suppliesPickerRowsFA?.controls ?? []) as FormGroup[];
        return rows.filter(r => r.get('selected')?.value === true).length;
    }

    isAllPickerSuppliesSelected(): boolean {
        const visible = this.getFilteredSuppliesPickerRowVM();
        return visible.length > 0 && visible.every(r => r.fg.get('selected')?.value === true);
    }

    isSomePickerSuppliesSelected(): boolean {
        const visible = this.getFilteredSuppliesPickerRowVM();
        const selectedCount = visible.filter(r => r.fg.get('selected')?.value === true).length;
        return selectedCount > 0 && selectedCount < visible.length;
    }

    toggleAllPickerSupplies(checked: boolean): void {
        for (const r of this.getFilteredSuppliesPickerRowVM()) {
            r.fg.get('selected')?.setValue(checked);
        }
    }

    closePracticesPickerDialog(): void {
        this._practicesDialogRef?.close();
    }

    applyPracticesPickerSelection(): void {
        if (!this.practicesPickerForm) return;
        if (this.practicesPickerForm.invalid) {
            this.practicesPickerForm.markAllAsTouched();
            return;
        }

        const rows = (this.practicesPickerRowsFA?.getRawValue?.() ?? []) as any[];
        const selected = rows.filter(r => r.selected);
        this.modulePracticesSelected = selected.map(r => ({
            practiceId: r.practiceId,
            code: r.code,
            name: r.name,
            frequencyAmount: this.toFiniteOrNull(r.frequencyAmount),
            frequencyUnit: r.frequencyUnit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT,
            allowedHours: this.toNumberOrNull(r.allowedHours),
            restrictionId: this.toNumberOrNull(r.restrictionId),
            restrictionRequiredTime: !!r.restrictionRequiredTime,
            restrictionPatient: !!r.restrictionPatient,
            minAttentions: this.toFiniteOrNull(r.minAttentions),
            maxAttentions: this.toFiniteOrNull(r.maxAttentions),
        }));

        // Si estamos en el flujo "Cargar prácticas" de un CaseModule existente,
        // mandamos un PATCH mínimo con solo las prácticas NUEVAS (no las bloqueadas).
        const isCaseModuleAddPracticesFlow =
            !!this.caseModuleSelected &&
            this.isEditMode === true &&
            this.isAddMode === false &&
            this.isRePostMode === false &&
            !!this.editingModuleRow;

        if (isCaseModuleAddPracticesFlow) {
            const lockedIds = this.lockedPracticeIdsForPicker ?? new Set<number>();
            const newSelectedIds = new Set<number>(
                (this.modulePracticesSelected ?? [])
                    .map(p => Number(p.practiceId))
                    .filter(id => Number.isFinite(id) && id > 0 && !lockedIds.has(id))
            );

            // Si no agregaron nada nuevo, solo cerramos.
            if (newSelectedIds.size === 0) {
                this.closePracticesPickerDialog();
                return;
            }

            const newPractices = (this.modulePracticesSelected ?? []).filter(p => newSelectedIds.has(Number(p.practiceId)));
            const missingModuleItem = newPractices.find(p => this.resolveModuleItemIdForPractice(Number(p.practiceId)) == null);
            if (missingModuleItem) {
                const label = missingModuleItem.code
                    ? `${missingModuleItem.code} - ${missingModuleItem.name}`
                    : missingModuleItem.name;
                (this.swalAutoClose ?? Swal).fire({ text: `${this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-resolver-el-moduleitemid-de-la-practica', undefined, 'No se pudo resolver el moduleItemId de la práctica')}: ${label}.`, icon: 'error' });
                return;
            }

            // PATCH como "corregir", pero mandando SOLO las prácticas nuevas.
            // (Backend requiere además fromDate/toDate/frequency).
            const anyCM = this.caseModuleSelected as any;

            const { fromDate, toDate } = this.resolveCaseModulePatchDateRange(anyCM);
            if (!fromDate || !toDate) {
                (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-resolver-la-vigencia-del-modulo', undefined, 'No se pudo resolver la vigencia del módulo.'), icon: 'error' });
                return;
            }

            const freqFromString = this.parseFrequencyString(anyCM?.frequency);
            const freqAmount = this.toFiniteOrNull(anyCM?.frequencyAmount ?? freqFromString?.amount) ?? 0;
            const freqUnit = String(anyCM?.frequencyUnit ?? freqFromString?.unit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT);

            const practiceItems = newPractices.map(p => {
                const frequency: any = {
                    amount: Number(p.frequencyAmount) || 0,
                    unit: (p.frequencyUnit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT),
                };
                if (this.isGuardiaLikePracticeItem(p)) {
                    frequency.allowedHours = this.toFiniteOrNull(p.allowedHours);
                }
                return {
                    moduleItemId: this.resolveModuleItemIdForPractice(Number(p.practiceId)),
                    practiceId: Number(p.practiceId),
                    frequency,
                };
            });

            const patchBody: any = {
                fromDate,
                toDate,
                frequency: {
                    amount: Number(freqAmount) || 0,
                    unit: freqUnit,
                },
                practiceItems,
            };

            this.modulesFacade.patchCaseModule(patchBody, this.caseId, this.caseModuleSelected.id).subscribe({
                next: () => {
                    this.caseModulesChanged.emit();
                    (this.swalAutoClose ?? Swal).fire({
                        text: this.experienceI18n.translate('modules.cases.modules-scheme.practicas-agregadas-con-exito', undefined, 'Prácticas agregadas con éxito'),
                        icon: 'success',
                    });
                    this.closePracticesPickerDialog();
                },
                error: (error) => {
                    const msg = error?.error?.message || this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudieron-agregar-las-practicas', undefined, 'No se pudieron agregar las prácticas.');
                    (this.swalAutoClose ?? Swal).fire({ text: msg, icon: 'error' });
                },
            });
            return;
        }

        // Mantener el form centralizado en sync con el picker
        if (this.formModule?.get?.(this.PRACTICE_ITEMS)) {
            this.syncPracticeItemsToForm(this.modulePracticesSelected);
            // Importante: el sync recrea controles (quedan pristine). Marcamos como dirty si el usuario confirmó el picker.
            this.practiceItemsFA?.markAsDirty();
        }

        // Sincroniza con el SelectionModel histórico + formControl `moduleItemIds`
        // para que el POST mande solo lo seleccionado.
        const selectedPracticeIds = new Set(this.modulePracticesSelected.map(p => p.practiceId));
        this.practicesSelection.clear();
        const items = this.moduleSelected?.moduleItems ?? [];
        const selectedModuleItems = items.filter(i => !i.isSupply && selectedPracticeIds.has(i.practiceId));
        if (selectedModuleItems.length) this.practicesSelection.select(...selectedModuleItems);
        this.closePracticesPickerDialog();
    }

    getSelectedPickerPracticesCount(): number {
        const rows = (this.practicesPickerRowsFA?.controls ?? []) as FormGroup[];
        return rows.filter(r => r.get('selected')?.value === true).length;
    }

    isAllPickerPracticesSelected(): boolean {
        const visible = this.getFilteredPracticePickerRowVM();
        return visible.length > 0 && visible.every(r => r.fg.get('selected')?.value === true);
    }

    isSomePickerPracticesSelected(): boolean {
        const visible = this.getFilteredPracticePickerRowVM();
        const selectedCount = visible.filter(r => r.fg.get('selected')?.value === true).length;
        return selectedCount > 0 && selectedCount < visible.length;
    }

    getSelectablePickerPracticesCount(): number {
        const visible = this.getFilteredPracticePickerRowVM();
        return visible.filter(r => r.fg.get('selected')?.enabled).length;
    }

    toggleAllPickerPractices(checked: boolean): void {
        for (const r of this.getFilteredPracticePickerRowVM()) {
            const ctrl = r.fg.get('selected');
            if (ctrl?.disabled) continue;
            ctrl?.setValue(checked);
        }
    }

    private toFiniteOrNull(value: any): number | null {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }

    // Como toFiniteOrNull, pero null/''/undefined siguen siendo null (Number(null) === 0).
    private toNumberOrNull(value: any): number | null {
        return value == null || value === '' ? null : this.toFiniteOrNull(value);
    }

    // ===== Forms: module =====
    private setCaseArrangements(arrangements: CaseArrangement[]): void {
        this.caseArrangements = arrangements;
        this.caseArrangementsWithModules = arrangements.filter(arrangement => arrangement.moduleArrangements?.length > 0);
        this.caseArrangementsForSelection = this.caseArrangementsWithModules.filter(arrangement => arrangement.active === true);
    }

    createModuleForm(provision?: Provision, provisionFee?: ProvisionFee, selectedModuleRow?: CaseModuleComposed | null): FormGroup {

        this.formFrequency = this.createFrequencyGroup(!!provisionFee ? provisionFee : provision); // Accept Provision or ProvisionFee
        this.hoursControl();
        // Form auth, it has other controls if add/edit provision
        this.formAuthorization = this.createAuthorizationGroup();
        let radioButton = '';
        if (!this.caseArrangementsForSelection || this.caseArrangementsForSelection.length == 0) {
            radioButton = this.experienceI18n.translate('modules.cases.modules-scheme.radio-sin-convenio', undefined, 'S/C');
            this.practicesToShow = this.practices;
        }

        // If exist provisionFee.arrangement
        if (!!provisionFee) {
            this.showTxtWithOutArrangement = !!provisionFee?.arrangementId ? true : false;
        }
        // If module has arrangement
        if (!!selectedModuleRow) {
            this.showTxtWithOutArrangement = !!selectedModuleRow?.arrangementId ? true : false;
        }

        return this.formBuilder.group({
            arrangement: new FormControl({ value: radioButton, disabled: false }),
            [this.MODULE]: ['', CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.modulo-requerido', undefined, 'Módulo requerido'))],
            [this.MODULE_SEARCHING]: [''],
            [this.VALIDITY_FROM_DATE]: ['', Validators.compose([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.vigencia-desde-requerida', undefined, 'Vigencia desde requerida'))])],
            [this.VALIDITY_TO_DATE]: ['', Validators.compose([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.vigencia-hasta-requerida', undefined, 'Vigencia hasta requerida'))])],
            [this.FEE]: ['', Validators.compose([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.valor-venta-requerido', undefined, 'Valor venta requerido'))])], // TODO: when going to v2 modules, remove required
            [this.MODULE_FREQUENCY]: this.formFrequency,
            [this.PRACTICE_ITEMS]: this.formBuilder.array([]),
            [this.SUPPLY_ITEMS]: this.formBuilder.array([]),
            [this.ARRANGEMENT]: [''],
            [this.IS_ADDING_AUTHORIZATION]: [{ value: false, disabled: this.isShowAuth ? false : true }],
            [this.AUTHORIZATION]: this.formAuthorization,
        });
    }

    openDialog(title: string, template, style: { minWidth: string, maxHeight: string }, afterClosed?): void {
        this._dialogRef = this.dialog.open(DialogComponent, {
            disableClose: true,
            minWidth: style.minWidth,
            maxHeight: style.maxHeight,
            data: { template, title },
        });
        this._dialogRef.afterClosed().subscribe(() => {
            afterClosed?.();
            this.resetModuleSelectionState();
        });
    }

    onClickCloseDialogAgreementTemplate(): void {
        this._dialogRefAgreementTemplate?.close();
    }

    openDialogAgreementTemplate(title: string, template, style: { minWidth: string, maxHeight: string }, afterClosed?): void {
        this._dialogRefAgreementTemplate = this.dialog.open(DialogComponent, {
            disableClose: true,
            minWidth: style.minWidth,
            maxHeight: style.maxHeight,
            data: { template, title },
        });
        this._dialogRefAgreementTemplate.afterClosed().subscribe(() => afterClosed?.());
    }

    // ===== Agreements: crear acuerdo prestacional (replica provisions-detail) =====
    private loadProvidersForAgreement(specialtyId: number | null | undefined): void {
        const sid = Number(specialtyId);
        if (!Number.isFinite(sid)) {
            this.providers = [];
            return;
        }

        const qps: ProvidersBySpecialtyQPS = { active: true, specialtyId: sid };
        this.providersService.getProvidersBySpecialty(qps).then(response => {
            this.providers = (response?.data ?? []).filter(p => p.active);
        }).catch(() => {
            this.providers = [];
        });
    }

    isPatientSelfCareProvision(provision: Provision | ProvisionRow | null | undefined): boolean {
        const resolved = this.resolveProvisionEntity(provision);
        return isPatientPracticeRestriction(resolved?.practice?.restriction);
    }

    isPatientSelfCareModulePractice(practice: ModulePracticeSelected | null | undefined): boolean {
        if (!practice) {
            return false;
        }
        if (practice.restrictionPatient != null) {
            return !!practice.restrictionPatient;
        }
        const catalogPractice =
            this.practices?.find(p => p.id === practice.practiceId) ??
            this.practicesToShow?.find(p => p.id === practice.practiceId);
        return isPatientPracticeRestriction(catalogPractice?.restriction);
    }

    isPatientSelfCarePracticePickerRow(row: { fg?: FormGroup } | null | undefined): boolean {
        const fg = row?.fg;
        if (!fg) {
            return false;
        }
        const flag = fg.get('restrictionPatient')?.value;
        if (flag != null && flag !== '') {
            return !!flag;
        }
        const practiceId = Number(fg.get('practiceId')?.value);
        if (!Number.isFinite(practiceId)) {
            return false;
        }
        const catalogPractice =
            this.practices?.find(p => p.id === practiceId) ??
            this.practicesToShow?.find(p => p.id === practiceId);
        return isPatientPracticeRestriction(catalogPractice?.restriction);
    }

    isAddAgreementDisabledForSelfCare(provision: Provision | ProvisionRow | null | undefined): boolean {
        const resolved = this.resolveProvisionEntity(provision);
        return this.isPatientSelfCareProvision(resolved) && (resolved?.agreements?.length ?? 0) > 0;
    }

    private resolveProvisionEntity(provision: Provision | ProvisionRow | null | undefined): Provision | null {
        if (!provision) {
            return null;
        }
        return (provision as ProvisionRow).raw ?? (provision as Provision);
    }

    isSelfCareAgreementForm(): boolean {
        return this.isPatientSelfCareProvision(this.provisionToWork);
    }

    getCasePatientLabel(): string {
        return this.entitiesFacade.getProvidersFullName(this.case?.patient as Provider);
    }

    canSubmitAgreementForm(): boolean {
        if (!this.formAgreement) {
            return false;
        }
        if (this.isSelfCareAgreementForm()) {
            return this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.valid ?? false;
        }
        return this.formAgreement.valid;
    }

    private configureSelfCareAgreementForm(): void {
        if (!this.formAgreement || !this.isSelfCareAgreementForm()) {
            return;
        }
        const providerCtrl = this.formAgreement.get(this.PROVIDER);
        providerCtrl?.clearValidators();
        providerCtrl?.setValue(null);
        providerCtrl?.updateValueAndValidity({ emitEvent: false });

        const feeCtrl = this.formAgreement.get(this.AGREEMENT_PROVISION_FEE);
        feeCtrl?.clearValidators();
        feeCtrl?.setValue(0);
        feeCtrl?.updateValueAndValidity({ emitEvent: false });

        if (this.isAddModeAgreement && this.provisionToWork) {
            const fees = (this.provisionToWork as any)?.provisionFees as Array<{ fromDate?: any; toDate?: any }> | undefined;
            const lastFee = fees?.length ? fees[fees.length - 1] : null;
            const from = (this.provisionToWork as any).fromDate ?? lastFee?.fromDate;
            const to = (this.provisionToWork as any).toDate ?? lastFee?.toDate;
            if (from) {
                this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValue(moment(from).toDate());
            }
            if (to) {
                this.formAgreement.get(this.AGREEMENT_TO_DATE)?.setValue(moment(to).toDate());
            }
        }

        this.formAgreement.updateValueAndValidity({ emitEvent: false });
    }

    private createAgreementForm(): FormGroup {
        return this.formBuilder.group({
            [this.PROVIDER]: [this.agreementToWork?.provider ? this.agreementToWork.provider : '', [CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.prestador-requerido', undefined, 'Prestador requerido'))]],
            [this.PROVIDER_SEARCHING]: [''],
            [this.PROVIDER_NAME]: [{ value: '', disabled: true }],
            [this.AGREEMENT_FROM_DATE]: ['', [CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida'))]],
            [this.AGREEMENT_TO_DATE]: [''],
            [this.FILTERED_BY_REGION]: [''],
            radioButton: ['cost'],
            [this.AGREEMENT_MARGIN]: [{ value: '', disabled: true }],
            [this.AGREEMENT_PROVISION_FEE]: [
                '',
                [
                    CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.honorarios-del-prestador-requeridos', undefined, 'Honorarios del prestador requeridos')),
                    CustomValidators.minValue(0, this.experienceI18n.translate('modules.cases.modules-scheme.debe-ser-un-valor-positivo', undefined, 'Debe ser un valor positivo')),
                ],
            ],
        });
    }

    private agreementForm(): void {
        if (!this.provisionToWork) return;

        if (this.isAddModeAgreement) {
            this.loadProvidersForAgreement((this.provisionToWork as any)?.specialtyId);
        }

        this.formAgreement = this.createAgreementForm();

        if (!this.isAddModeAgreement && this.agreementToWork) {
            this.formAgreement.get(this.AGREEMENT_PROVISION_FEE)?.setValue(Number(this.agreementToWork.providerFee));
            this.formAgreement.get(this.PROVIDER_NAME)?.setValue(this.entitiesFacade.getProvidersFullName(this.agreementToWork.provider));

            if (!!this.isUpdateModeAgreement) {
                if (!!this.agreementToWork.toDate) {
                    const newFromDate = moment(this.agreementToWork.toDate, 'YYYY-MM-DD').add(1, 'days').toDate();
                    this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValue(newFromDate);
                } else {
                    const actualDate = moment(new Date(), 'YYYY-MM-DD').hours(0).minutes(0).seconds(0);
                    const agreementFromDate = moment(this.agreementToWork.fromDate, 'YYYY-MM-DD').hours(0).minutes(0).seconds(0);
                    if (moment(actualDate.format('YYYY-MM-DD')).isSameOrBefore(moment(agreementFromDate.format('YYYY-MM-DD')))) {
                        this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValue(moment(this.agreementToWork.fromDate).add(1, 'days').toDate());
                    } else {
                        this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValue(moment(moment().format('YYYY-MM-DD')).toDate());
                    }
                }
            } else {
                this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValue(this.agreementToWork.fromDate);
                if (!!this.agreementToWork.toDate) {
                    this.formAgreement.get(this.AGREEMENT_TO_DATE)?.setValue(this.agreementToWork.toDate);
                }
            }

            this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValidators(CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida')));
            if (this.isUpdateModeAgreement) {
                this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValidators(CustomValidators.dateGreaterThan(this.agreementToWork.fromDate));
            }

            // En actualización mostramos el radio group y habilitamos margen
            this.formAgreement.get('radioButton')?.setValue('cost');
            this.enableMarginField = false;
            this.formAgreement.get(this.AGREEMENT_MARGIN)?.disable({ emitEvent: false });
            this.formAgreement.get(this.AGREEMENT_PROVISION_FEE)?.enable({ emitEvent: false });
            // Forzar recálculo de validez completo: setValidators() no llama updateValueAndValidity()
            Object.values(this.formAgreement.controls).forEach(c => c.updateValueAndValidity({ emitEvent: false }));
            this.formAgreement.updateValueAndValidity();
        } else {
            const defaultFrom = this.dateRange?.historyFromDate ? moment(this.dateRange.historyFromDate).toDate() : null;
            if (defaultFrom) {
                this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValue(defaultFrom);
                this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValidators(
                    Validators.compose([
                        CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida')),
                        CustomValidators.dateLowerThan(this.formAgreement.get(this.AGREEMENT_TO_DATE)?.value),
                        CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-de-inicio-de-acuerdo-requerida', undefined, 'Fecha de inicio de acuerdo requerida')),
                    ]),
                );
                this.formAgreement.get(this.AGREEMENT_TO_DATE)?.setValidators(
                    Validators.compose([CustomValidators.dateGreaterThan(this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.value)]),
                );
            }
        }

        if (this.isSelfCareAgreementForm()) {
            this.configureSelfCareAgreementForm();
        }

        this.openDialogAgreementTemplate(this.title, this.agreementTemplate, { maxHeight: '95vh', minWidth: '30%' }, () => { });
    }

    openUpdateAgreementDialogForCaseModule(provision: Provision, agreement: any): void {
        this.isAddModeAgreement = false;
        this.isUpdateModeAgreement = true;
        this.actionAgreement = this.experienceI18n.translate('common.update', undefined, 'ACTUALIZAR');
        this.provisionToWork = provision;
        this.agreementToWork = agreement;
        this.title = this.experienceI18n.translate('modules.cases.modules-scheme.actualizar-acuerdo-prestacional', undefined, 'Actualizar acuerdo prestacional');
        this.openUpdateAgreementFeeDialog();
    }

    openFixAgreementDialogForCaseModule(provision: Provision, agreement: any): void {
        this.isAddModeAgreement = false;
        this.isUpdateModeAgreement = false;
        this.actionAgreement = this.experienceI18n.translate('modules.cases.modules-scheme.corregir', undefined, 'CORREGIR');
        this.provisionToWork = provision;
        this.agreementToWork = agreement;
        this.title = this.experienceI18n.translate('modules.cases.modules-scheme.corregir-acuerdo-prestacional', undefined, 'Corregir acuerdo prestacional');
        this.openUpdateAgreementFeeDialog();
    }

    private openUpdateAgreementFeeDialog(): void {
        this.agreementForm();
    }

    clickRadiobuttonAgreementCost(event: any) {
        if (!!event && !!event.value) {
            if (event.value == 'margin') {
                this.enableMarginField = true;
                this.formAgreement.get(this.AGREEMENT_PROVISION_FEE)?.disable();
                this.formAgreement.get(this.AGREEMENT_MARGIN)?.enable();
            } else {
                this.enableMarginField = false;
                this.formAgreement.get(this.AGREEMENT_MARGIN)?.disable();
                this.formAgreement.get(this.AGREEMENT_PROVISION_FEE)?.enable();
            }
        }
    }

    updateMargin() {
        if (!this.agreementToWork) return;
        const providerFee = Number(this.agreementToWork.providerFee);
        const current = Number(this.formAgreement.get(this.AGREEMENT_PROVISION_FEE)?.value);
        if (!Number.isFinite(providerFee) || providerFee === 0 || !Number.isFinite(current)) return;

        let newMargin = current / providerFee;
        newMargin *= 100;
        newMargin -= 100;
        this.formAgreement.get(this.AGREEMENT_MARGIN)?.setValue(newMargin.toFixed(2));
    }

    updateCost() {
        if (!this.agreementToWork) return;
        const baseFee = Number(this.agreementToWork.providerFee);
        const margin = Number(this.formAgreement.get(this.AGREEMENT_MARGIN)?.value);
        if (!Number.isFinite(baseFee)) return;

        if (Number.isFinite(margin) && this.formAgreement.get(this.AGREEMENT_MARGIN)?.value !== '') {
            const newCost = baseFee + baseFee * (margin / 100);
            this.formAgreement.get(this.AGREEMENT_PROVISION_FEE)?.setValue(Number(newCost.toFixed(2)));
        } else {
            this.formAgreement.get(this.AGREEMENT_MARGIN)?.setValue(Number(Number(0).toFixed(2)));
        }
    }

    setAgreementDateCustomValidators(fromDateSelected, toDateSelected) {
        this.addCustomValidatorsfromDate(toDateSelected);
        this.addCustomValidatorsToDate(fromDateSelected);
    }

    addCustomValidatorsfromDate(date: Date) {
        if (moment(date).isValid()) {
            this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.clearValidators();
            this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValidators(CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida')));
            this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValidators(CustomValidators.dateLowerEqualThan(date));
        }
    }

    addCustomValidatorsToDate(date: Date) {
        if (moment(date).isValid()) {
            this.formAgreement.get(this.AGREEMENT_TO_DATE)?.clearValidators();
            this.formAgreement.get(this.AGREEMENT_TO_DATE)?.setValidators(Validators.compose([CustomValidators.dateGreaterEqualThan(date)]));
        }
    }

    onClickDateFrom(event) {
        if (moment(event.value).isValid()) {
            const fromDateSelected = moment(event.value).toDate();
            let toDateSelected = null;
            if (moment(this.formAgreement.get(this.AGREEMENT_TO_DATE)?.value).isValid()) {
                toDateSelected = moment(this.formAgreement.get(this.AGREEMENT_TO_DATE)?.value).hours(23).minutes(59).seconds(59).toDate();
            }
            this.formAgreement.get(this.AGREEMENT_TO_DATE)?.setErrors(null);
            this.setAgreementDateCustomValidators(fromDateSelected, toDateSelected);
        }
    }

    onClickDateTo(event) {
        if (!!event.value) {
            if (moment(event.value).isValid()) {
                const toDateSelected = moment(event.value).hours(23).minutes(59).seconds(59).toDate();
                let fromDateSelected = null;
                if (!!this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.valid) {
                    fromDateSelected = moment(this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.value).toDate();
                }
                this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setErrors(null);
                this.setAgreementDateCustomValidators(fromDateSelected, toDateSelected);
                this.formAgreement.get(this.AGREEMENT_TO_DATE)?.updateValueAndValidity({ emitEvent: false });
            }
        } else {
            this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setErrors(null);
            this.formAgreement.get(this.AGREEMENT_TO_DATE)?.clearValidators();

            const isBeeingCorrect = !this.isAddModeAgreement && !this.isUpdateModeAgreement;
            if (isBeeingCorrect && !!this.agreementToWork?.toDate) {
                this.formAgreement.get(this.AGREEMENT_TO_DATE)?.setErrors({
                    required: { message: this.experienceI18n.translate('modules.cases.modules-scheme.vigencia-hasta-requerida-si-antes-ya-existia', undefined, 'Vigencia hasta requerida, si antes ya existia') }
                });
                this.formAgreement.get(this.AGREEMENT_TO_DATE)?.markAsTouched();
                return; // no pisar el valor ni limpiar validators
            }

            if (this.formAgreement.get(this.AGREEMENT_TO_DATE)?.touched) {
                this.formAgreement.get(this.AGREEMENT_TO_DATE)?.setValue(null);
            }
        }
    }

    onClickAgreementDateFrom(event: any) {
        if (!this.formAgreement) return;
        const from = this.coerceToDate(event?.value);
        if (!from) return;

        const to = this.coerceToDate(this.formAgreement.get(this.AGREEMENT_TO_DATE)?.value);

        this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.clearValidators();
        const fromValidators: ValidatorFn[] = [CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida'))];
        if (to) fromValidators.push(CustomValidators.dateLowerEqualThan(to));
        this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValidators(fromValidators);

        this.formAgreement.get(this.AGREEMENT_TO_DATE)?.clearValidators();
        const toValidators: ValidatorFn[] = [];
        if (to) toValidators.push(CustomValidators.dateGreaterEqualThan(from));
        this.formAgreement.get(this.AGREEMENT_TO_DATE)?.setValidators(toValidators);

        this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.updateValueAndValidity({ emitEvent: false });
        this.formAgreement.get(this.AGREEMENT_TO_DATE)?.updateValueAndValidity({ emitEvent: false });
    }

    onClickAgreementDateTo(event: any) {
        if (!this.formAgreement) return;
        const to = this.coerceToDate(event?.value);
        const from = this.coerceToDate(this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.value);
        if (!from) return;

        this.formAgreement.get(this.AGREEMENT_TO_DATE)?.clearValidators();
        const toValidators: ValidatorFn[] = [];
        if (to) toValidators.push(CustomValidators.dateGreaterEqualThan(from));
        this.formAgreement.get(this.AGREEMENT_TO_DATE)?.setValidators(toValidators);

        this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.clearValidators();
        const fromValidators: ValidatorFn[] = [CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida'))];
        if (to) fromValidators.push(CustomValidators.dateLowerEqualThan(to));
        this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.setValidators(fromValidators);

        this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.updateValueAndValidity({ emitEvent: false });
        this.formAgreement.get(this.AGREEMENT_TO_DATE)?.updateValueAndValidity({ emitEvent: false });
    }

    openAddAgreementDialogFromExpandedChild(): void {
        const child: any = this.expandedChild as any;
        const provision: any = child?.raw ?? child;

        if (this.isAddAgreementDisabledForSelfCare(provision)) {
            return;
        }

        const provisionId = Number(provision?.id);

        if (!Number.isFinite(provisionId) || provisionId <= 0) {
            (this.swalAutoClose ?? Swal).fire({
                text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-identificar-la-practica', undefined, 'No se pudo identificar la práctica para crear el acuerdo.'),
                icon: 'error',
            });
            return;
        }

        const caseId = Number(this.caseId ?? (this.case as any)?.id);
        if (!Number.isFinite(caseId) || caseId <= 0) {
            (this.swalAutoClose ?? Swal).fire({
                text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-identificar-el-caso-para-crear-el-acuerdo', undefined, 'No se pudo identificar el caso para crear el acuerdo.'),
                icon: 'error',
            });
            return;
        }

        this.isAddModeAgreement = true;
        this.isUpdateModeAgreement = false;
        this.actionAgreement = this.experienceI18n.translate('modules.cases.modules-scheme.crear', undefined, 'Crear');
        this.provisionToWork = provision as Provision;
        this.agreementToWork = null;
        this.title = this.experienceI18n.translate('modules.cases.modules-scheme.crear-acuerdo-prestacional', undefined, 'Crear acuerdo prestacional');
        this.agreementForm();
    }

    onClickConfirmFormAgreement() {
        if (!this.formAgreement) return;

        const formValues: any = { ...this.formAgreement.value };
        delete formValues.providerSearching;
        delete formValues.isFilteredByRegion;
        if (this.isSelfCareAgreementForm()) {
            formValues.providerId = null;
            formValues.providerFee = 0;
        }
        formValues.fromDate = this.formAgreement.get(this.AGREEMENT_FROM_DATE)?.value;
        if (!!this.isAddModeAgreement || (!this.isAddModeAgreement && !this.isUpdateModeAgreement)) {
            formValues.toDate = this.formAgreement.get(this.AGREEMENT_TO_DATE)?.value;
        }
        delete formValues.agreementFromDate;
        delete formValues.agreementToDate;

        if (!this.isSelfCareAgreementForm() && formValues.providerFee == null && this.agreementToWork) {
            const newCost = this.agreementToWork.providerFee + (this.agreementToWork.providerFee * (this.formAgreement.get(this.AGREEMENT_MARGIN)?.value / 100));
            formValues.providerFee = newCost;
        }
        delete formValues.marginFee;
        delete formValues.radioButton;

        const provisionId = Number((this.provisionToWork as any)?.id);
        const caseId = Number(this.caseId ?? (this.case as any)?.id);
        if (!Number.isFinite(provisionId) || !Number.isFinite(caseId)) return;

        if (this.isAddModeAgreement) {
            this.isLoadingCreatingAgreement = true;
            (this.provisionsFacade.addAgreement(formValues, provisionId, caseId) as Observable<any>)
            .pipe(
                finalize(() => {
                    this.isLoadingCreatingAgreement = false;
                })
            )
            .subscribe({
                next: () => {
                    (this.swalAutoClose ?? Swal).fire({
                        text: this.experienceI18n.translate('modules.cases.modules-scheme.el-acuerdo-prestacional-se-creo-correctamente', undefined, 'El acuerdo prestacional se creó correctamente'),
                        icon: 'success',
                    });
                    this._dialogRefAgreementTemplate?.close();
                    this.caseModulesChanged.emit();
                },
                error: () => { }
            });
        } else {
            delete formValues.providerId;

            const successMsg = () => {
                (this.swalAutoClose ?? Swal).fire({
                    text: this.experienceI18n.translate('modules.cases.modules-scheme.el-acuerdo-prestacional-se-actualizo-correctamente', undefined, 'El acuerdo prestacional se actualizó correctamente'),
                    icon: 'success',
                });
                this._dialogRefAgreementTemplate?.close();
                this.caseModulesChanged.emit();
            };

            formValues.force = false;
            if (!this.isUpdateModeAgreement) {
                formValues.edit = true;
                if (formValues.providerFee == this.agreementToWork?.providerFee || !this.formAgreement.get(this.PROVIDER_FEE)?.dirty) {
                    delete formValues.providerFee;
                }
            }

            this.isLoadingUpdatingAgreement = true;

            this.provisionsFacade.updateAgreement(
                formValues,
                caseId,
                provisionId,
                this.agreementToWork?.id
            ).subscribe({
                next: () => { successMsg(); },
                error: error => {
                    if (error.status === 418) {
                        let errorContent: HttpBaseResponseErrorTeaPot = error.error;
                        this.attentionsService.processErrorCountAttentions(errorContent, 'error').then(res => {
                            if (!res) {
                                // Nothing to do
                                this._dialogRef.close();
                            } else {
                                // User confirm force
                                formValues.force = true;
                                this.provisionsFacade.updateAgreement(formValues, caseId, provisionId, this.agreementToWork?.id).subscribe(
                                    () => successMsg()
                                )
                            }
                        })
                    } else {
                        const message = error?.error?.error?.message;
                        this.formAgreement.get(this.AGREEMENT_TO_DATE)?.setErrors({ required: { message } });
                        this.formAgreement.get(this.AGREEMENT_TO_DATE)?.markAsTouched();
                        this.isLoadingUpdatingAgreement = false;
                    }
                }
            })
        }
    }

    onClickDeleteAgreementForCaseModule(provision: Provision, agreement: any) {
        const caseId = Number(this.caseId ?? (this.case as any)?.id);
        const provisionId = Number((provision as any)?.id);
        const agreementId = Number((agreement as any)?.id);
        if (!Number.isFinite(caseId) || !Number.isFinite(provisionId) || !Number.isFinite(agreementId)) return;

        const deleteAgreement = (force: boolean = false) =>
            this.provisionsFacade.deleteAgreement(caseId, provisionId, agreementId, force).toPromise();

        this.swalWithCustomizeButtons.fire({
            text: this.experienceI18n.translate('modules.cases.modules-scheme.seguro-que-desea-eliminar-el-acuerdo-prestacional', undefined, '¿Seguro desea eliminar el acuerdo prestacional?'),
            icon: 'question',
            confirmButtonText: this.experienceI18n.translate('common.delete-variant', undefined, 'Eliminar'),
            cancelButtonText: this.experienceI18n.translate('common.cancel', undefined, 'Cancelar'),
            showLoaderOnConfirm: true,
            showCancelButton: true,
            reverseButtons: true,
            preConfirm: () => {
                return deleteAgreement()
                    .then(() => {
                        this.swalWithCustomizeButtons
                            .fire({
                                text: this.experienceI18n.translate('modules.cases.modules-scheme.acuerdo-prestacional-eliminado-correctamente', undefined, 'Acuerdo prestacional eliminado correctamente'),
                                icon: 'success',
                                timer: 2500,
                                showConfirmButton: false,
                            })
                            .then(() => this.caseModulesChanged.emit());
                    })
                    .catch(error => {
                        if (error.status === 418) {
                            let errorContent: HttpBaseResponseErrorTeaPot = error.error;
                            // WARNING! It's a promise, waiting the user
                            this.attentionsService.processErrorCountAttentions(errorContent, 'error').then(res => {
                                if (!res) {
                                    // Nothing to do
                                } else {
                                    // User confirm force
                                    deleteAgreement(true).then(() => {
                                        this.swalWithCustomizeButtons
                                            .fire({
                                                text: 'Acuerdo prestacional eliminado correctamente',
                                                icon: 'success',
                                                timer: 2500,
                                                showConfirmButton: false,
                                            })
                                            .then(() => this.caseModulesChanged.emit())
                                    })
                                }
                            })
                        }
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        });
    }

    addModuleForm(provision?: Provision, provisionFee?: ProvisionFee, selectedModuleRow?: CaseModuleComposed | null) {
        this.arrangementItemsByModule = null;
        this.showAlertMsg = false;

        this.practicesToShow = this.practices;
        this.formModule = this.createModuleForm(provision, provisionFee, selectedModuleRow);

        // // Adding without arrangement
        // if (!!provisionFee) {
        //     this.arrangementSelected = (!!provisionFee?.arrangementId) ? this.caseArrangements.find(arr => arr.id == provisionFee.arrangementId) : undefined;
        // }

        // Cuando cambian las fechas de validez, recalcular los arrangement items del módulo
        // this._moduleDatesSub?.unsubscribe();
        // const fromDateCtrl = this.formModule.get(this.VALIDITY_FROM_DATE);
        // const toDateCtrl = this.formModule.get(this.VALIDITY_TO_DATE);
        // if (!!fromDateCtrl && !!toDateCtrl) {
        //     this._moduleDatesSub = merge(fromDateCtrl.valueChanges, toDateCtrl.valueChanges)
        //         .subscribe(() => this.saveArrangementItemsByModule());
        // }

        // unsubscribe in the onDestroy()
        this._moduleDatesSub = merge(this.formModule.get(this.VALIDITY_FROM_DATE)?.valueChanges, this.formModule.get(this.VALIDITY_TO_DATE)?.valueChanges)
            .subscribe(() => this.saveArrangementItemsByModule());

        // No template-level [disabled] with reactive forms: drive it from the FormControls.
        this.setControlEnabled(this.formModule.get(this.MODULE), this.isAddMode);
        this.setControlEnabled(this.formModule.get(this.MODULE_SEARCHING), this.isAddMode);

        // Fuente de verdad para el pre-check en edición
        this.editingModuleRow = this.isEditMode ? (selectedModuleRow ?? null) : null;

        // En edición (agregar prácticas a un módulo ya creado en el caso),
        // dejamos seleccionado el módulo correspondiente.
        if (this.isEditMode && selectedModuleRow) {
            const moduleId = selectedModuleRow.moduleId;

            if (moduleId != null) {
                this.formModule.get(this.MODULE)?.patchValue(moduleId, { emitEvent: false });
                this.formModule.get(this.MODULE_SEARCHING)?.patchValue(selectedModuleRow.name, { emitEvent: false });

                // Reusa el flujo de selección para cargar items y sincronizar ids
                this.onSelectModule(moduleId);
            }
        }

        this.openDialog(this.title, this.moduleTemplate, { maxHeight: '95vh', minWidth: this.isEditMode ? '30%' : '90%' },
            () => {
                this.practiceSelected = null;
                this.formFrequency.reset();
                this.formModule.reset();
            }
        );
    }

    createAuthorizationGroup(provisionFee?: ProvisionFee) {
        if (!!provisionFee) {
            return this.formBuilder.group({
                [this.AUTHORIZATION_NUMBER]: ['',],
                [this.AUTHORIZATION_FROM_DATE]: [(!!provisionFee.fromDate) ? provisionFee.fromDate : '',],
                [this.AUTHORIZATION_TO_DATE]: [(!!provisionFee.toDate) ? provisionFee.toDate : '',],
                [this.AUTHORIZATION_OBSERVATIONS]: [''],
                [this.AUTHORIZATION_MEDIAURLS]: [''],
            });
        } else {
            return this.formBuilder.group({
                [this.AUTHORIZATION_NUMBER]: ['',],
                [this.AUTHORIZATION_FROM_DATE]: ['',],
                [this.AUTHORIZATION_TO_DATE]: ['',],
                [this.AUTHORIZATION_OBSERVATIONS]: ['']
            });
        }
    }

    // ===== Helpers: CaseModule form arrays =====
    private get practiceItemsFA(): FormArray {
        return (this.formModule?.get(this.PRACTICE_ITEMS) as FormArray) ?? this.formBuilder.array([]);
    }

    private get supplyItemsFA(): FormArray {
        return (this.formModule?.get(this.SUPPLY_ITEMS) as FormArray) ?? this.formBuilder.array([]);
    }

    private clearFormArray(fa: FormArray): void {
        if (!fa) return;
        while (fa.length) fa.removeAt(0);
    }

    private syncPracticeItemsToForm(items: ModulePracticeSelected[]): void {
        const fa = this.practiceItemsFA;
        this.clearFormArray(fa);

        for (const p of items ?? []) {
            fa.push(this.formBuilder.group({
                practiceId: [p.practiceId],
                code: [p.code ?? null],
                name: [p.name ?? null],
                frequencyAmount: [this.toFiniteOrNull(p.frequencyAmount)],
                frequencyUnit: [p.frequencyUnit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT],
                allowedHours: [this.toNumberOrNull(p.allowedHours)],
                restrictionRequiredTime: [p.restrictionRequiredTime],
                restrictionId: [this.toFiniteOrNull((p as any).restrictionId)],
                restrictionPatient: [!!(p as any).restrictionPatient],
                minAttentions: [this.toFiniteOrNull((p as any).minAttentions)],
                maxAttentions: [this.toFiniteOrNull((p as any).maxAttentions)],
            }));
        }

        fa.updateValueAndValidity({ emitEvent: false });
    }

    private syncSupplyItemsToForm(items: ModuleSupplySelected[]): void {
        const fa = this.supplyItemsFA;
        this.clearFormArray(fa);

        for (const s of items ?? []) {
            fa.push(this.formBuilder.group({
                practiceId: [s.practiceId],
                code: [s.code ?? null],
                name: [s.name ?? null],
                providerId: [this.toFiniteOrNull(s.providerId)],
                providerName: [s.providerName ?? null],
                quantity: [this.toFiniteOrNull(s.quantity)],
                unit: [s.unit ?? ProvisionsUnit.UNIDAD],
                unitCost: [this.toFiniteOrNull(s.unitCost)],
            }));
        }

        fa.updateValueAndValidity({ emitEvent: false });
    }

    private getPracticeItemsFromForm(): ModulePracticeSelected[] {
        const raw = (this.practiceItemsFA?.getRawValue?.() ?? []) as any[];
        return raw.map(r => ({
            practiceId: Number(r.practiceId),
            code: r.code ?? null,
            name: r.name ?? null,
            frequencyAmount: this.toFiniteOrNull(r.frequencyAmount),
            frequencyUnit: r.frequencyUnit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT,
            allowedHours: this.toNumberOrNull(r.allowedHours),
            restrictionId: this.toNumberOrNull(r.restrictionId),
            restrictionRequiredTime: r.restrictionRequiredTime,
            restrictionPatient: !!r.restrictionPatient,
            minAttentions: this.toFiniteOrNull(r.minAttentions),
            maxAttentions: this.toFiniteOrNull(r.maxAttentions),
        })) as any;
    }

    private isGuardiaLikeForPracticeId(practiceId: number): boolean {
        const practice =
            this.practices?.find((p) => p.id === practiceId) ??
            this.practicesToShow?.find((p) => p.id === practiceId);
        return isGuardiaLikeRestriction(practice?.restriction);
    }

    private isGuardiaLikePracticeItem(p: ModulePracticeSelected): boolean {
        if (p.restrictionRequiredTime != null) {
            return p.restrictionRequiredTime;
        }
        return this.isGuardiaLikeForPracticeId(Number(p.practiceId));
    }

    private rowRequiresAllowedHours(row: FormGroup): boolean {
        const flag = row.get('restrictionRequiredTime')?.value;
        if (flag != null && flag !== '') {
            return !!flag;
        }
        return this.isGuardiaLikeForPracticeId(Number(row.get('practiceId')?.value));
    }

    private getSupplyItemsFromForm(): ModuleSupplySelected[] {
        const raw = (this.supplyItemsFA?.getRawValue?.() ?? []) as any[];
        return raw.map(r => ({
            practiceId: Number(r.practiceId),
            code: r.code ?? null,
            name: r.name ?? null,
            providerId: this.toFiniteOrNull(r.providerId),
            provider: null,
            providerName: r.providerName ?? null,
            quantity: this.toFiniteOrNull(r.quantity),
            unit: r.unit ?? ProvisionsUnit.UNIDAD,
            unitCost: this.toFiniteOrNull(r.unitCost),
        })) as any;
    }

    private buildPracticesSelectedFromCaseModuleRow(cmRow: CaseModuleComposed): ModulePracticeSelected[] {
        const lockedMap = this.buildLockedPracticesFromCaseModuleRow(cmRow);
        // lockedMap values already have the shape we need for payload
        return Array.from(lockedMap.values() ?? []);
    }

    private buildCaseModuleFrequencyPayload(): CaseModuleFrequency | undefined {
        const fg = this.formModule?.get(this.MODULE_FREQUENCY) as FormGroup | null;
        if (!fg) return undefined;

        const amount = Number(fg.get(this.AMOUNT)?.value);
        const unit = fg.get(this.UNIT)?.value;

        if (!Number.isFinite(amount) || amount < 0) return undefined;
        if (unit == null || String(unit).trim() === '') return undefined;

        return {
            amount,
            unit: String(unit),
        };
    }

    private resolveModuleItemIdForSupply(practiceId: number): number | null {
        const items = this.moduleSelected?.moduleItems ?? [];
        const found = items.find(i => i.isSupply && i.practiceId === practiceId);
        const id = Number((found as any)?.id);
        return Number.isFinite(id) && id > 0 ? id : null;
    }

    private resolveModuleItemIdForPractice(practiceId: number): number | null {
        const items = this.moduleSelected?.moduleItems ?? [];
        const found = items.find(i => !i.isSupply && i.practiceId === practiceId);
        const id = Number((found as any)?.id);
        return Number.isFinite(id) && id > 0 ? id : null;
    }

    private buildPracticeItemsPayload(): any[] {
        return (this.formModule?.get?.(this.PRACTICE_ITEMS) ? this.getPracticeItemsFromForm() : (this.modulePracticesSelected ?? [])).map(p => {
            const moduleItemId = this.resolveModuleItemIdForPractice(p.practiceId);
            const frequency: any = {
                amount: Number(p.frequencyAmount) || 0,
                unit: (p.frequencyUnit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT),
            };
            if (this.isGuardiaLikePracticeItem(p)) {
                frequency.allowedHours = this.toFiniteOrNull(p.allowedHours);
            }
            return {
                moduleItemId,
                practiceId: p.practiceId,
                frequency,
            };
        });
    }

    private buildSupplyItemsPayload(fromDate: Date, toDate: Date): any[] {
        return (this.formModule?.get?.(this.SUPPLY_ITEMS) ? this.getSupplyItemsFromForm() : (this.moduleSuppliesSelected ?? [])).map(s => {
            const moduleItemId = this.resolveModuleItemIdForSupply(s.practiceId);

            return {
                moduleItemId,
                medicalSupply: {
                    provision: {
                        practiceId: s.practiceId,
                        frequency: {
                            amount: Number(s.quantity) || 0,
                            unit: String(s.unit ?? ProvisionsUnit.UNIDAD),
                        },
                        ...(s.unitCost != null ? { fee: Number(s.unitCost) || 0 } : {}),
                    },
                    // En backend el agreement puede ser null
                    agreement: (s.providerId != null && s.providerId > 0)
                        ? {
                            // No hay input específico de providerFee en el picker.
                            // Enviamos unitCost como fallback (si luego se agrega providerFee, se reemplaza acá).
                            providerFee: Number(s.unitCost) || 0,
                            providerId: s.providerId,
                            fromDate,
                            toDate,
                        }
                        : null,
                },
            };
        });
    }

    getColorAuthorizationState(provisionFee: ProvisionFee) {
        let state = this.generalService.getAuthorizationStatus(provisionFee.authorization);
        return this.AUTHORIZATION_COLORS[state]
    }

    getBackgroundColorAuthorizationState(provisionFee: ProvisionFee) {
        let state = this.generalService.getAuthorizationStatus(provisionFee.authorization);
        return this.AUTHORIZATION_BACKGROUND_COLORS[state]
    }

    // ===== Submit =====
    onSubmitModuleForm() {
        this.syncAuthorizationValidators();
        if (this.formModule.invalid || this.formAuthorization.invalid) {
            this.formModule.markAllAsTouched();
            this.formAuthorization.markAllAsTouched();
            return;
        }

        const coerceToDate = (value: any): Date | null => {
            if (!value) return null;
            if (value instanceof Date) return value;
            if (typeof value?.toDate === 'function') {
                try {
                    const d = value.toDate();
                    return d instanceof Date ? d : null;
                } catch {
                    return null;
                }
            }
            const parsed = new Date(value);
            return Number.isFinite(parsed.getTime()) ? parsed : null;
        };

        const raw = { ...this.formModule.getRawValue() } as any;

        const moduleId = raw?.[this.MODULE];
        if (!moduleId) return;

        const isAddingAuth = raw?.[this.IS_ADDING_AUTHORIZATION] === true;
        const authorization = isAddingAuth ? this.buildAuthorizationPayload() : null;

        const fromDate = coerceToDate(raw?.[this.VALIDITY_FROM_DATE]);
        const toDate = coerceToDate(raw?.[this.VALIDITY_TO_DATE]);
        if (!fromDate || !toDate) {
            (this.swalAutoClose ?? Swal).fire({
                text: this.experienceI18n.translate('modules.cases.modules-scheme.vigencia-invalida-revisa-fechas', undefined, 'Vigencia inválida. Revisá fechas desde/hasta.'),
                icon: 'error',
            });
            return;
        }

        if (this.arrangementSelected && !this.existPriceList) {
            (this.swalAutoClose ?? Swal).fire({
                text: 'No hay valores de venta definidos para el convenio en el rango de fechas especificado.',
                icon: 'error',
            });
            return;
        }

        if (this.arrangementSelected && this.hasMissingPriceRange) {
            (this.swalAutoClose ?? Swal).fire({
                text: 'El convenio no tiene precio de venta definido para todo el rango de fechas seleccionado.',
                icon: 'error',
            });
            return;
        }

        if (this.arrangementSelected && !this.existPriceList) {
            (this.swalAutoClose ?? Swal).fire({
                text: 'No hay valores de venta definidos para el convenio en el rango de fechas especificado.',
                icon: 'error',
            });
            return;
        }

        if (this.arrangementSelected && this.hasMissingPriceRange) {
            (this.swalAutoClose ?? Swal).fire({
                text: 'El convenio no tiene precio de venta definido para todo el rango de fechas seleccionado.',
                icon: 'error',
            });
            return;
        }

        // Validación: para cada insumo seleccionado necesitamos moduleItemId
        const currentSupplies = (this.formModule?.get?.(this.SUPPLY_ITEMS) ? this.getSupplyItemsFromForm() : (this.moduleSuppliesSelected ?? []));
        const missingModuleItemId = currentSupplies.find(s => this.resolveModuleItemIdForSupply(s.practiceId) == null);
        if (missingModuleItemId) {
            const label = missingModuleItemId.code ? `${missingModuleItemId.code} - ${missingModuleItemId.name}` : missingModuleItemId.name;
            (this.swalAutoClose ?? Swal).fire({
                text: `${this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-resolver-el-moduleitemid-del-insumo', undefined, 'No se pudo resolver el moduleItemId del insumo')}: ${label}.`,
                icon: 'error',
            });
            return;
        }

        // Validación: para cada práctica seleccionada necesitamos moduleItemId
        const currentPractices = (this.formModule?.get?.(this.PRACTICE_ITEMS) ? this.getPracticeItemsFromForm() : (this.modulePracticesSelected ?? []));
        const missingPracticeModuleItemId = currentPractices.find(p => this.resolveModuleItemIdForPractice(p.practiceId) == null);
        if (missingPracticeModuleItemId) {
            const label = missingPracticeModuleItemId.code
                ? `${missingPracticeModuleItemId.code} - ${missingPracticeModuleItemId.name}`
                : missingPracticeModuleItemId.name;
            (this.swalAutoClose ?? Swal).fire({
                text: `${this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-resolver-el-moduleitemid-de-la-practica', undefined, 'No se pudo resolver el moduleItemId de la práctica')}: ${label}.`,
                icon: 'error',
            });
            return;
        }

        const renewFromCaseModuleId = Number(this.caseModuleSelected?.id);
        if (this.isRePostMode && (!Number.isFinite(renewFromCaseModuleId) || renewFromCaseModuleId <= 0)) {
            (this.swalAutoClose ?? Swal).fire({
                text: this.experienceI18n.translate(
                    'modules.cases.modules-scheme.no-se-pudo-resolver-el-modulo-origen-de-la-renovacion',
                    undefined,
                    'No se pudo resolver el módulo origen de la renovación.',
                ),
                icon: 'error',
            });
            return;
        }

        const cmFrequency = this.buildCaseModuleFrequencyPayload();

        const postCaseModuleBody: CaseModulePostQPS = {
            fromDate,
            toDate,
            arrangementId: this.arrangementSelected ? this.arrangementSelected.id : null,
            fee: this.arrangementSelected ? undefined : (raw?.[this.FEE] != null ? Number(raw?.[this.FEE]) : undefined),
            frequency: {
                amount: cmFrequency?.amount,
                unit: cmFrequency?.unit,
            },
            authorizationId: null,
            authorization: authorization ?? undefined,
            practiceItems: this.buildPracticeItemsPayload() as any,
            supplyItems: this.buildSupplyItemsPayload(fromDate, toDate) as any,
            ...(this.isRePostMode ? { renewFromCaseModuleId } : {}),
        };

        if (this.isAddMode || this.isRePostMode) {
            const wasRePostMode = this.isRePostMode;
            this.modulesFacade.postCaseModule(postCaseModuleBody, this.caseId, moduleId).subscribe({
                next: (response: any) => {
                    this.caseModulesChanged.emit();
                    this.actionMessageSuccesAddUpdateProvisionFee(wasRePostMode ? this.experienceI18n.translate('modules.cases.modules-scheme.modulo-renovado-con-exito', undefined, 'Módulo renovado con éxito') : this.experienceI18n.translate('modules.cases.modules-scheme.modulo-agregado-con-exito', undefined, 'Módulo agregado con éxito'));
                    this.caseModuleSelected = null;
                    this.isRePostMode = false;
                    this.resetModuleSelectionState();
                },
                error: (error) => {
                    // NO reseteamos el estado para permitir reintentar con los datos del form intactos
                    const msg = error?.error?.message || (wasRePostMode ? this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-renovar-el-modulo', undefined, 'No se pudo renovar el módulo') : this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-agregar-el-modulo', undefined, 'No se pudo agregar el módulo'));
                    (this.swalAutoClose ?? Swal).fire({
                        text: msg,
                        icon: 'error',
                    });
                },
            });
            return;
        }

        if (this.isEditMode) {
            const patchBody: any = { ...postCaseModuleBody };

            // Criterio: si el form/items NO están dirty o son iguales a los actuales, no enviamos practiceItems/supplyItems.
            // (En backend, omitir el campo evita recalcular/reescribir ítems sin cambios.)
            const originalPractices = this.normalizePracticeItemsForComparison(
                this.buildPracticesSelectedFromCaseModuleRow(this.caseModuleSelected)
            );
            const currentPracticesNorm = this.normalizePracticeItemsForComparison(currentPractices);
            const practicesDirty = this.practiceItemsFA?.dirty === true;
            if (!practicesDirty || this.areComparableArraysEqual(originalPractices, currentPracticesNorm)) {
                delete patchBody.practiceItems;
            }

            const originalSupplies = this.normalizeSupplyItemsForComparison(
                this.buildSuppliesSelectedFromCaseModuleRow(this.caseModuleSelected)
            );
            const currentSuppliesNorm = this.normalizeSupplyItemsForComparison(currentSupplies);
            const suppliesDirty = this.supplyItemsFA?.dirty === true;
            if (!suppliesDirty || this.areComparableArraysEqual(originalSupplies, currentSuppliesNorm)) {
                delete patchBody.supplyItems;
            }

            this.modulesFacade.patchCaseModule(patchBody, this.caseId, this.caseModuleSelected.id).subscribe({
                next: (response: any) => {
                    this.caseModulesChanged.emit();
                    this.actionMessageSuccesAddUpdateProvisionFee(this.experienceI18n.translate('modules.cases.modules-scheme.modulo-corregido-con-exito', undefined, 'Módulo corregido con éxito'));
                    this.resetModuleSelectionState();
                },
                error: (error) => {
                    // NO reseteamos el estado para permitir reintentar con los datos del form intactos
                    const msg = error?.error?.message || this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-corregir-el-modulo', undefined, 'No se pudo corregir el módulo');
                    (this.swalAutoClose ?? Swal).fire({
                        text: msg,
                        icon: 'error',
                    });
                },
            });
        }
    }

    private normalizePracticeItemsForComparison(items: ModulePracticeSelected[]): Array<{ practiceId: number; amount: number; unit: string }> {
        return (items ?? [])
            .map((p) => ({
                practiceId: Number(p.practiceId),
                amount: Number((p as any)?.frequencyAmount) || 0,
                unit: String((p as any)?.frequencyUnit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT),
            }))
            .filter((x) => Number.isFinite(x.practiceId) && x.practiceId > 0)
            .sort((a, b) => a.practiceId - b.practiceId);
    }

    private normalizeSupplyItemsForComparison(items: ModuleSupplySelected[]): Array<{ practiceId: number; quantity: number; unit: string; unitCost: number | null; providerId: number | null }> {
        return (items ?? [])
            .map((s) => ({
                practiceId: Number(s.practiceId),
                quantity: Number((s as any)?.quantity) || 0,
                unit: String((s as any)?.unit ?? ProvisionsUnit.UNIDAD),
                unitCost: (s as any)?.unitCost == null ? null : (Number((s as any)?.unitCost) || 0),
                providerId: (s as any)?.providerId == null ? null : (Number((s as any)?.providerId) || null),
            }))
            .filter((x) => Number.isFinite(x.practiceId) && x.practiceId > 0)
            .sort((a, b) => a.practiceId - b.practiceId);
    }

    private areComparableArraysEqual(a: unknown[], b: unknown[]): boolean {
        // Igualdad “estable” sin depender de referencias.
        return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
    }

    private syncAuthorizationValidators(): void {
        const isAddingAuth = this.formModule?.get(this.IS_ADDING_AUTHORIZATION)?.value === true;

        const numberCtrl = this.formAuthorization?.get(this.AUTHORIZATION_NUMBER);

        if (isAddingAuth) {
            numberCtrl?.setValidators([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.numero-de-autorizacion-requerido', undefined, 'Número de autorización requerido'))]);
        } else {
            numberCtrl?.clearValidators();
        }

        numberCtrl?.updateValueAndValidity({ emitEvent: false });
        this.formAuthorization?.updateValueAndValidity({ emitEvent: false });
    }

    buildAuthorizationPayload(): Authorization | null {
        const mediaUrls: string[] = [];
        // 1) Si el checkbox de "Autorizar" no está tildado, no mandamos nada
        const isAddingAuth = this.formModule.get(this.IS_ADDING_AUTHORIZATION)?.value;
        if (!isAddingAuth) {
            return null;
        }

        const toTrimmedString = (value: any): string => {
            if (value === null || value === undefined) return '';
            return String(value).trim();
        };

        const coerceToDate = (value: any): Date | null => {
            if (!value) return null;
            if (value instanceof Date) return value;
            if (typeof value?.toDate === 'function') {
                try {
                    const d = value.toDate();
                    return d instanceof Date ? d : null;
                } catch {
                    return null;
                }
            }
            const parsed = new Date(value);
            return Number.isFinite(parsed.getTime()) ? parsed : null;
        };
        const authNumber: string = toTrimmedString(
            this.formAuthorization.get(this.AUTHORIZATION_NUMBER)?.value
        );

        if (!authNumber) {
            return null;
        }

        const observations: string = toTrimmedString(
            this.formAuthorization.get(this.AUTHORIZATION_OBSERVATIONS)?.value
        );
        const fromDate: Date | null = coerceToDate(
            this.formModule.get(this.VALIDITY_FROM_DATE)?.value
        );

        const toDate: Date | null = coerceToDate(
            this.formModule.get(this.VALIDITY_TO_DATE)?.value
        );

        if (!fromDate || !toDate) {
            return null;
        }

        if (this.photoEvolutionB64 && this.photoEvolutionB64.length > 0) {
            mediaUrls.push(this.photoEvolutionB64)
        }

        const authorization: any = {
            authorizationNumber: authNumber,
            fromDate,
            toDate,
            observations,
            mediaUrls: mediaUrls.length > 0 ? mediaUrls : null,
        };

        return authorization as Authorization;
    }

    toogleRetroactive(event: boolean = false) {
        this.isRetroactive = event;
        this.formModule.get(this.VALIDITY_FROM_DATE).clearValidators();
        this.formModule.get(this.VALIDITY_TO_DATE).clearValidators();

        // fromdate <= todate
        this.formModule.setValidators(CustomValidators.fromToDate('fromDate', 'toDate'));

        if (!this.isRetroactive) {
            // Retroactive OFF
            const startOfMonth = moment(this.formModule.get(this.VALIDITY_FROM_DATE).value).startOf('month').format('YYYY-MM-DD');
            const endOfMonth = moment(this.formModule.get(this.VALIDITY_FROM_DATE).value).endOf('month').format('YYYY-MM-DD');

            if (startOfMonth == moment(this.formModule.get(this.VALIDITY_FROM_DATE).value).format('YYYY-MM-DD') && endOfMonth == moment(this.formModule.get(this.VALIDITY_TO_DATE).value).format('YYYY-MM-DD')) {

                const startOfNextMonth = moment(startOfMonth).add(1, 'M');
                const endOfNextMonth = moment(startOfNextMonth).endOf('month').format('YYYY-MM-DD');

                this.formModule.get(this.VALIDITY_FROM_DATE).setValue(startOfNextMonth);
                this.formModule.get(this.VALIDITY_TO_DATE).setValue(endOfNextMonth);
            } else {

                let diff = moment.duration(moment(this.provisionFeeToWork.toDate).diff(this.provisionFeeToWork.fromDate))
                diff = diff.asDays() > 0 ? diff : diff.add(1, 'day');
                this.formModule.get(this.VALIDITY_FROM_DATE).setValue(moment(this.provisionFeeToWork.toDate).add(1, 'day'));
                this.formModule.get(this.VALIDITY_TO_DATE).setValue(moment(this.provisionFeeToWork.toDate).add(diff));
            }

            this.formModule.get(this.VALIDITY_FROM_DATE).setValidators(Validators.compose([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida')), CustomValidators.dateGreaterThan(this.provisionFeeToWork.toDate)]))
            this.formModule.get(this.VALIDITY_TO_DATE).setValidators(Validators.compose([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida')), CustomValidators.dateGreaterThan(this.formModule.controls['fromDate'].value)]))

        } else {
            // Retroactive ON
            this.formModule.get(this.VALIDITY_FROM_DATE).setValidators(Validators.compose([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida')),
            CustomValidators.dateGreaterThan(moment(this.provisionFeeToWork.fromDate).toDate()),
            CustomValidators.dateLowerThan(moment(this.provisionFeeToWork.toDate).toDate())]));

            this.formModule.get(this.VALIDITY_FROM_DATE).setValue(moment(this.provisionFeeToWork.fromDate).add(1, 'day'));
            this.formModule.get(this.VALIDITY_TO_DATE).setValue(this.provisionFeeToWork.toDate);
            this.formModule.get(this.VALIDITY_TO_DATE).setValidators(Validators.compose([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida')),
            CustomValidators.dateGreaterThan(moment(this.provisionFeeToWork.toDate).subtract(1, 'day').toDate())]))
        }
    }

    actionMessageSuccesAddUpdateProvisionFee(msg: string) {
        const swal = this.swalAutoClose ?? this.swalWithCustomizeButtons ?? Swal;
        swal.fire({
            text: msg,
            icon: 'success'
        });
        this._dialogRef.close();
    }

    createFrequencyGroup(provision?: Provision | ProvisionFee) {

            const initialUnit =
            provision?.frequency?.unit ?? this.PROVISION_FREQUENCY_UNIT_DEFAULT;

            return this.formBuilder.group({
                [this.AMOUNT]: [provision && provision.frequency && provision.frequency.amount ? provision.frequency.amount : '', [
                    CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.requerida', undefined, 'Requerida')),
                    CustomValidators.minValue(1, this.experienceI18n.translate('modules.cases.modules-scheme.debe-ser-un-valor-positivo', undefined, 'Debe ser un valor positivo'))
                ]
            ],
            [this.UNIT]: [initialUnit, [CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.periodo-requerido', undefined, 'Período requerido'))]], //  Se setea síncronamente
            [this.HOURS]: [provision && provision.frequency && provision.frequency.allowedHours ? provision.frequency.allowedHours : '']
        });
    }

    onClickCloseDialog() {
        this.photoEvolutionB64 = null;
        this.loadingAddFile = false;
        this.isAddingAuthorization = false;
        this.caseModuleToAuthorize = null;
        this._rePostFromDateSub?.unsubscribe();
        this._rePostFromDateSub = undefined;
        try {
            (this.myPond as any)?.removeFiles?.();
        } catch {
            // noop
        }

        try {
            (this.authPond as any)?.removeFiles?.();
        } catch {
            // noop
        }

        this._dialogRef?.close();

        this.practiceSelected = null;
        this.formFrequency?.reset();
        this.formAuthorization?.reset();
        this.formModule?.reset();
        this.formAmendPractice?.reset();
        this.isRePostMode = false;
        this.resetModuleSelectionState();
    }

    // ===== PRACTICES: Finalizar / Corregir (replica provisions-detail) =====
    private resolveCaseIdOrFail(): number | null {
        const caseId = Number(this.caseId ?? (this.case as any)?.id);
        return Number.isFinite(caseId) && caseId > 0 ? caseId : null;
    }

    openSuspendPracticeProvisionFee(provision: Provision): void {
        const provisionFee = this.resolveCurrentProvisionFee(provision);
        const caseId = this.resolveCaseIdOrFail();

        if (!caseId) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-identificar-el-caso', undefined, 'No se pudo identificar el caso.'), icon: 'error' });
            return;
        }
        if (!provisionFee) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-identificar-el-esquema-prestacional', undefined, 'No se pudo identificar el esquema prestacional de la práctica.'), icon: 'error' });
            return;
        }

        this.provisionToWork = provision;
        this.provisionFeeToWork = provisionFee;

        this.formSuspend = this.createSuspendPracticeForm(provision, provisionFee);
        this.title = this.experienceI18n.translate('modules.cases.modules-scheme.finalizar-practica', undefined, 'Finalizar práctica');

        this.openDialog(this.title, this.suspendPracticeTemplate, { maxHeight: '95vh', minWidth: '60%' });
    }

    private createSuspendPracticeForm(provision: Provision, provisionFee: ProvisionFee): FormGroup {
        return this.formBuilder.group({
            [this.PRACTICE_NAME]: [{ value: provision?.practice?.name ?? '-', disabled: true }],
            [this.VALIDITY_FROM_DATE]: [
                !!provisionFee?.fromDate ? provisionFee.fromDate : '',
                Validators.compose([
                    CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-requerida', undefined, 'Fecha requerida')),
                    // mismos validadores que provisions-detail
                    CustomValidators.dateLowerThan(provisionFee.toDate),
                    CustomValidators.dateGreaterThan(provisionFee.fromDate),
                ]),
            ],
            [this.VALIDITY_TO_DATE]: [{ value: provisionFee?.toDate ?? null, disabled: true }],
        });
    }

    private get suspendPracticeFromDate(): Date {
        return this.formSuspend?.get(this.VALIDITY_FROM_DATE)?.value;
    }

    onClickSuspendPracticeProvisionFee(): void {
        if (!this.formSuspend?.valid || !this.provisionFeeToWork) return;

        const caseId = this.resolveCaseIdOrFail();
        if (!caseId) return;

        const provisionId = Number(this.provisionFeeToWork.provisionId ?? (this.provisionToWork as any)?.id);

        const newRange: any = {
            fromDate: this.provisionFeeToWork.fromDate,
            toDate: moment(this.suspendPracticeFromDate).subtract(1, 'days').toDate(),
        };

        this.executeSuspendPatch(newRange, caseId, provisionId);
    }

    private executeSuspendPatch(patchBody: any, caseId: number, provisionId: number): void {
        this.provisionsFacade.updateProvisionFeePatch(patchBody, caseId, provisionId, this.provisionFeeToWork.id).subscribe({
            next: () => {
                (this.swalAutoClose ?? Swal)
                    .fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.la-operacion-fue-realizada-con-exito', undefined, 'La operación fue realizada con éxito'), icon: 'success' })
                this._dialogRef?.close();
                this.caseModulesChanged.emit();
            },
            error: (error) => {
                if (error?.status === 418) {
                    const errorContent: HttpBaseResponseErrorTeaPot = error.error;
                    this.attentionsService.processErrorCountAttentions(errorContent, 'error').then((confirmed) => {
                        if (confirmed) {
                            this.executeSuspendPatch({ ...patchBody, force: true }, caseId, provisionId);
                        }
                    });
                }
            },
        });
    }

    amendPracticeProvisionFee(provision: Provision): void {
        const provisionFee = this.resolveCurrentProvisionFee(provision);
        if (!provisionFee) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-identificar-el-esquema-prestacional', undefined, 'No se pudo identificar el esquema prestacional de la práctica.'), icon: 'error' });
            return;
        }

        (this.swalWithCustomizeButtons ?? Swal)
            .fire({
                text: this.experienceI18n.translate('modules.cases.modules-scheme.corregir-la-practica-modifica-los-datos-que-corresponden-a-la-autorizacion-actual', undefined, 'Corregir la práctica modifica los datos que corresponden a la autorización actual.'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.experienceI18n.translate('common.accept', undefined, 'Aceptar'),
                cancelButtonText: this.experienceI18n.translate('common.cancel', undefined, 'Cancelar'),
                reverseButtons: true,
            })
            .then((result) => {
                if (result.isConfirmed) {
                    this.openAmendPracticeDialog(provision, provisionFee);
                }
            });
    }

    private openAmendPracticeDialog(provision: Provision, provisionFee: ProvisionFee): void {
        this.provisionToWork = provision;
        this.provisionFeeToWork = provisionFee;

        this.checkboxAuthorizationText = !!provisionFee?.authorization ? this.experienceI18n.translate('modules.cases.modules-scheme.reemplazar-autorizacion', undefined, 'Reemplazar autorización') : this.experienceI18n.translate('modules.cases.modules-scheme.agregar-autorizacion', undefined, 'Agregar autorización');
        this.isShowAuth = true;
        this.photoEvolutionB64 = null;
        this.isAddingAuthorization = false;

        // Frecuencia (se edita en formFrequency)
        this.formFrequency = this.createFrequencyGroup(provisionFee);
        this.hoursControl();

        // Authorization form (solo si se tilda)
        this.formAuthorization = this.createAuthorizationGroup(provisionFee);

        this.formAmendPractice = this.formBuilder.group({
            [this.VALIDITY_FROM_DATE]: [{ value: provisionFee?.fromDate ?? this.toDay, disabled: true }],
            [this.VALIDITY_TO_DATE]: [{ value: provisionFee?.toDate ?? null, disabled: true }],
            [this.IS_ADDING_AUTHORIZATION]: [{ value: false, disabled: false }],
        });

        this.title = this.experienceI18n.translate('modules.cases.modules-scheme.corregir-practica-del-modulo', undefined, 'Corregir práctica del módulo');
        this.openDialog(this.title, this.amendPracticeTemplate, { maxHeight: '95vh', minWidth: '40%' });
    }

    onClickConfirmAmendPracticeProvisionFee(): void {
        if (!this.formAmendPractice || !this.provisionToWork || !this.provisionFeeToWork) return;
        if (this.formAmendPractice.invalid || this.formFrequency?.invalid) {
            this.formAmendPractice.markAllAsTouched();
            this.formFrequency?.markAllAsTouched();
            this.formAuthorization?.markAllAsTouched();
            return;
        }

        const caseId = this.resolveCaseIdOrFail();
        if (!caseId) return;

        const isAddingAuth = this.formAmendPractice.get(this.IS_ADDING_AUTHORIZATION)?.value === true;
        if (isAddingAuth) {
            // Reglas mínimas para replicar provisions-detail
            this.formAuthorization?.get(this.AUTHORIZATION_NUMBER)?.setValidators([
                CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.numero-de-autorizacion-requerido', undefined, 'Número de autorización requerido')),
            ]);
            this.formAuthorization?.get(this.AUTHORIZATION_NUMBER)?.updateValueAndValidity({ emitEvent: false });

            if (this.formAuthorization?.invalid || !this.photoEvolutionB64) {
                this.formAuthorization?.markAllAsTouched();
                return;
            }
        }

        let formValues: any = {
            ...this.formAmendPractice.getRawValue(),
        };

        // frequency solo si cambió
        const freqDirty =
            !!this.formFrequency?.get(this.UNIT)?.dirty ||
            !!this.formFrequency?.get(this.AMOUNT)?.dirty ||
            !!this.formFrequency?.get(this.HOURS)?.dirty;
        if (freqDirty) {
            formValues.frequency = { ...this.formFrequency.value };
        }

        // fee solo si cambió
        if (!this.formAmendPractice.get(this.FEE)?.dirty) {
            delete formValues.fee;
        }

        // Authorization
        if (!isAddingAuth) {
            // no authorization
        } else {
            const authPayload: any = {
                authorizationNumber: String(this.formAuthorization.get(this.AUTHORIZATION_NUMBER)?.value ?? '').trim(),
                observations: String(this.formAuthorization.get(this.AUTHORIZATION_OBSERVATIONS)?.value ?? '').trim(),
                mediaUrls: [this.photoEvolutionB64],
                fromDate: this.formAmendPractice.get(this.VALIDITY_FROM_DATE)?.value,
                toDate: this.formAmendPractice.get(this.VALIDITY_TO_DATE)?.value,
            };
            formValues.authorization = authPayload;
        }

        delete formValues[this.IS_ADDING_AUTHORIZATION];
        formValues.force = false;

        const provisionId = Number((this.provisionToWork as any)?.id);
        this.provisionsFacade.updateProvisionFeePatch(formValues, caseId, provisionId, this.provisionFeeToWork.id).subscribe({
            next: () => {
                (this.swalWithCustomizeButtons ?? Swal)
                    .fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.el-esquema-prestacional-se-actualizo-correctamente', undefined, 'El esquema prestacional se actualizó correctamente'), icon: 'success' })
                    .then(() => {
                        this._dialogRef?.close();
                        this.caseModulesChanged.emit();
                    });
            },
            error: (error) => {
                if (error?.status === 418) {
                    let errorContent: HttpBaseResponseErrorTeaPot = error.error;
                    this.attentionsService.processErrorCountAttentions(errorContent).then((res) => {
                        if (!res) return;
                        const forced = { ...formValues, force: true };
                        this.provisionsFacade.updateProvisionFeePatch(forced, caseId, provisionId, this.provisionFeeToWork.id).subscribe({
                            next: () => {
                                (this.swalWithCustomizeButtons ?? Swal)
                                    .fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.el-esquema-prestacional-se-actualizo-correctamente', undefined, 'El esquema prestacional se actualizó correctamente'), icon: 'success' })
                                    .then(() => {
                                        this._dialogRef?.close();
                                        this.caseModulesChanged.emit();
                                    });
                            },
                        });
                    });
                }
            },
        });
    }

    hoursControl() {
        this.formFrequency.get(this.HOURS).clearValidators();
        if (this.isGuard()) {
            this.formFrequency.get(this.HOURS).setValidators([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.cantidad-requerida', undefined, 'Cantidad requerida'))]);
        }
    }
    isGuard(): boolean {
        if (!!this.practiceSelected && this.nomenclatorService.isGuard(this.practiceSelected) ||
            !!this.provisionToWork && this.nomenclatorService.isGuard(this.provisionToWork.practice)) {
            return true;
        } else {
            return false;
        }
    }

    radioButtonArrangementSelected(id) {

        if (!!id && id.value != 'S/C') {
            this.arrangementSelected = this.caseArrangements.find(arr => arr.id == id.value);
            if (!!this.arrangementSelected) {
                const modulesIds = [
                    ...new Set(this.arrangementSelected.moduleArrangements.map(m => m.moduleId))
                ];
                let qps: modulesGetQPS = {
                    active: true,
                    financierId: this.financierId,
                    ids: modulesIds,
                    composeItems: true,
                };
                this.modulesService.getModules(qps).subscribe(modules => {
                    this.modulesFilteredByFinancier = modules.data;
                });
            }
            this.showTxtWithOutArrangement = true;
            this.formModule.get(this.FEE).clearValidators();
            this.formModule.get(this.FEE).setValue('');
            this.formModule.get(this.FEE).updateValueAndValidity();
            this.saveArrangementItemsByModule();
        } else {
            this.getModulesFilteredByFinancier();
            this.arrangementSelected = undefined;
            this.showTxtWithOutArrangement = false;
            this.arrangementItemsByModule = null;
            this.existPriceList = false;
            this.formModule.get(this.FEE).setValidators([CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.valor-venta-requerido', undefined, 'Valor venta requerido'))]);
            this.formModule.get(this.FEE).updateValueAndValidity();
        }
    }

    onSelectModule(event: number) {
        this.practicesSelection.clear();
        this.suppliesSelection.clear();
        this.modulePracticesSelected = [];
        this.moduleSuppliesSelected = [];

        const moduleSelected = this.modulesFilteredByFinancier?.find(m => m.id == event);
        if (!moduleSelected) {
            this.moduleSelected = null;
            return;
        }

        this.moduleSelected = moduleSelected;

        const items = this.moduleSelected?.moduleItems ?? [];
        this.moduleSelectedHasSupplies = items.some(i => i.isSupply);
        this.moduleSelectedHasPractices = items.some(i => !i.isSupply);

        const practiceIds = items
            .filter(item => !item.isSupply)
            .map(item => Number(item.practiceId))
            .filter(practiceId => Number.isFinite(practiceId) && practiceId > 0);

        if (practiceIds.length) {
            this.nomenclatorService.getPractices({ ids: practiceIds, active: true })
                .pipe(take(1))
                .subscribe(response => {
                    if (this.moduleSelected?.id !== moduleSelected.id) return;

                    this.practices = response?.data ?? [];
                    this.practicesToShow = this.practices;
                });
        } else {
            this.practices = [];
            this.practicesToShow = [];
        }

        if (this.isEditMode && this.editingModuleRow) {
            const checkedPracticeIds = this.getCheckedPracticeIdsFromModuleRow(this.editingModuleRow);
            const checkedSupplyIds = this.getCheckedSupplyIdsFromModuleRow(this.editingModuleRow);

            const selectedPractices = items.filter(i => !i.isSupply && checkedPracticeIds.has(i.practiceId));
            const selectedSupplies = items.filter(i => i.isSupply && checkedSupplyIds.has(i.practiceId));

            this.practicesSelection.select(...selectedPractices);
            this.suppliesSelection.select(...selectedSupplies);
            this.hydrateSelectedListsFromEditingRow(this.editingModuleRow);
        }
        this.saveArrangementItemsByModule();
    }

    saveArrangementItemsByModule() {
        if (!this.moduleSelected || !this.arrangementSelected) {
            this.arrangementItemsByModule = null;
            this.existPriceList = false;
            return;
        }
        this.arrangementItemsByModule = this.getArrangementItemsByModuleValids(this.arrangementSelected?.id, this.moduleSelected?.id, this.formModule.get(this.VALIDITY_FROM_DATE)?.value, this.formModule.get(this.VALIDITY_TO_DATE)?.value);
    }

    // ===== File upload (FilePond) =====
    pondHandleInit() {
        // console.log('Inicio la carga');
    }

    pondHandleAddFile(event: any) {
        if (!event.error) {
            const b64 = event.file.getFileEncodeBase64String();
            const extension = event.file.fileExtension;
            const mimeType = mime.getType(extension);
            this.photoEvolutionB64 = `data:${mimeType};base64,${b64}`;
            console.log('file added', this.photoEvolutionB64);

        } else {
            this.photoEvolutionB64 = null;
        }
        this.loadingAddFile = false;
    }

    pondHandleRemoveFile(event) {
        this.photoEvolutionB64 = null;
    }

    pondHandleProcessFile(event) {
        this.loadingAddFile = true;
    }



    calculateIdModule(element: Module) {
        return element ? element.id : null;
    }

    calculateNameModule(element: Module) {
        return element ? element.name : null;
    }

    calculateIdUnit(element) {
        return element ? element.id : null;
    }

    calculateNameUnit(element) {
        return element ? element.name : null;
    }

    togglePracticesPanel(): void {
        this.practicesPanelExpanded = !this.practicesPanelExpanded;
    }

    toggleSuppliesPanel(): void {
        this.suppliesPanelExpanded = !this.suppliesPanelExpanded;
    }

    //Selection model Helpers
    getPracticeItems(): ModuleItems[] {
        return (this.moduleSelected?.moduleItems ?? []).filter(i => !i.isSupply);
    }

    getSupplyItems(): ModuleItems[] {
        return (this.moduleSelected?.moduleItems ?? []).filter(i => i.isSupply);
    }

    // ===== Practices selection =====
    isAllPracticesSelected(): boolean {
        const items = this.getPracticeItems();
        if (items.length === 0) return false;
        return items.every(i => this.practicesSelection.isSelected(i));
    }

    isSomePracticesSelected(): boolean {
        const items = this.getPracticeItems();
        const selectedCount = items.filter(i => this.practicesSelection.isSelected(i)).length;
        return selectedCount > 0 && selectedCount < items.length;
    }

    toggleAllPractices(checked: boolean): void {
        const items = this.getPracticeItems();
        if (checked) this.practicesSelection.select(...items);
        else this.practicesSelection.deselect(...items);
    }

    togglePracticeItem(item: ModuleItems, checked: boolean): void {
        if (checked) this.practicesSelection.select(item);
        else this.practicesSelection.deselect(item);
    }

    // ===== Supplies selection =====
    isAllSuppliesSelected(): boolean {
        const items = this.getSupplyItems();
        if (items.length === 0) return false;
        return items.every(i => this.suppliesSelection.isSelected(i));
    }

    isSomeSuppliesSelected(): boolean {
        const items = this.getSupplyItems();
        const selectedCount = items.filter(i => this.suppliesSelection.isSelected(i)).length;
        return selectedCount > 0 && selectedCount < items.length;
    }

    toggleAllSupplies(checked: boolean): void {
        const items = this.getSupplyItems();
        if (checked) this.suppliesSelection.select(...items);
        else this.suppliesSelection.deselect(...items);
    }

    toggleSupplyItem(item: ModuleItems, checked: boolean): void {
        if (checked) this.suppliesSelection.select(item);
        else this.suppliesSelection.deselect(item);
    }

    getSupplyTotalFee(r: Provision): number | null {
        if (!r?.provisionFeeId) return null;
        let provisionFeeId = r.provisionFeeId;
        let agreement = r.agreements.filter(a => a.provisionFeeId == provisionFeeId)
        const freq = this.resolveDisplayFrequencyFromProvision(r) ?? ({} as any);
        const amount = (freq as any)?.amount;
        return agreement && agreement.length > 0 ? agreement[0].providerFee * (amount ?? 1) : null
    }


    resetModuleSelectionState(): void {
        this.moduleSelected = null;
        this.editingModuleRow = null;
        this.arrangementSelected = undefined;
        this.isRePostMode = false;

        // flags
        this.moduleSelectedHasPractices = false;
        this.moduleSelectedHasSupplies = false;

        // panels
        this.practicesPanelExpanded = true;
        this.suppliesPanelExpanded = true;

        // selections
        this.practicesSelection.clear();
        this.suppliesSelection.clear();

        this._rePostFromDateSub?.unsubscribe();
        this._rePostFromDateSub = undefined;

        // opcional: si cacheás datasources por módulo
        this.clearChildCache();
    }

    setOrderComposeWithProvisionFee() {
        this.orders?.forEach(or => or?.items?.forEach(it => {
            let provFeeForItem = this.provisionFees.find(provFee => provFee.id == it.provisionFeeId);
            if (provFeeForItem != null) {
                it.provisionFee = provFeeForItem;
            }
        }))
    }

    // ===== PEDIDOS =====
    private createOrderErrorSupplyKeys = new Set<string>();

    private getSelectedSuppliesForCreateOrder(): Provision[] {
        return [...this.selectionSupplies.selected];
    }

    getSelectedSuppliesForCreateOrderCount(): number {
        return this.getSelectedSuppliesForCreateOrder().length;
    }

    createOrderButtonDisabled(): boolean {
        return this.getCreateOrderDisabledReasons().length > 0;
    }

    getCreateOrderDisabledReasons(): string[] {
        const reasons: string[] = [];

        if (!this.case && !this.caseId) {
            reasons.push(this.experienceI18n.translate('modules.cases.modules-scheme.no-hay-caso-seleccionado', undefined, 'No hay caso seleccionado.'));
        }

        if (this.case && this.case.active === false) {
            reasons.push(this.experienceI18n.translate('modules.cases.modules-scheme.el-caso-esta-finalizado-inactivo', undefined, 'El caso está finalizado/inactivo.'));
        }

        const selectedSupplies = this.getSelectedSuppliesForCreateOrder();
        // if (selectedSupplies.length === 0) {
        //     // reasons.push('Seleccione al menos un insumo.');
        //     return reasons;
        // }

        const providerIds = selectedSupplies
            .map((p: any) => p?.agreements?.[0]?.provider?.id)
            .filter((id) => Number.isFinite(Number(id)))
            .map((id) => Number(id));

        const uniqueProviderIds = new Set(providerIds);
        if (uniqueProviderIds.size > 1) {
            reasons.push(this.experienceI18n.translate('modules.cases.modules-scheme.los-insumos-seleccionados-tienen-distinto-proveedor', undefined, 'Los insumos seleccionados tienen distinto proveedor.'));
        }

        const suppliesWithEmptyProvider = selectedSupplies.filter((p) => {
            const anyP: any = p as any;
            const providerId = anyP?.agreements?.[0]?.provider?.id;
            return !Number.isFinite(Number(providerId));
        }).length;
        if (suppliesWithEmptyProvider > 0) {
            reasons.push(`${this.experienceI18n.translate('modules.cases.modules-scheme.existe-n', undefined, 'Existe/n')} ${suppliesWithEmptyProvider} ${this.experienceI18n.translate('modules.cases.modules-scheme.insumo-s-con-proveedor-vacio', undefined, 'insumo/s con proveedor vacío.')}`);
        }

        const suppliesWithInactiveProvider = selectedSupplies.filter(p => this.supplyHasInactiveProvider(p)).length;
        if (suppliesWithInactiveProvider > 0) {
            reasons.push(`${this.experienceI18n.translate('modules.cases.modules-scheme.existe-n', undefined, 'Existe/n')} ${suppliesWithInactiveProvider} ${this.experienceI18n.translate('modules.cases.modules-scheme.insumo-s-con-proveedor-inactivo', undefined, 'insumo/s con proveedor inactivo.')}`);
        }

        const suppliesWithoutCost = selectedSupplies.filter((p) => {
            const anyP: any = p as any;
            const providerFee = anyP?.agreements?.[0]?.providerFee;
            const fee = anyP?.fee;
            const fixedPrice = anyP?.fixedPrice;
            // Si tiene fixedPrice (Kairos), no requiere costo
            return (providerFee == null && fee == null) && !fixedPrice;
        }).length;
        if (suppliesWithoutCost > 0) {
            reasons.push(`${this.experienceI18n.translate('modules.cases.modules-scheme.existe-n', undefined, 'Existe/n')} ${suppliesWithoutCost} ${this.experienceI18n.translate('modules.cases.modules-scheme.insumo-s-sin-costo', undefined, 'insumo/s sin costo.')}`);
        }

        // Validación: insumos con pedido activo (no cancelado)
        const suppliesAlreadyOrdered = selectedSupplies.filter((p) => this.supplyHasActiveOrder(p)).length;
        if (suppliesAlreadyOrdered > 0) {
            reasons.push(`${this.experienceI18n.translate('modules.cases.modules-scheme.existe-n', undefined, 'Existe/n')} ${suppliesAlreadyOrdered} ${this.experienceI18n.translate('modules.cases.modules-scheme.insumo-s-con-pedido-activo', undefined, 'insumo/s con pedido activo.')}`);
        }

        // Validación: insumos sin fecha de finalización (toDate)
        const suppliesWithoutToDate = selectedSupplies.filter((p) => {
            const anyP: any = p as any;
            const toDate = anyP?.agreements?.[0]?.toDate ?? anyP?.toDate;
            return toDate == null;
        }).length;
        if (suppliesWithoutToDate > 0) {
            reasons.push(`${this.experienceI18n.translate('modules.cases.modules-scheme.existe-n', undefined, 'Existe/n')} ${suppliesWithoutToDate} ${this.experienceI18n.translate('modules.cases.modules-scheme.insumo-s-sin-fecha-de-finalizacion', undefined, 'insumo/s sin fecha de finalización.')}`);
        }

        // Validación: insumos con pedido en otro mes
        const suppliesWithOrderInAnotherPeriod = this.countSuppliesWithOrderInAnotherTimePeriod(selectedSupplies);
        if (suppliesWithOrderInAnotherPeriod > 0) {
            reasons.push(`${this.experienceI18n.translate('modules.cases.modules-scheme.existe-n', undefined, 'Existe/n')} ${suppliesWithOrderInAnotherPeriod} ${this.experienceI18n.translate('modules.cases.modules-scheme.insumo-s-con-pedido-en-otro-mes', undefined, 'insumo/s con pedido en otro mes.')}`);
        }

        return reasons;
    }

    rowsWithErrorsForCreateOrder(): void {
        this.createOrderErrorSupplyKeys.clear();

        const selectedSupplies = this.getSelectedSuppliesForCreateOrder();
        if (!selectedSupplies.length) {
            return;
        }

        const expectedProviderId = Number(selectedSupplies?.[0]?.agreements?.[0]?.provider?.id);
        const hasExpectedProviderId = Number.isFinite(expectedProviderId);

        for (const prov of selectedSupplies) {
            const anyP: any = prov as any;
            const providerId = Number(anyP?.agreements?.[0]?.provider?.id);
            const hasProvider = Number.isFinite(providerId);
            const providerFee = anyP?.agreements?.[0]?.providerFee;
            const fee = anyP?.fee;
            const fixedPrice = anyP?.fixedPrice;
            const hasCost = (providerFee != null || fee != null) || fixedPrice;
            const hasActiveOrder = this.supplyHasActiveOrder(prov);
            const toDate = anyP?.agreements?.[0]?.toDate ?? anyP?.toDate;
            const hasToDate = toDate != null;

            const hasOrderInAnotherPeriod = this.supplyHasOrderInAnotherTimePeriod(prov);
            const differentProvider = hasExpectedProviderId && hasProvider && providerId !== expectedProviderId;
            const isError = !hasProvider || this.supplyHasInactiveProvider(prov) || !hasCost || hasActiveOrder || differentProvider || !hasToDate || hasOrderInAnotherPeriod;
            if (isError) {
                const key = this.getSupplyKey(prov);
                if (key) this.createOrderErrorSupplyKeys.add(key);
            }
        }
    }

    isCreateOrderErrorSupplyRow(prov: Provision): boolean {
        if (!prov) return false;
        const key = this.getSupplyKey(prov);
        return key ? this.createOrderErrorSupplyKeys.has(key) : false;
    }

    private supplyHasInactiveProvider(prov: Provision): boolean {
        const provider = prov?.agreements?.[0]?.provider;
        return !!provider && !provider.active;
    }

    supplyHasActiveOrder(prov: Provision): boolean {
        const anyP: any = prov as any;
        const pfId = anyP?.provisionFeeId ?? anyP?.provisionFees?.[0]?.id;
        if (pfId == null) return false;
        return (this.orders ?? [])
            .filter((or: any) => or?.state?.id !== this.ORDERS_STATES.CANCELADO)
            .some((or: any) => (or.items ?? []).some((it: any) => it.provisionFeeId === pfId));
    }

    supplyHasOrderInAnotherTimePeriod(prov: Provision): boolean {
        const anyP: any = prov as any;
        const pfId = anyP?.provisionFeeId ?? anyP?.provisionFees?.[0]?.id;
        if (pfId == null) return false;
        const from = this.dateRange?.historyFromDate;
        const to = this.dateRange?.historyToDate;
        if (!from || !to) return false;
        const dateRange = new DateRange(from, to);
        return (this.allOrdersWithoutFilter ?? [])
            .filter(or => or.state?.id !== this.ORDERS_STATES.CANCELADO && !dateRange.contains(moment(or.orderDate)))
            .some(or => (or.items ?? []).some(it => it.provisionFeeId === pfId));
    }

    countSuppliesWithOrderInAnotherTimePeriod(selectedSupplies: Provision[]): number {
        return selectedSupplies.filter(p => this.supplyHasOrderInAnotherTimePeriod(p)).length;
    }

    get itemsFormArray(): FormArray {
        return this.formAddOrder?.get(this.ORDER_ITEMS) as FormArray;
    }

    getItemsFormArrayFormGroup(index: number): FormGroup {
        return this.itemsFormArray.at(index) as FormGroup;
    }

    private createOrderFormFromSupplies(selectedSupplies: Provision[]): FormGroup {
        const providerId = selectedSupplies?.[0]?.agreements?.[0]?.provider?.id ?? null;
        return this.formBuilder.group({
            [this.ORDER_PROVIDER]: [providerId ? String(providerId) : '', [CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.requerido', undefined, 'Requerido'))]],
            [this.ORDER_PROVIDER_SEARCHING]: [''],
            [this.ORDER_DATE]: [this.toDay, [CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.requerida', undefined, 'Requerida'))]],
            [this.ORDER_OBSERVATIONS]: [''],
            [this.ORDER_ITEMS]: this.formBuilder.array(this.createItemsFormArrayFromSupplies(selectedSupplies)),
        });
    }

    private createItemsFormArrayFromSupplies(selectedSupplies: Provision[]): FormGroup[] {
        return (selectedSupplies ?? []).map((prov) => this.createOrderItemFormFromSupply(prov));
    }

    private createOrderItemFormFromSupply(prov: Provision): FormGroup {
        const anyP: any = prov as any;

        // Usar el mismo método que getSupplyQuantityLabel para obtener la cantidad correcta
        const freq = this.resolveDisplayFrequencyFromProvision(prov) ?? ({} as any);
        const amount = (freq as any)?.amount ?? anyP?.frequencyAmount ?? anyP?.frequency?.amount ?? null;
        const unit = (freq as any)?.unit ?? anyP?.frequencyUnit ?? anyP?.frequency?.unit ?? ProvisionsUnit.UNIDAD;

        // Obtener el provisionFee actual
        const currentProvisionFee = this.resolveCurrentProvisionFee(prov);
        const provisionFeeId = currentProvisionFee?.id ?? anyP?.provisionFeeId ?? null;

        // Agreement del provisionFee o de la provision
        const agreement = currentProvisionFee?.agreement ?? anyP?.agreements?.[0] ?? null;

        // Precio del proveedor
        const price = agreement?.providerFee ?? currentProvisionFee?.fee ?? anyP?.fee ?? null;

        // Practice (el insumo)
        const practice = anyP?.practice ?? {
            id: anyP?.practiceId,
            name: anyP?.practiceName ?? anyP?.name,
            code: anyP?.practiceCode ?? anyP?.code,
            restriction: anyP?.practice?.restriction ?? null,
            active: anyP?.practice?.active ?? true
        };

        // CaseModuleId
        const caseModuleId = currentProvisionFee?.caseModuleId ?? anyP?.caseModuleId ?? null;

        // Fee y profitMargin del provisionFee
        const fee = currentProvisionFee?.fee ?? anyP?.fee ?? null;
        const cost = (currentProvisionFee as any)?.cost ?? price ?? null;
        const profitMargin = (currentProvisionFee as any)?.profitMargin ?? anyP?.profitMargin ?? null;
        const fixedPrice = (currentProvisionFee as any)?.fixedPrice ?? anyP?.fixedPrice ?? null;

        return this.formBuilder.group({
            [this.ITEM_QUANTITY]: [amount, CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.requerida', undefined, 'Requerida'))],
            [this.ITEM_PRICE]: [price, CustomValidators.minValue(0, this.experienceI18n.translate('modules.cases.modules-scheme.debe-ser-un-valor-positivo', undefined, 'Debe ser un valor positivo'))],
            [this.ITEM_PROVISIONFEEID]: [provisionFeeId],
            [this.ITEM_UNIT]: [unit],
            [this.ITEM_PRACTICE]: [practice],
            [this.ITEM_PROVISION]: [prov],
            // Campos adicionales para replicar estructura del flujo clásico
            caseModuleId: [caseModuleId],
            fee: [fee],
            cost: [cost],
            profitMargin: [profitMargin],
            fixedPrice: [fixedPrice],
            provisionFee: [currentProvisionFee],
            agreement: [agreement],
        });
    }

    openAddOrderDialog(): void {
        if (this.createOrderButtonDisabled()) {
            return;
        }

        const selectedSupplies = this.getSelectedSuppliesForCreateOrder();
        if (!selectedSupplies.length) {
            return;
        }

        this.formAddOrder = this.createOrderFormFromSupplies(selectedSupplies);
        this.orderItemsDataSource.data = this.itemsFormArray?.controls ?? [];

        const provider = selectedSupplies?.[0]?.agreements?.[0]?.provider as any;
        if (provider?.id) {
            this.actualProviderOrderSelected[0] = String(provider.id);
            this.providerOrderOnSelect[0] = [provider];
        } else {
            this.actualProviderOrderSelected[0] = null;
            this.providerOrderOnSelect[0] = [];
        }

        this._dialogRefOrder = this.dialog.open(DialogComponent, {
            disableClose: true,
            minWidth: '60%',
            maxHeight: '95vh',
            data: { template: this.addOrderTemplate, title: this.experienceI18n.translate('modules.cases.modules-scheme.crear-pedido', undefined, 'Crear pedido') },
        });

        this._dialogRefOrder.afterClosed().subscribe(() => {
            this.formAddOrder = null;
            this.orderItemsDataSource.data = [];
            this.actualProviderOrderSelected[0] = null;
            this.providerOrderOnSelect[0] = [];
        });
    }

    closeOrderDialog(): void {
        this._dialogRefOrder?.close();
    }

    onClickCreateOrder(): void {
        if (!this.formAddOrder?.valid) {
            return;
        }

        const rawValue: any = { ...(this.formAddOrder.getRawValue() ?? {}) };
        delete rawValue[this.ORDER_PROVIDER_SEARCHING];

        // Transformar items al formato mínimo esperado por el backend
        rawValue.items = (rawValue.items ?? []).map((item: any) => {
            const provision = item?.provision as any;
            const practice = item?.practice ?? item?.[this.ITEM_PRACTICE];

            // Solo enviar los campos que el backend espera
            return {
                quantityOrdered: item?.[this.ITEM_QUANTITY] ?? item?.quantityOrdered,
                priceOrdered: item?.[this.ITEM_PRICE] ?? item?.priceOrdered,
                provisionFeeId: item?.[this.ITEM_PROVISIONFEEID] ?? item?.provisionFeeId,
                unit: item?.[this.ITEM_UNIT] ?? item?.unit,
                practice: practice ? {
                    id: practice?.id,
                    name: practice?.name,
                    restriction: practice?.restriction ?? null,
                    active: practice?.active ?? true,
                    code: practice?.code,
                } : null,
                provisionId: provision?.id ?? null,
                caseModuleId: item?.caseModuleId ?? provision?.caseModuleId ?? null,
            };
        });

        // Provider y fecha
        rawValue.providerId = parseInt(rawValue.providerId);
        rawValue.orderDate = moment(rawValue.orderDate).set('hour', 8);

        // Limpiar campos vacíos
        const formValue: any = rawValue;
        for (const property in formValue) {
            if (formValue[property] === '' || formValue[property] == null) {
                delete formValue[property];
            }
        }

        const caseId = this.case?.id ?? this.caseId;
        this.casesFacade.addOrder(formValue, caseId).subscribe({
            next: () => {
                this.swalWithCustomizeButtons
                    .fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.el-pedido-se-creo-correctamente', undefined, 'El pedido se creó correctamente'), icon: 'success' })
                    .then(() => {
                        this.closeOrderDialog();
                        // Limpiamos selección de insumos para evitar re-crear el mismo pedido
                        this.selectionSupplies.clear();
                        this.createOrderErrorSupplyKeys.clear();
                        // Emitir cambios para refrescar datos
                        this.caseModulesChanged.emit();
                    });
            },
            error: () => {
                this.closeOrderDialog();
            },
        });
    }

    /** alterna expandido/colapsado de un pedido y lo selecciona para filtrar en calendario */
    toggleOrderRow(order: Order): void {
        this.expandedOrder = this.expandedOrder === order ? null : order;
        // this.toggleOrderSelection(order);
    }

    /** cantidad total de insumos pedidos (suma quantityOrdered de cada ítem) */
    getOrderTotalQuantity(order: Order): number {
        if (!order?.items?.length) {
            return 0;
        }
        return order.items.reduce((acc, it: any) => {
            return acc + (Number(it.quantityOrdered) || 0);
        }, 0);
    }

    /** cantidad entregada: si no hay quantityDelivered usamos quantityOrdered como fallback */
    getOrderDeliveredQuantity(item: any): number {
        if (item?.quantityDelivered != null) {
            return item.quantityDelivered;
        }
        if (item?.quantityOrdered != null) {
            return item.quantityOrdered;
        }
        return 0;
    }

    /** módulo que mostramos en la tabla hija */
    getOrderModuleLabel(item: Item): string {
        const caseModuleId = item?.caseModuleId ?? null;
        if (caseModuleId) {
            const caseModule = this.caseModulesComposed?.find(cm => cm.id === caseModuleId);
            return caseModule?.name || '-';
        }
        return '-';
    }

    /** Retorna los items de un order para la tabla hija */
    getOrderItems(order: Order): Item[] {
        return order?.items ?? [];
    }

    /** trackBy para mejorar render de la tabla de items */
    trackByItemId(index: number, item: Item): number {
        return item?.id ?? index;
    }

    /** nombre completo del proveedor: name + surname */
    getOrderProviderName(order: Order): string {
        const p: any = order?.provider;
        if (!p) {
            return '-';
        }
        return (
            p.fullName ||
            [p.name, p.surname].filter(Boolean).join(' ') ||
            '-'
        );
    }
    /** trackBy para mejorar render de la tabla de pedidos */
    trackByOrderId(index: number, order: Order): number {
        return order?.id ?? index;
    }

    getProvidersByName = (value: string): Observable<Provider[]> => {
        return this.entitiesFacade
            .loadProviders({ fullName_like: value, active: true, isSupply: true })
            .pipe(
                map((providers) => (Array.isArray(providers) ? providers : [])),
                catchError(() => of([])),
            );
    }

    calculateIdProvider = (element: Provider) => {
        return element ? element.id : null;
    }

    calculateNameProvider = (element: Provider) => {
        return element ? this.entitiesFacade.getProvidersFullName(element) : null;
    }
    // providerSelected(provider: Provider, i?: number) {
    //     this.formSupplies.updateValueAndValidity();
    // }

    private parseVigencyToDates(vigency: string | null | undefined): { from: Date | null; to: Date | null } {
        const v = String(vigency ?? '').trim();
        const parts = v.split(' - ');
        if (parts.length !== 2) return { from: null, to: null };

        const from = moment(parts[0], 'DD/MM/YYYY', true);
        const to = moment(parts[1], 'DD/MM/YYYY', true);
        return {
            from: from.isValid() ? from.toDate() : null,
            to: to.isValid() ? to.toDate() : null,
        };
    }

    private resolveCaseModulePatchDateRange(caseModule: any): { fromDate: Date | null; toDate: Date | null } {
        const { from: vigFrom, to: vigTo } = this.parseVigencyToDates(caseModule?.vigency);

        // Para PATCH de módulos, la vigencia visible es la fuente de verdad.
        // Evita reusar horas persistidas previamente como 23:59:59.
        const fromSource = vigFrom ?? this.coerceToDate(caseModule?.fromDate);
        const toSource = vigTo ?? this.coerceToDate(caseModule?.toDate);

        return {
            fromDate: fromSource ? moment(fromSource).startOf('day').toDate() : null,
            toDate: toSource ? moment(toSource).startOf('day').toDate() : null,
        };
    }

    private parseFrequencyString(freq: string | null | undefined): { amount: number | null; unit: any | null } {
        // Espera "N por X"
        const f = String(freq ?? '').trim();
        if (!f) return { amount: null, unit: null };

        const m = f.match(/^(\d+(?:[.,]\d+)?)\s+por\s+(.+)$/i);
        if (!m) return { amount: null, unit: null };

        const amount = Number(String(m[1]).replace(',', '.'));
        const unitRaw = String(m[2] ?? '').trim().toLowerCase();

        // Mapeo a tus ids de ProvisionsFrequencyUnit cuando aplica
        let unit: any = null;
        if (unitRaw.startsWith('dia') || unitRaw === 'día') unit = ProvisionsFrequencyUnit.DIA;
        else if (unitRaw.startsWith('sem')) unit = ProvisionsFrequencyUnit.SEMANA;
        else if (unitRaw.startsWith('mes')) unit = ProvisionsFrequencyUnit.MES;
        else unit = m[2].trim(); // fallback string

        return { amount: Number.isFinite(amount) ? amount : null, unit };
    }

    private coerceToDate(value: any): Date | null {
        if (!value) return null;
        if (value instanceof Date) return value;
        if (typeof value?.toDate === 'function') {
            try {
                const d = value.toDate();
                return d instanceof Date ? d : null;
            } catch {
                return null;
            }
        }
        const parsed = new Date(value);
        return Number.isFinite(parsed.getTime()) ? parsed : null;
    }

    private resolveCurrentProvisionFee(provision: Provision | null | undefined): ProvisionFee | null {
        const fees = (provision?.provisionFees ?? []) as ProvisionFee[];
        if (!fees.length) return null;

        const currentId = Number((provision as any)?.provisionFeeId);
        if (Number.isFinite(currentId) && currentId > 0) {
            const byId = fees.find(f => f?.id === currentId);
            if (byId) return byId;
        }

        // Fallback: la más reciente por fromDate (y por id si no hay fechas válidas)
        return fees.reduce<ProvisionFee>((best, fee) => {
            if (!best) return fee;
            const bestFrom = this.coerceToDate(best.fromDate)?.getTime() ?? -1;
            const feeFrom = this.coerceToDate(fee.fromDate)?.getTime() ?? -1;
            if (feeFrom !== bestFrom) return feeFrom > bestFrom ? fee : best;
            return (fee?.id ?? 0) > (best?.id ?? 0) ? fee : best;
        }, fees[0]);
    }

    private resolveProvisionFeesToAuthorizeFromCaseModule(cm: CaseModuleComposed | null | undefined): ProvisionFee[] {
        const provisions = (cm?.provisions ?? []) as any[];
        if (!provisions.length) return [];

        const checked = provisions.filter(p => p?.checked === true) as Provision[];
        const targets: Provision[] = (checked.length ? checked : provisions) as Provision[];

        const fees = targets
            .map(p => this.resolveCurrentProvisionFee(p))
            .filter((f): f is ProvisionFee => !!f);

        const dedup = new Map<number, ProvisionFee>();
        for (const f of fees) dedup.set(f.id, f);
        return [...dedup.values()];
    }

    private resolveMinFromDate(fees: ProvisionFee[]): Date | null {
        let min: Date | null = null;
        for (const f of fees) {
            const d = this.coerceToDate(f?.fromDate);
            if (!d) continue;
            if (!min || d.getTime() < min.getTime()) min = d;
        }
        return min;
    }

    openUpdateAuthorizationDialog(cm: CaseModuleComposed) {
        this.photoEvolutionB64 = null;
        this.caseModuleToAuthorize = cm;
        this.provisionFeesToAuthorize = this.resolveProvisionFeesToAuthorizeFromCaseModule(cm);
        this.provisionsToUpdate = (cm?.provisions ?? []) as Provision[];

        if (!this.provisionFeesToAuthorize.length) {
            (this.swalAutoClose ?? Swal).fire({
                text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-encontraron-practicas-insumos-para-autorizar-en-este-modulo', undefined, 'No se encontraron prácticas/insumos para autorizar en este módulo.'),
                icon: 'error',
            });
            return;
        }

        // Se usa como referencia para mostrar fechas en el template
        this.provisionFeeSelectedToAutorize = this.provisionFeesToAuthorize[0];
        this.formAuthorization = this.createAuthorizationGroup(this.provisionFeeSelectedToAutorize);

        const minFrom = this.resolveMinFromDate(this.provisionFeesToAuthorize) || this.coerceToDate(this.provisionFeeSelectedToAutorize?.fromDate);
        if (minFrom) {
            this.formAuthorization.get(this.AUTHORIZATION_FROM_DATE)?.setValue(minFrom);
        }

        // Only auth, then set custom validators
        this.formAuthorization.get(this.AUTHORIZATION_NUMBER)?.setValidators([
            CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.numero-de-autorizacion-requerido', undefined, 'Número de autorización requerido')),
        ]);

        const toDateValidators: ValidatorFn[] = [CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.fecha-de-fin-de-autorizacion-requerida', undefined, 'Fecha de fin de autorización requerida'))];
        if (minFrom) {
            toDateValidators.push(CustomValidators.dateGreaterEqualThan(minFrom));
        }
        this.formAuthorization.get(this.AUTHORIZATION_TO_DATE)?.setValidators(toDateValidators);
        this.formAuthorization.updateValueAndValidity();

        this.openDialog(
            this.experienceI18n.translate('modules.cases.modules-scheme.agregar-autorizacion', undefined, 'Agregar autorización'),
            this.updateAuthorizationTemplate,
            { maxHeight: '95vh', minWidth: '40%' },
            () => { }
        );
    }

    showAuthEvolution(authorization: Authorization) {
        // Find evolution
        this.evolutionsFacade.loadEvolutionsById(authorization.evolutionId).subscribe(evol => {
            this._bottomSheet.open(EvolutionsDialogComponent, {
                panelClass: 'bottomSheetPanelClass',
                data: {
                    evolutions: [evol],
                    attentions: [],
                    name: this.case.patient.name,
                    surname: this.case.patient.surname,
                    expanded: true
                },
            });
        })
    }

    onClickAuthorization(cm: CaseModuleComposed) {
        const authorization = cm?.authorization;
        if (this.generalService.getAuthorizationStatus(authorization) !== AuthorizationState.UNAUTHORIZED) {
            this.showAuthEvolution(authorization);
        } else {
            this.openUpdateAuthorizationDialog(cm);
        }
    }


    onClickCreateAuthorization() {
        if (!this.formAuthorization) return;

        if (this.formAuthorization.invalid) {
            this.formAuthorization.markAllAsTouched();
            return;
        }

        // Consistencia con el alta de módulo: archivo requerido
        if (!this.photoEvolutionB64) {
            (this.swalAutoClose ?? Swal).fire({
                text: this.experienceI18n.translate('modules.cases.modules-scheme.adjunta-el-archivo-de-autorizacion-antes-de-guardar', undefined, 'Adjuntá el archivo de autorización antes de guardar.'),
                icon: 'error',
            });
            return;
        }

        if (this.formAuthorization.get(this.AUTHORIZATION_OBSERVATIONS)?.value == '') {
            this.formAuthorization.get(this.AUTHORIZATION_OBSERVATIONS)?.setValue(this.experienceI18n.translate('modules.cases.modules-scheme.sin-observaciones', undefined, 'Sin observaciones.'))
        }

        const caseId = this.resolveCaseIdOrFail();
        if (!caseId) return;

        const cm = this.caseModuleToAuthorize;
        const caseModuleId = Number((cm as any)?.id);
        if (!cm || !Number.isFinite(caseModuleId) || caseModuleId <= 0) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-identificar-el-modulo-a-autorizar', undefined, 'No se pudo identificar el módulo a autorizar.'), icon: 'error' });
            return;
        }

        const authPayload: any = {
            authorizationNumber: String(this.formAuthorization.get(this.AUTHORIZATION_NUMBER)?.value ?? '').trim(),
            observations: String(this.formAuthorization.get(this.AUTHORIZATION_OBSERVATIONS)?.value ?? '').trim(),
            mediaUrls: [this.photoEvolutionB64],
            fromDate: this.formAuthorization.get(this.AUTHORIZATION_FROM_DATE)?.value,
            toDate: this.formAuthorization.get(this.AUTHORIZATION_TO_DATE)?.value,
        };

        const { from: cmFrom, to: cmTo } = this.parseVigencyToDates((cm as any)?.vigency);
        const { amount: freqAmount, unit: freqUnit } = this.parseFrequencyString((cm as any)?.frequency);

        const patchBody: any = {
            // Datos base del CaseModule
            ...(cmFrom ? { fromDate: cmFrom } : {}),
            ...(cmTo ? { toDate: cmTo } : {}),
            ...(cm?.fee != null ? { fee: Number((cm as any)?.fee) } : {}),
            ...(freqAmount != null && freqUnit != null ? { frequency: { amount: freqAmount, unit: String(freqUnit) } } : {}),

            // Authorization + archivo
            authorizationId: null,
            authorization: authPayload,
        };

        // Requerimiento: no enviar prácticas/insumos en este flujo
        delete patchBody.practiceItems;
        delete patchBody.supplyItems;

        this.modulesFacade.patchCaseModule(patchBody, caseId, caseModuleId).subscribe({
            next: () => {
                (this.swalAutoClose ?? Swal).fire({
                    text: this.experienceI18n.translate('modules.cases.modules-scheme.autorizacion-cargada-correctamente', undefined, 'Autorización cargada correctamente'),
                    icon: 'success',
                });
                this.caseModulesChanged.emit();
                this.onClickCloseDialog();
            },
            error: (error) => {
                const msg = error?.error?.message || this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-cargar-la-autorizacion', undefined, 'No se pudo cargar la autorización.');
                (this.swalAutoClose ?? Swal).fire({ text: msg, icon: 'error' });
            },
        });
    }

    private createSuspendCaseModuleForm(caseModuleComposed: any): FormGroup {
        const moduleName =
            caseModuleComposed?.name ||
            caseModuleComposed?.module?.name ||
            this.experienceI18n.translate('modules.cases.modules-scheme.sin-nombre', undefined, 'Sin nombre');

        const arrangementName =
            caseModuleComposed?.arrangement?.name ||
            caseModuleComposed?.arrangementName ||
            this.experienceI18n.translate('modules.cases.modules-scheme.sin-convenio', undefined, 'Sin convenio');

        // TODO: refactor
        // return this.formBuilder.group({
        //     moduleName: [{ value: moduleName, disabled: true }],
        //     arrangementName: [{ value: arrangementName, disabled: true }],

        //     // Último día de agenda (lo que el usuario elige)
        //     lastScheduleDay: [
        //         null,
        //         Validators.compose([
        //             Validators.required,
        //             // Opcional: si tenés fechas de vigencia del módulo, podés acotar
        //             // CustomValidators.dateGreaterEqualThan(caseModuleComposed?.fromDate),
        //             // caseModuleComposed?.toDate ? CustomValidators.dateLowerEqualThan(caseModuleComposed?.toDate) : null,
        //         ].filter(Boolean)),
        //     ],
        // });

        return this.formBuilder.group({
            [this.VALIDITYMODULE_FROM_DATE]: [
                !!caseModuleComposed.fromDate ? caseModuleComposed.fromDate : '',
                Validators.compose([
                    CustomValidators.required(
                        this.experienceI18n.translate(
                            'modules.cases.modules-scheme.fecha-requerida',
                            undefined,
                            'Fecha requerida'
                        )
                    ),
                    CustomValidators.dateLowerEqualThan(caseModuleComposed.toDate),
                    CustomValidators.dateGreaterThan(caseModuleComposed.fromDate),
                ]),
            ],
            [this.VALIDITYMODULE_TO_DATE]: [{ value: caseModuleComposed.toDate, disabled: true }]
        })
    }


    // ===== Dialogs: suspend module =====
    openSuspendCaseModule(caseModuleComposed: any) {
        this.caseModuleToWork = caseModuleComposed;
        this.formSuspendCaseModule = this.createSuspendCaseModuleForm(caseModuleComposed);

        this.openDialog(
            this.experienceI18n.translate('modules.cases.modules-scheme.finalizar-modulo', undefined, 'Finalizar módulo'),
            this.suspendCaseModuleTemplate,
            { maxHeight: '95vh', minWidth: '40%' },
            () => {
                this.caseModuleToWork = null;
                this.formSuspendCaseModule?.reset();
                this.isFinalizingCaseModule = false;
            },
        );

        // openDialog usa _dialogRef como ref principal
        this._dialogRefSuspendCaseModule = this._dialogRef as any;
    }

    onClickCloseSuspendCaseModuleDialog() {
        this._dialogRefSuspendCaseModule?.close();
        this._dialogRef?.close();
    }

    onClickFinalizeCaseModule(): void {
        if (!this.caseModuleToWork || !this.formSuspendCaseModule) return;

        if (this.formSuspendCaseModule.invalid) {
            this.formSuspendCaseModule.markAllAsTouched();
            return;
        }

        const caseId = this.resolveCaseIdOrFail();
        if (!caseId) return;

        const caseModuleId = Number((this.caseModuleToWork as any)?.id);
        if (!Number.isFinite(caseModuleId) || caseModuleId <= 0) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-identificar-el-modulo-a-finalizar', undefined, 'No se pudo identificar el módulo a finalizar.'), icon: 'error' });
            return;
        }

        const toDate: Date = moment(this.suspendModuleFromDate).subtract(1, 'days').toDate();
        // TODO: use when refactor pop-up
        //const lastScheduleDay: Date = this.formSuspendCaseModule.get('lastScheduleDay')?.value;
        //const toDate: Date = moment(lastScheduleDay).endOf('day').toDate();

        // En este sistema siempre existe vigencia desde/hasta, así que la fuente de verdad es `vigency`.
        const { from: fromDate } = this.parseVigencyToDates((this.caseModuleToWork as any)?.vigency);
        if (!fromDate) {
            (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-leer-la-vigencia-desde-del-modulo', undefined, 'No se pudo leer la vigencia desde del módulo.'), icon: 'error' });
            return;
        }

        const basePatchBody: any = { fromDate, toDate };

        const runPatch = (body: any) =>
            this.modulesFacade.patchCaseModule(body, caseId, caseModuleId).subscribe({
                next: () => {
                    this.isFinalizingCaseModule = false;
                    (this.swalAutoClose ?? Swal).fire({ text: this.experienceI18n.translate('modules.cases.modules-scheme.modulo-actualizado', undefined, 'Módulo actualizado'), icon: 'success' });
                    this.caseModulesChanged.emit();
                    this.onClickCloseSuspendCaseModuleDialog();
                },
                error: (error) => {
                    if (error?.status === 418) {
                        this.isFinalizingCaseModule = false;
                        const errorContent: HttpBaseResponseErrorTeaPot = error.error;
                        this.attentionsService.processErrorCountAttentions(errorContent, 'error').then((confirm) => {
                            if (!confirm) return;
                            this.isFinalizingCaseModule = true;
                            runPatch({ ...basePatchBody, force: true });
                        });
                        return;
                    }

                    this.isFinalizingCaseModule = false;
                    const msg = error?.error?.message || this.experienceI18n.translate('modules.cases.modules-scheme.no-se-pudo-finalizar-el-modulo', undefined, 'No se pudo finalizar el módulo');
                    (this.swalAutoClose ?? Swal).fire({ text: msg, icon: 'error' });
                },
            });

        this.isFinalizingCaseModule = true;
        runPatch(basePatchBody);
    }

    private suggestRenewalDateRange(prevFrom: Date, prevTo: Date): { from: Date; to: Date } {
        const from = moment(prevFrom).startOf('day');
        const to = moment(prevTo).startOf('day');

        // Helper para comparar por día (ignora hora)
        const sameDay = (a: moment.Moment, b: moment.Moment) =>
            a.format('YYYY-MM-DD') === b.format('YYYY-MM-DD');

        // ===== REGLA: Mes o meses completos calendario =====
        // - from: primer día de su mes
        // - to: último día de su mes
        const isFullMonthsCalendar =
            sameDay(from, from.clone().startOf('month')) &&
            sameDay(to, to.clone().endOf('month').startOf('day'));

        if (isFullMonthsCalendar) {
            // Cantidad de meses completos incluidos en el rango (1..N)
            const monthsCount = to.diff(from, 'months') + 1;

            // Como to es fin de mes, newFrom = día siguiente => 1er día del mes siguiente
            const newFrom = to.clone().add(1, 'day').startOf('day');

            // newTo = fin del mes correspondiente a newFrom + (monthsCount - 1) meses
            const newTo = newFrom
                .clone()
                .add(monthsCount - 1, 'month')
                .endOf('month')
                .startOf('day');

            return { from: newFrom.toDate(), to: newTo.toDate() };
        }

        // ===== FALLBACK: replica por días =====
        // Mantiene la distancia entre fechas del rango original.
        const diff = Math.max(to.diff(from, 'days'), 0);

        const newFrom = to.clone().add(1, 'day').startOf('day');
        const newTo = newFrom.clone().add(diff, 'days').startOf('day');

        return { from: newFrom.toDate(), to: newTo.toDate() };
    }


    // ===== Dialogs: repost/renew module =====
    openRePostModuleDialog(row?: CaseModuleComposed) {
        const target = row ?? this.expandedElement;
        if (!target) return;

        this.isAddMode = false;
        this.isEditMode = true;
        this.isRePostMode = true;
        this.arrangementSelected = target.arrangementId != null
            ? this.caseArrangements?.find(arrangement => Number(arrangement.id) === Number(target.arrangementId))
            : undefined;

        // Referencia al módulo del caso que se está renovando (para hidratar prácticas/insumos)
        this.caseModuleSelected = target;
        this.editingModuleRow = target;

        this.title = this.experienceI18n.translate('modules.cases.modules-scheme.renovar-modulo', undefined, 'Renovar módulo');
        this.checkboxAuthorizationText = this.experienceI18n.translate('modules.cases.modules-scheme.agregar-autorizacion', undefined, 'Agregar autorización');

        this.addModuleForm(undefined, undefined, target);
        this.formModule.get(this.ARRANGEMENT)?.setValue(target.arrangementId ?? null, { emitEvent: false });

        // ADD: fromDate <= toDate (igual que en toogleRetroactive)
        this.formModule.setValidators(CustomValidators.fromToDate(this.VALIDITY_FROM_DATE, this.VALIDITY_TO_DATE));

        const { from: prevFrom, to: prevTo } = this.parseVigencyToDates(target.vigency);

        if (prevFrom && prevTo) {
            const suggested = this.suggestRenewalDateRange(prevFrom, prevTo);

            this.formModule.get(this.VALIDITY_FROM_DATE)?.setValue(suggested.from, { emitEvent: false });
            this.formModule.get(this.VALIDITY_TO_DATE)?.setValue(suggested.to, { emitEvent: false });

            // Validadores equivalentes al “renovar” en provisions-detail (Retroactive OFF)
            this.formModule.get(this.VALIDITY_FROM_DATE)?.setValidators(
                Validators.compose([
                    CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.vigencia-desde-requerida', undefined, 'Vigencia desde requerida')),
                    CustomValidators.dateGreaterThan(prevTo),
                ])
            );

            this.formModule.get(this.VALIDITY_TO_DATE)?.setValidators(
                Validators.compose([
                    CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.vigencia-hasta-requerida', undefined, 'Vigencia hasta requerida')),
                    CustomValidators.dateGreaterThan(this.formModule.get(this.VALIDITY_FROM_DATE)?.value),
                ])
            );
            this.formModule.get(this.VALIDITY_FROM_DATE)?.updateValueAndValidity({ emitEvent: false });
            this.formModule.get(this.VALIDITY_TO_DATE)?.updateValueAndValidity({ emitEvent: false });

            // IMPORTANT: si cambia fromDate, actualizar validador de toDate
            this._rePostFromDateSub?.unsubscribe();
            this._rePostFromDateSub = this.formModule
                .get(this.VALIDITY_FROM_DATE)
                ?.valueChanges.subscribe((newFrom: any) => {
                    const fromDate = this.coerceToDate(newFrom);
                    const toCtrl = this.formModule.get(this.VALIDITY_TO_DATE);
                    if (!toCtrl) return;

                    toCtrl.setValidators(
                        Validators.compose([
                            CustomValidators.required(this.experienceI18n.translate('modules.cases.modules-scheme.vigencia-hasta-requerida', undefined, 'Vigencia hasta requerida')),
                            ...(fromDate ? [CustomValidators.dateGreaterThan(fromDate)] : []),
                        ])
                    );
                    toCtrl.updateValueAndValidity({ emitEvent: false });
                });
        }

        this.saveArrangementItemsByModule();

        if (target.fee != null) {
            this.formModule.get(this.FEE)?.setValue(target.fee, { emitEvent: false });
        }
        if (target.arrangementId != null) {
            const feeControl = this.formModule.get(this.FEE);
            feeControl?.clearValidators();
            feeControl?.updateValueAndValidity({ emitEvent: false });
        }

        const { amount, unit } = this.parseFrequencyString(target.frequency);
        if (amount != null) this.formFrequency.get(this.AMOUNT)?.setValue(amount, { emitEvent: false });
        if (unit != null) this.formFrequency.get(this.UNIT)?.setValue(unit, { emitEvent: false });

        this.setControlEnabled(this.formModule.get(this.MODULE), false);
        this.setControlEnabled(this.formModule.get(this.MODULE_SEARCHING), false);

        this.formModule.updateValueAndValidity({ emitEvent: false });
        this.formFrequency.updateValueAndValidity({ emitEvent: false });
    }

    private emitSelectedCaseModuleIds(): void {
        const ids = (this.selectionModules?.selected ?? [])
            .map((m: any) => Number(m?.id))
            .filter((id: number) => Number.isFinite(id));
        this.selectedCaseModuleIdsChanged.emit(ids);
    }

    // ===== Métodos de selección de pedidos (orders) =====
    toggleOrderSelection(order: Order): void {
        this.selectionOrders.toggle(order);
        this.onOrderSelectionChange();
    }

    isOrderSelected(order: Order): boolean {
        const orderId = order?.id;
        if (orderId == null) return false;
        return this.selectionOrders.selected.some(o => o?.id === orderId);
    }

    toggleAllOrders(checked: boolean): void {
        const allOrders = this.ordersDataSource?.data ?? [];
        if (checked) {
            this.selectionOrders.select(...allOrders);
        } else {
            this.selectionOrders.clear();
        }
        this.onOrderSelectionChange();
    }

    isAllOrdersSelected(): boolean {
        const allOrders = this.ordersDataSource?.data ?? [];
        if (allOrders.length === 0) return false;
        return this.selectionOrders.selected.length === allOrders.length;
    }

    isSomeOrdersSelected(): boolean {
        const selectedCount = this.selectionOrders.selected.length;
        const totalCount = this.ordersDataSource?.data?.length ?? 0;
        return selectedCount > 0 && selectedCount < totalCount;
    }

    private onOrderSelectionChange(): void {
        this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
        this.emitSelectedCaseModuleIds();
    }

    /**
     * Construye el objeto de filtros basado en las selecciones actuales de módulos, prácticas e insumos.
     */
    updateFilter(): ProvisionAgreementFilters {
        const allModules = this.caseModulesComposed ?? [];
        const allOrders = this.ordersDataSource?.data ?? [];

        // Contadores desde los flat SelectionModels
        this.modules_trueChecks = this.selectionModules?.selected?.length ?? 0;
        this.practices_RowChecks_trueChecks = this.selectionProvisions.selected.length;
        this.agreements_RowChecks_trueChecks = this.selectionAgreements.selected.length;
        this.orders_trueChecks = this.selectionOrders?.selected?.length ?? 0;
        const supplies_trueChecks = this.selectionSupplies?.selected?.length ?? 0;

        const hasSingleModuleSelected = this.singleSelectionMode && this.singleSelectedModuleId != null;
        const hasOrdersRadioSelected = this.singleSelectionMode && this.ordersRadioSelected;

        const isNothingSelected =
            this.modules_trueChecks === 0 &&
            this.practices_RowChecks_trueChecks === 0 &&
            this.agreements_RowChecks_trueChecks === 0 &&
            this.orders_trueChecks === 0 &&
            supplies_trueChecks === 0 &&
            !hasSingleModuleSelected &&
            !hasOrdersRadioSelected;

        if (isNothingSelected) {
            // Sin selección → arrays vacíos; schedule-case mostrará el mensaje de "seleccione un módulo"
            this.provisionAgreementsInput = {
                provisions: [],
                provisionFees: [],
                agreements: [],
                orders: [],
                provisionsSupplies: [],
                caseModuleIds: [],
                modulesFlow: true,
                singleMode: this.singleSelectionMode
            };

            return this.provisionAgreementsInput;
        }

        // ===== ALGO ESTÁ SELECCIONADO → enviar SOLO lo que tiene check =====
        const provisions: Provision[] = [];
        const provisionFees: ProvisionFee[] = [];
        const caseModuleIds: number[] = [];

        // 0a. En singleSelectionMode con PEDIDOS seleccionado por radio → TODOS los pedidos, sin caseModuleIds
        if (hasOrdersRadioSelected) {
            this.provisionAgreementsInput = {
                provisions: [],
                provisionFees: [],
                agreements: [],
                orders: [...allOrders],
                provisionsSupplies: [],
                caseModuleIds: [],
                modulesFlow: true,
                singleMode: this.singleSelectionMode
            };
            return this.provisionAgreementsInput;
        }

        // 0b. En singleSelectionMode con MÓDULO seleccionado por radio, siempre incluir su caseModuleId.
        //     No volcamos provisions aquí: selectModuleSingle() ya envía solo caseModuleIds
        //     y mamushkaCheckBoxesV2 se encarga de resolver las attentions por caseModuleId.
        if (hasSingleModuleSelected) {
            const singleId = Number(this.singleSelectedModuleId);
            if (Number.isFinite(singleId)) {
                caseModuleIds.push(singleId);
            }
        }

        // 1. Módulos seleccionados (checkboxes) → agregar sus IDs
        if (this.modules_trueChecks > 0) {
            const selectedModules = this.selectionModules?.selected ?? [];
            for (const m of selectedModules) {
                const id = Number(m?.id);
                if (Number.isFinite(id) && !caseModuleIds.includes(id)) caseModuleIds.push(id);
            }
        }

        // 2. Prácticas seleccionadas → usar selectionProvisions flat
        const selectedAgreements: any[] = [];
        const hasIndividualAgreementsSelected = this.agreements_RowChecks_trueChecks > 0;

        if (this.practices_RowChecks_trueChecks > 0) {
            for (const practice of this.selectionProvisions.selected) {
                this.addProvisionToFilter(practice, provisions, provisionFees);
                if (!hasIndividualAgreementsSelected) {
                    const practiceAgreements = (practice as any)?.agreements ?? [];
                    for (const agreement of practiceAgreements) {
                        const agrId = this.getAgreementId(agreement);
                        if (agrId != null && !selectedAgreements.some(a => this.getAgreementId(a) === agrId)) {
                            selectedAgreements.push(agreement);
                        }
                    }
                }
            }
        }

        // 3. Agreements seleccionados individualmente → usar selectionAgreements flat
        if (hasIndividualAgreementsSelected) {
            for (const agreement of this.selectionAgreements.selected) {
                const id = this.getAgreementId(agreement);
                if (id != null && !selectedAgreements.some(a => this.getAgreementId(a) === id)) {
                    selectedAgreements.push(agreement);
                }
                // Agregar la provision padre del agreement
                for (const module of allModules) {
                    const practices = this.getPracticesByModule(module);
                    for (const practice of practices) {
                        const agrList = (practice as any)?.agreements ?? [];
                        if (agrList.some((a: any) => this.getAgreementId(a) === id)) {
                            this.addProvisionToFilter(practice, provisions, provisionFees);
                            break;
                        }
                    }
                }
            }
        }

        // 4. Orders seleccionados
        const selectedOrders = this.orders_trueChecks > 0
            ? [...(this.selectionOrders?.selected ?? [])]
            : [];

        this.provisionAgreementsInput = {
            provisions,
            provisionFees,
            agreements: selectedAgreements,
            orders: selectedOrders,
            provisionsSupplies: [...this.selectionSupplies.selected],
            caseModuleIds,
            modulesFlow: true,
            singleMode: this.singleSelectionMode
        };

        return this.provisionAgreementsInput;
    }

    /**
     * Agrega una provision y sus fees al array de filtros evitando duplicados
     */
    private addProvisionToFilter(
        provision: Provision,
        provisions: Provision[],
        provisionFees: ProvisionFee[]
    ): void {
        const anyProv = provision as any;
        const provId = anyProv?.id ?? anyProv?.provisionId;

        // Evitar duplicados
        if (provId && !provisions.some(p => (p as any)?.id === provId || (p as any)?.provisionId === provId)) {
            provisions.push(provision);

            // Agregar provisionFees si existen
            if (anyProv?.provisionFees?.length) {
                for (const fee of anyProv.provisionFees) {
                    const feeId = fee?.id ?? fee?.provisionFeeId;
                    if (feeId && !provisionFees.some(f => (f as any)?.id === feeId || (f as any)?.provisionFeeId === feeId)) {
                        provisionFees.push(fee);
                    }
                }
            }
        }
    }

    //get total scheduled by provision
    totalOfProvision(provision: Provision, provisionFee: ProvisionFee) {
        return this.getAttentionsProvisionFee(provisionFee)?.reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision?.practice)
                ? Math.round(moment(attention.toDate).diff(moment(attention.fromDate)) / (1000 * 60 * 60)) ? Math.round(moment(attention.toDate).diff(moment(attention.fromDate)) / (1000 * 60 * 60)) : 1 // Attention by hous
                : 1), 0                                                                      // Attention by unit
        )
    }

    getAttentionsProvisionFee(provisionFee: ProvisionFee): Attention[] {
        return !!this.attentionsINRange?.filter(att => att.provisionFeeId == provisionFee?.id)
            ? this.attentionsINRange.filter(att => att.provisionFeeId == provisionFee?.id)
            : [];
    }



    getAttentionsAgreement(agreement: Agreement): Attention[] {
        return this.attentionsINRange?.filter(att => att.agreementId == agreement.id)
            ? this.attentionsINRange.filter(att => att.agreementId == agreement.id)
            : [];
    }

    practiceCountedByHours(practice: Practice) {
        return (!!practice?.restriction?.requiredTime || !!practice?.restriction?.requiredToDate);
    }

    totalRealizedOfProvision(provision: Provision, provisionFee: ProvisionFee) {
        return this.getAttentionsProvisionFee(provisionFee)?.filter(att => att.state.id === AttentionStates.REALIZADA).reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision.practice)
                ? attention.quantity ? attention.quantity : 1
                : 1), 0
        )
    }


    invoicedOfProvision(provision: Provision, provisionFee: ProvisionFee) {
        return this.getAttentionsProvisionFee(provisionFee).filter(att => att.state.id === AttentionStates.FACTURABLE).reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision.practice)
                ? attention.quantity ? attention.quantity : 1 : 1
            ), 0
        )
    }

    debitedOfProvision(provision: Provision, provisionFee?: ProvisionFee) {
        return this.getAttentionsProvisionFee(provisionFee).filter(att => att.state.id === AttentionStates.DEBITADA).reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision.practice)
                ? attention.quantity ? attention.quantity : 1 : 1
            ), 0
        )
    }

    fullyDeliveredOfProvision(provision: Provision, provisionFee?: ProvisionFee) {
        return this.getAttentionsProvisionFee(provisionFee).filter(att => att.state.id === AttentionStates.INFORMADA).reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision.practice)
                ? attention.quantity ? attention.quantity : 1 : 1
            ), 0
        )
    }

    // ===== MODULE COUNTS (by AttentionType.MODULE) =====

    /**
     * Obtiene las atenciones de tipo MODULE para un CaseModule específico
     */
    getAttentionsCaseModule(caseModule: CaseModuleComposed): Attention[] {
        const caseModuleId = caseModule?.id;
        if (!caseModuleId) return [];
        return this.attentionsINRange?.filter(
            att => att.type === AttentionType.MODULE && att.caseModuleId === caseModuleId
        ) ?? [];
    }

    /**
     * Total agendado del módulo (todas las attentions de tipo MODULE)
     */
    totalOfModule(caseModule: CaseModuleComposed): number {
        let total = this.getAttentionsCaseModule(caseModule)?.reduce((accum, attention) =>
            accum + 1, 0
        ) ?? 0;
        return total;
    }

    /**
     * Total realizado del módulo (state = REALIZADA)
     */
    totalRealizedOfModule(caseModule: CaseModuleComposed): number {
        return this.getAttentionsCaseModule(caseModule)
            ?.filter(att => att.state?.id === AttentionStates.REALIZADA)
            .reduce((accum, attention) => accum + 1, 0) ?? 0;
    }

    /**
     * Total facturable del módulo (state = FACTURABLE)
     */
    invoicedOfModule(caseModule: CaseModuleComposed): number {
        return this.getAttentionsCaseModule(caseModule)
            ?.filter(att => att.state?.id === AttentionStates.FACTURABLE)
            .reduce((accum, attention) => accum + 1, 0) ?? 0;
    }

    /**
     * Total debitado del módulo (state = DEBITADA)
     */
    debitedOfModule(caseModule: CaseModuleComposed): number {
        return this.getAttentionsCaseModule(caseModule)
            ?.filter(att => att.state?.id === AttentionStates.DEBITADA)
            .reduce((accum, attention) => accum + 1, 0) ?? 0;
    }

    /**
     * Total informado del módulo (state = INFORMADA)
     */
    fullyDeliveredOfModule(caseModule: CaseModuleComposed): number {
        return this.getAttentionsCaseModule(caseModule)
            ?.filter(att => att.state?.id === AttentionStates.INFORMADA)
            .reduce((accum, attention) => accum + 1, 0) ?? 0;
    }


    agreementPerformed(provision: Provision, agreement: Agreement) { // Realizadas
        return this.getAttentionsAgreement(agreement).filter(att => att.state.id === AttentionStates.REALIZADA).reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision?.practice)
                ? attention.quantity ? attention.quantity : 1 : 1), 0
        )
    }

    agreementNotPerformed(provision: Provision, agreement: Agreement) { // No realizadas
        return this.getAttentionsAgreement(agreement).filter(att => [AttentionStates.AGENDADA, AttentionStates.EN_CURSO, AttentionStates.NO_REALIZADA, AttentionStates.CREACION].includes(att.state.id)).reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision?.practice)
                ? Math.round(moment(attention.toDate).diff(moment(attention.fromDate)) / (1000 * 60 * 60)) ? Math.round(moment(attention.toDate).diff(moment(attention.fromDate)) / (1000 * 60 * 60)) : 1 : 1 // Attention by hours
            ), 0                                                                      // Attention by unit
        )
    }

    agreementInvoiced(provision: Provision, agreement: Agreement) { // Facturables
        return this.getAttentionsAgreement(agreement).filter(att => [AttentionStates.FACTURABLE].includes(att.state.id)).reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision?.practice)
                ? attention.quantity ? attention.quantity : 1 : 1
            ), 0
        )
    }

    agreementDebited(provision: Provision, agreement: Agreement) { // Debitadles
        return this.getAttentionsAgreement(agreement).filter(att => [AttentionStates.DEBITADA].includes(att.state.id)).reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision?.practice)
                ? attention.quantity ? attention.quantity : 1 : 1
            ), 0
        )
    }

    //get total scheduled by agreement
    agreementTotal(provision: Provision, agreement: Agreement) {
        return this.getAttentionsAgreement(agreement).reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision?.practice)
                ? Math.round(moment(attention.toDate).diff(moment(attention.fromDate)) / (1000 * 60 * 60)) ? Math.round(moment(attention.toDate).diff(moment(attention.fromDate)) / (1000 * 60 * 60)) : 1 : 1 // Attention by hours
            ), 0                                                                      // Attention by unit
        )
    }

    agreementInformed(provision: Provision, agreement: Agreement) {
        return this.getAttentionsAgreement(agreement).filter(att => [AttentionStates.INFORMADA].includes(att.state.id)).reduce((accum, attention) =>
            accum + (!!this.practiceCountedByHours(provision?.practice)
                ? attention.quantity ? attention.quantity : 1 : 1 // Attention by hous
            ), 0                                                                      // Attention by unit
        )
    }

    //Single Selection Mode
    selectModuleSingle(row: CaseModuleComposed): void {
        const isDeselecting = this.singleSelectedModuleId === row.id;
        this.singleSelectedModuleId = isDeselecting ? null : row.id;
        // Deseleccionar el radio de pedidos si estaba activo (mutuamente excluyentes)
        this.ordersRadioSelected = false;
        this.ordersRadioSelectedChanged.emit(false);
        // Limpiar checkboxes al cambiar de radio
        this.clearCheckboxesWithoutEmit();
        this.singleSelectedModuleChanged.emit({
            id: isDeselecting ? null : row.id,
            name: isDeselecting ? null : row.name
        });

        // Al seleccionar/deseleccionar un módulo con radio, emitir filtro con TODAS sus provisions
        if (!isDeselecting && row) {
            // marcar todas las prácticas del módulo como checked
            const practices = this.getPracticesByModule(row);
            for (const p of practices) {
                if (!this.selectionProvisions.selected.some(sel => sel.id === (p as any).id)) {
                    this.selectionProvisions.select(p);
                }
                (p as any).checked = true;
            }
            const supplies = this.getSuppliesByModule(row);
            for (const s of supplies) {
                if (!this.selectionSupplies.selected.some(sel => sel.id === (s as any).id)) {
                    this.selectionSupplies.select(s);
                }
                (s as any).checked = true;
            }
            // marcar todos los agreements de las prácticas del módulo como checked
            for (const p of practices) {
                const agreements = this.getVisibleAgreementsForProvision(p);
                for (const agreement of agreements) {
                    const id = this.getAgreementId(agreement);
                    if (id == null) continue;
                    if (!this.selectionAgreements.selected.some(sel => sel.id === id)) {
                        this.selectionAgreements.select(agreement as Agreement);
                    }
                    (agreement as any).checked = true;
                }
            }
            // updateFilter() construirá el filtro con caseModuleIds + provisions/provisionFees seleccionados
            this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
        } else {
            // Deseleccionando: enviar filtro actualizado (que ahora no tendrá módulo de radio)
            this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
        }
    }

    /**
     * Selecciona TODOS los pedidos con radio button (mutuamente excluyente con módulos)
     */
    selectOrdersRadio(): void {
        const isDeselecting = this.ordersRadioSelected;
        this.ordersRadioSelected = !isDeselecting;
        // Emitir el cambio de estado del radio de pedidos
        this.ordersRadioSelectedChanged.emit(this.ordersRadioSelected);
        // Deseleccionar cualquier módulo de radio activo (mutuamente excluyentes)
        this.singleSelectedModuleId = null;
        this.singleSelectedModuleChanged.emit({ id: null, name: null });
        // Limpiar checkboxes al cambiar de radio
        this.clearCheckboxesWithoutEmit();

        // El filtro de pedidos NO envía caseModuleId, envía TODOS los orders
        if (!isDeselecting) {
            const allOrders = this.ordersDataSource?.data ?? [];
            const allOrdersFilter: ProvisionAgreementFilters = {
                provisions: [],
                provisionFees: [],
                agreements: [],
                orders: [...allOrders],
                provisionsSupplies: [],
                caseModuleIds: [], // Sin caseModuleId para pedidos
                modulesFlow: true
            };

            this.provisionAgreementsInput = allOrdersFilter;
            this.provisionsFacade.setFiltersOnProvisions(allOrdersFilter);
        } else {
            // Deseleccionando: enviar filtro actualizado
            this.provisionsFacade.setFiltersOnProvisions(this.updateFilter());
        }
    }

    deletePracticeModule(provision: Provision) {
        let provisionFee = provision.provisionFees[0];
        const textAux = !!provisionFee.toDate ? `- ${moment(provisionFee.toDate).format('DD/MM/YYYY')}` : this.experienceI18n.translate('modules.cases.modules-scheme.en-adelante', undefined, 'en adelante').toLowerCase();
        if (!!provision && !!provision.provisionFees && provision.provisionFees.length == 1) {
            this.swalWithCustomizeButtons.fire({
                text: this.experienceI18n.translate('modules.cases.modules-scheme.se-eliminara-la-prestacion-en-su-totalidad-desea-continuar', undefined, 'Se eliminará la prestación en su totalidad, ¿Desea continuar?'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: this.experienceI18n.translate('common.accept', undefined, 'Aceptar'),
                cancelButtonText: this.experienceI18n.translate('common.cancel', undefined, 'Cancelar'),
                reverseButtons: true
            }).then(result => {
                if (result.isConfirmed) {
                    this.confirmDeleteProvisionFee(provision, provisionFee);
                }
            });
        } else {
            this.swalWithCustomizeButtons.fire({
                text: `${this.experienceI18n.translate('modules.cases.modules-scheme.se-eliminara-la-prestacion-para-el-periodo-del', undefined, 'Se eliminará la prestación para el período del')} ${moment(provisionFee.fromDate).format('DD/MM/YYYY')} ${textAux} ${this.experienceI18n.translate('modules.cases.modules-scheme.y-su-agenda-asociada-desea-continuar', undefined, 'y su agenda asociada ¿Desea continuar?')}`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: this.experienceI18n.translate('common.accept', undefined, 'Aceptar'),
                cancelButtonText: this.experienceI18n.translate('common.cancel', undefined, 'Cancelar'),
                reverseButtons: true
            }).then(result => {
                if (result.isConfirmed) {
                    this.confirmDeleteProvisionFee(provision, provisionFee);
                }
            });
        }
    }

    confirmDeleteProvisionFee(provision: Provision, provisionFee: ProvisionFee) {

        let successMsg = () => {
            this.swalWithCustomizeButtons.fire({
                text: this.experienceI18n.translate('modules.cases.modules-scheme.la-sub-prestacion-se-elimino-correctamente', undefined, 'La sub-prestación se eliminó correctamente'),
                icon: 'success'
            }).then(() => {
                this._dialogRef.close();
            })
        }

        this.provisionsFacade.deleteProvisionFee(this.case.id, provision.id, provisionFee.id).subscribe({
            next: (res) => {
                successMsg();
            },
            error: (error) => {
                if (error.status === 418) {
                    let errorContent: HttpBaseResponseErrorTeaPot = error.error;

                    // WARNING! It's a promise, waiting the user
                    this.attentionsService.processErrorCountAttentions(errorContent, 'error').then(res => {
                        if (!res) {
                            this._dialogRef.close();
                        } else {
                            // User confirm force
                            // Nothing to do, it doesn't fire force action
                        }
                    })
                }
            }
        });
    }


    deleteSupply(provFees: ProvisionFee[]) {
        this.openMessageDialogComponent(this.experienceI18n.translate('modules.cases.modules-scheme.se-eliminara-el-o-los-insumos-seleccionados', undefined, 'Se eliminará el o los insumos seleccionados. Esta acción no se puede revertir.'));
        this._dialogRef.componentInstance.ok.subscribe(result => {
            if (result) {
                let provFeesWithOrders = provFees.filter(provFee => provFee.orders?.length > 0 && !!provFee.orders.find(or => or.state.id != this.ORDERS_STATES.CANCELADO));
                if (provFeesWithOrders.length == 0) {
                    this.confirmDeleteSupplies(provFees)
                } else {
                    this.message = this.experienceI18n.translate('modules.cases.modules-scheme.los-siguientes-insumos-son-parte-de-un-pedido-y-no-se-pueden-eliminar', undefined, 'Los siguientes insumos son parte de un pedido y no se pueden eliminar');
                    this.arrayTable = this.getProvisionFeesDynamicTableWithActiveOrders(this.getProvisionFeesWithActiveOrder(provFeesWithOrders))
                    this.openDialog(this.experienceI18n.translate('modules.cases.modules-scheme.atencion', undefined, 'Atención'), this.popUpMessage, { minWidth: '30%', maxHeight: '70vh' }, () => { })
                }
            }
        })
    }

    getProvisionFeesWithActiveOrder(provFees: ProvisionFee[]): ProvisionFee[] {
        return provFees.filter(pF => this.isProvisionFeeWithActiveOrders(pF));
    }

    isProvisionFeeWithActiveOrders(provFee: ProvisionFee): boolean {
        return provFee?.orders?.filter(order => order.state.id != this.ORDERS_STATES.CANCELADO).length > 0 ? true : false;
    }


    confirmDeleteSupplies(provFees: ProvisionFee[]) {
        let itemsToDelete = provFees.flatMap(provFee => this.orders.flatMap(or => or.items.filter(it => it.provisionFeeId == provFee.id)));
        let successMsg = () => {
            this.swalWithCustomizeButtons.fire({
                text: this.experienceI18n.translate('modules.cases.modules-scheme.se-elimino-el-o-los-insumos-seleccionados', undefined, 'Se eliminó el o los insumos seleccionados'),
                icon: 'success'
            }).then(() => {
                this._dialogRef.close();
                if (itemsToDelete != null && itemsToDelete.length > 0) {
                    this.casesFacade.deleteOrderItems(itemsToDelete, this.orders, this.caseId);
                }
            })
        }
        this.provisionsFacade.deleteSupplies(provFees).subscribe(
            res => {
                successMsg()
                // this.selectionProvFees.clear()
                this.provisionsFacade.setFiltersOnProvisions(this.updateFilter())
            }
        )
    }

    getProvisionFeesDynamicTableWithActiveOrders(provisionFeesSelected: ProvisionFee[]): any[] {
        let _array = [];
        provisionFeesSelected.forEach(pFee => {
            // Dynamic table
            pFee.orders.forEach(order => {
                if (order.state.id != this.ORDERS_STATES.CANCELADO)
                    _array.push({
                        // Example row -> Jeringas | 3 paquetes | Lezamas Josefina
                        supplyName: pFee.practice.name,
                        quantity: pFee.frequency?.amount ? `${pFee.frequency?.amount} ${pFee.frequency?.unit}` : '-',
                        provider: `${order.provider.surname} ${order.provider.name}`
                    })
            })
        })
        return _array;
    }

    getArrangementItemsByModuleValids(arrangementId: number, moduleId: number, vigencyFromDate: Date, vigencyToDate: Date): Partial<ArrangementItem>[] {
        vigencyFromDate = moment(vigencyFromDate).toDate()
        vigencyToDate = moment(vigencyToDate).toDate()

        const arrangement: CaseArrangement = this.caseArrangements?.find(arr => arr.id == arrangementId); // Get arrangement
        const moduleSelected: Module = this.modulesFilteredByFinancier.find(module => module.id == moduleId); // Get module

        let allArrangementModules: Partial<ModuleArrangement>[] = this.casesService.getArrangementItemsByPracticeOrModuleBetweenDates(arrangement, moduleSelected, vigencyFromDate, vigencyToDate, true) as Partial<ModuleArrangement>[];
        this.existPriceList = false;
        this.hasMissingPriceRange = false;

        if (allArrangementModules?.length > 0) {

            this.existPriceList = true;

            // Normalize dates to keep module arrangements aligned with the practice flow in the UI.
            // allArrangementModules = allArrangementModules.map(item => ({
            //         ...item,
            //         fromDate: item.fromDate ? moment(moment(item.fromDate).utc().format('YYYY-MM-DD')).toDate() : item.fromDate,
            //         toDate: item.toDate ? moment(moment(item.toDate).utc().format('YYYY-MM-DD')).toDate() : item.toDate,
            // }));

            // First item where initial date is vigencyFromDate
            let firstItem = allArrangementModules.find(item => moment(moment(vigencyFromDate).utc().format('YYYY-MM-DD')).isSameOrAfter(item.fromDate))
            let index = 0
            if (!!firstItem) {
                index = allArrangementModules.findIndex(item => item.id == firstItem.id);
            } else {
                allArrangementModules.splice(0, 0, {
                    fee: -1, // Show "No definido"
                    fromDate: this.formModule ? this.formModule.get('fromDate').value : null,
                    toDate: moment(allArrangementModules[0].fromDate).subtract(1, 'day').toDate(),
                }
                )
            }
            // Replace fromDate
            allArrangementModules[index] = !!firstItem ? { ...allArrangementModules[index], fromDate: moment(moment(vigencyFromDate).utc().format('YYYY-MM-DD')).toDate() } : { ...allArrangementModules[index] };
            // Last item where final date is provisionFeeToDate or null (==null is messagge "en adelante")
            let lastItem = allArrangementModules[allArrangementModules.length - 1];
            // Replace toDate
            if (moment(vigencyToDate).isValid()) {
                lastItem = { ...lastItem, toDate: vigencyToDate }
            }
            allArrangementModules[allArrangementModules.length - 1] = { ...lastItem };
            this.hasMissingPriceRange = allArrangementModules.some(item => item?.fee === -1);
            return allArrangementModules;
        } else {
            this.hasMissingPriceRange = false;
            return null;
        }
    }

    totalUnitOfProvision(caseModuleComposed?: CaseModuleComposed): number {

        if (!!caseModuleComposed) {
            if (!!caseModuleComposed.arrangementId) {
                if (moment(caseModuleComposed.toDate) < moment(this.dateRange.historyToDate)) {
                    const modulesArrangement = this.getArrangementItemsByModuleValids(caseModuleComposed.arrangementId, caseModuleComposed.moduleId, this.dateRange.historyFromDate, this.dateRange.historyToDate) // Validate with price list
                    return modulesArrangement?.find(ma => this.generalService.validateDateBetween(ma.fromDate, ma.toDate, caseModuleComposed.toDate))?.fee;
                } else {
                    const practicesArrangement = this.getArrangementItemsByModuleValids(caseModuleComposed.arrangementId, caseModuleComposed.moduleId, this.dateRange.historyFromDate, this.dateRange.historyToDate) // Validate with price list
                    return practicesArrangement?.find(ma => this.generalService.validateDateBetween(ma.fromDate, ma.toDate, this.dateRange.historyToDate))?.fee;
                }
            } else if (!!caseModuleComposed.fee) {
                return caseModuleComposed.fee;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    setVariationTooltip(caseModuleComposed: CaseModuleComposed | null) {
        this.hoveredModuleTooltip = caseModuleComposed;
    }

    getVariationPriceTooltip(caseModuleComposed: CaseModuleComposed): Partial<ArrangementItem>[] {
        if (moment(caseModuleComposed.toDate) < moment(this.dateRange.historyToDate)) {
            return this.getArrangementItemsByModuleValids(caseModuleComposed.arrangementId, caseModuleComposed.moduleId, caseModuleComposed.fromDate, caseModuleComposed.toDate);
        } else {
            return this.getArrangementItemsByModuleValids(caseModuleComposed.arrangementId, caseModuleComposed.moduleId, caseModuleComposed.fromDate, this.dateRange.historyToDate);
        }
    }

    verifyVariationOnPrice(caseModuleComposed: CaseModuleComposed): boolean {
        if (caseModuleComposed) {
            if (!!caseModuleComposed.arrangementId) {
                const arrangement: CaseArrangement = this.caseArrangements?.find(arr => arr.id == caseModuleComposed.arrangementId);
                if (!!arrangement && !!arrangement.moduleArrangements) {
                    const module = this.modulesFilteredByFinancier.find(mod => mod.id == caseModuleComposed.moduleId);
                    let moduleArrangement: ModuleArrangement[] = this.casesService.getArrangementItemsByPracticeOrModuleBetweenDates(arrangement, module, caseModuleComposed.fromDate, caseModuleComposed.toDate, true);
                    if (!!moduleArrangement && moduleArrangement.length > 1) {
                        return true;
                    } else {
                        return false;
                    }
                }
            } else {
                return false;
            }
        } else {
            return false;
        }
    }
}
